/**
 * The HUD. A thin DOM overlay over the WebGL canvas.
 *
 * Tooltips are dense and information-first: exact numbers, no vagueness. If a
 * player has to guess at a value, that is a bug in this file.
 */

import { injectStyle } from './style.ts';
import { constructionStage } from '../render/construction.ts';
import { crownAfford, isVillage } from '../sim/campaign/commonwealth.ts';
import { EventChoiceDialog } from './realmevent.ts';
import { injectVillageStyle, villagePanel } from './villagepanel.ts';
import { keepScroll, setHtml, setText } from './dom.ts';
import { Minimap } from './minimap.ts';
import { folkIcon, resourceIcon } from './resicons.ts';
import { Alerts, MARKS, type Alert } from './alerts.ts';
import { buildingThumb } from './buildart.ts';
import { chromeMark } from './chromemarks.ts';
import { TouchTips } from './touch.ts';
import { unitCardArt } from './troopart.ts';
import { HeroWheel } from './herowheel.ts';
import { heroPortrait } from './portraits.ts';
import { HERO_CLASSES, type HeroArchetype } from '../data/heroes.ts';
import {
  companionAbilitySlots, companionAbilityError, companionUnit,
  companionByCompany, companionRecruitError, companionReviveError,
  COMPANION_RECRUIT_COST, COMPANION_REVIVE_GOLD,
} from '../sim/systems/companions.ts';
import type { CompanionHero } from '../sim/herotypes.ts';
import { realmPurse, type RealmPurse } from './purse.ts';
import { race } from '../data/races.ts';
import { bindingReport } from '../sim/systems/bindings.ts';
import { standingOn } from '../sim/systems/garrison.ts';
import {
  HERO_KEY_LABELS,
  KEY_FORMATION,
  KEY_HELP,
  KEY_HOLD,
  KEY_HORN,
  KEY_LEDGER,
  KEY_MUTE,
  KEY_PAUSE,
  KEY_RALLY,
  KEY_SHEET,
  KEY_SHIELDWALL,
  KEY_STANCE,
  KEY_SWAP,
  POWER_KEY_LABELS,
  keyLabel,
} from './keys.ts';
import {
  Phase,
  RESOURCE_GLYPHS,
  RESOURCE_KEYS,
  RESOURCE_LABELS,
  TAX_BANDS,
  taxBand,
  Team,
  TERRAIN_NAMES,
  UF,
  UNCAPPED_RESOURCES,
  UnitState,
  type ResourceKey,
} from '../sim/types.ts';
import {
  Formation,
  FORMATION_NAMES,
  Stance,
  statsOf,
  STANCE_NAMES,
  type Company,
  type World,
} from '../sim/world.ts';
import { HARVEST_RADIUS, VILLAGER_SHARE } from '../sim/constants.ts';
import { BRIDGE_DEF, BUILDING_TYPES, buildingType, GATE_DEF, WALL_DEF } from '../data/buildings.ts';
import { POWERS, power } from '../data/powers.ts';
import { unitType, unitTypeIndex, unitWage, type UnitType } from '../data/units.ts';
import {
  RANK_NAMES,
  affordableModels,
  minMuster,
  musterStrength,
  scaleCost,
} from '../sim/systems/companies.ts';
import { scoutingReport, remainingThreat, AGE_NAMES } from '../sim/systems/director.ts';
import { ageResearchSeconds, hudResources, MAX_AGE, RESOURCE_AGE } from '../data/ages.ts';
import {
  daysToNextMonth,
  daysToNextSeason,
  formatDateLong,
  formatDateShort,
  season,
  seasonUpkeep,
  seasonYield,
} from '../sim/systems/calendar.ts';
import { TurnPhase, TURN_PHASE_NAMES } from '../sim/campaign/types.ts';
import { GOLD_PER_MISSING_TIMBER } from '../sim/campaign/fleets.ts';
import { LoadoutAct, armyStrength, loadoutReport } from '../sim/battle.ts';
import { resolvePower } from '../sim/systems/powers.ts';
import {
  heroAbilitySlots,
  heroAbilityError,
  heroClassOrNull,
  heroPointsLeft,
  heroSkillError,
  heroUnit,
  heroXpProgress,
  HERO_ABILITY_RANGE,
  HERO_ABILITY_COOLDOWN_MUL,
  HERO_REVIVE_SOULS,
} from '../sim/systems/hero.ts';
import {
  describeItem,
  equippedStack,
  hasForge,
  INVENTORY_MAX,
  reforgeCost,
  reforgeError,
  scrapValue,
} from '../sim/systems/items.ts';
import {
  ITEM_RARITY_NAMES,
  ITEM_SLOT_COUNT,
  ITEM_SLOT_EMPTY,
  ITEM_SLOT_NAMES,
  itemDef,
} from '../data/items.ts';
import { buildCostFor, buildRefusal, trainerCategoryFor } from '../sim/apply.ts';
import { isRepairOrdered, productionReadout, repairQuote, siteHarvestKind } from '../sim/systems/settlement.ts';
import { nodeKind } from '../data/nodes.ts';
import { districtReasons } from '../sim/systems/districts.ts';
import { relic } from '../data/relics.ts';
import { chronicleEvent } from '../data/events.ts';
import { canAfford } from '../sim/types.ts';
import type { Scene } from '../render/scene.ts';
import { toCss, shade, C } from '../data/palette.ts';
import { clamp01 } from '../core/math.ts';

export type BuildSelection =
  | { kind: 'none' }
  | { kind: 'building'; typeIdx: number }
  | { kind: 'wall' }
  | { kind: 'gate' }
  | { kind: 'road' }
  | { kind: 'bridge' }
  | { kind: 'demolish' };

export interface HudCallbacks {
  onBuildSelect(sel: BuildSelection): void;
  onPowerSelect(id: string): void;
  onCompanySelect(id: number, additive: boolean): void;
  getControlledTeam?(): number;
  onStartWave(): void;
  onRecruit(typeIdx: number): void;
  onReinforce(id: number): void;
  onRelicPick(id: string): void;
  /** `index` is into the offered list on `world.pendingEvent.choices`. */
  onEventChoice(eventId: string, index: number): void;
  onFormation(f: number): void;
  onStance(s: number): void;
  /** Order selected troops down from their rampart positions. */
  onLeaveWall?(): void;
  /** Spend a skill point on the hero's tree. */
  onHeroSkill(id: string): void;
  /** Call the fallen back. */
  onHeroRevive(): void;
  onRecruitHero?(classId: string): void;
  onReviveCompanion?(id: number): void;
  /** Wear an item off the cart. `uid` is an `ItemStack.uid`. */
  onHeroEquip(uid: number): void;
  /** Take a slot's item off. `slot` is an `ItemSlot`. */
  onHeroUnequip(slot: number): void;
  /** Pay to raise an item one level. */
  onHeroReforge(uid: number): void;
  /** Melt an item down. */
  onHeroScrap(uid: number): void;
  /**
   * Move the tax dial. `policy` is an index into `TAX_BANDS`.
   *
   * Optional, and the dial reads as a dead readout until somebody supplies it:
   * the HUD has no command queue of its own, so a notch it cannot post is a
   * notch it must not pretend to offer. Wire this to `Cmd.SetTaxPolicy` and the
   * five notches become clickable with no further change here.
   */
  onTaxPolicy?(policy: number): void;
  /** Raise a building's tier. `idx` is a building slot. */
  onBuildingUpgrade?(idx: number): void;
  /** Pull one down for two fifths of what went in. */
  onBuildingDemolish?(tx: number, ty: number): void;
  /** The player closed the building panel, or clicked empty ground. */
  onBuildingClose?(): void;
  /**
   * Edit the army you are taking onto the field. `act` is a `LoadoutAct`.
   *
   * Optional for the same reason `onTaxPolicy` is: the HUD has no command queue
   * of its own, so a button it cannot post is a button it must not offer. Wire
   * this to `Cmd.BattleLoadout` and the muster list becomes clickable with no
   * further change here.
   */
  onLoadout?(act: number, typeIdx: number): void;
  /** Put a crew on a damaged building, or call them off. */
  onRepair?(idx: number, cancel: boolean): void;
  /** Open the Ledger. */
  onOpenManage?(): void;
  onOpenIndustry?(): void;
  onVillageUpgrade?(province: number): void;

  // ── The chrome bar ───────────────────────────────────────────────────────
  // Every one of these has a key, and every one of them now also has a button,
  // because a game you can only play with two hands is a game half the people
  // who open it will put down.
  /** Open the character sheet. */
  onOpenSheet?(): void;
  onTogglePause?(): void;
  /** Step the speed ladder by ±1 rung. */
  onSpeed?(delta: number): void;
  onToggleMute?(): void;
  /** Move a mix slider. Keys are `AudioSettings` volume fields, 0..1. */
  onAudioSettings?(patch: { master?: number; effects?: number; ambience?: number }): void;
  /** The current mix, for the panel to open on. */
  audioSettings?(): { master: number; effects: number; ambience: number; muted: boolean };
  /** Display feel: screen-shake scale (0..2) and floating combat numbers. */
  onDisplaySettings?(patch: { shake?: number; numbers?: boolean }): void;
  displaySettings?(): { shake: number; numbers: boolean };
  /** Realm ⇄ city. Only offered when there is a realm to go to. */
  onSwapView?(): void;
}

/**
 * What the frame loop owns and the HUD only prints.
 *
 * Pause, speed and mute are not simulation state — `realtime` put the ladder on
 * the frame loop on purpose, so that a session played at ×8 replays byte for
 * byte at ×1 — so the HUD cannot read them off the world and is told instead.
 */
export interface HudChrome {
  paused: boolean;
  timescale: number;
  muted: boolean;
  /** There is a realm to swap to. */
  hasRealm: boolean;
  /** We are currently looking at the realm rather than the city. */
  onRealm: boolean;
}

const CATEGORIES: Array<{ id: string; label: string }> = [
  { id: 'economy', label: 'Economy' },
  { id: 'civic', label: 'Civic' },
  { id: 'military', label: 'Military' },
  { id: 'defence', label: 'Defence' },
  { id: 'works', label: 'Works' },
];

// ── The Age axis ───────────────────────────────────────────────────────────
//
// Type was always the axis this menu sorted on, and for a long time that was
// enough: four headings over about thirty sheds. Three Ages and eleven peoples
// later the catalogue is ninety-four buildings, and a flat list under one
// heading is a wall a player at the Kindling scrolls through for an hour
// looking at things they cannot have.
//
// So Age is the *second* axis, and it works the way `hudResources` works on the
// resource strip rather than the way a filter works: nothing is switched off,
// the list is **windowed**. Everything at or below the current Age is shown,
// because a Kindling farm is still a farm in the Hollow. The Age *above* is
// shown too, greyed — that is the teaching, and it is the whole reason the
// window opens upward at all: the fastest way to say what an Age is for is to
// show the four buildings it hands you. Everything past that is one folded row
// with a count on it, because a player two Ages out is not making that decision
// today and does not need eleven rows of it in the way.
//
// The Age is carried by the *heading*, not repeated on every row. That is
// deliberate and it is what keeps the wording honest: a row under a heading
// that reads NOT YET does not need to also say "Not yet this Age.", so the only
// per-row refusals left are the interesting ones — the Academy nodes — and
// those are quoted from `buildRefusal` rather than written here.

const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V', 'VI'];

function categoryLabel(id: string): string {
  return CATEGORIES.find((c) => c.id === id)?.label ?? id;
}

/** `Age II` / `Iron` / `the next Age`, for a heading over a group. */
function ageHeading(age: number, now: number): { numeral: string; name: string; state: string; tone: string } {
  return {
    numeral: `Age ${ROMAN[age] ?? age}`,
    name: AGE_NAMES[age - 1] ?? '',
    state: age < now ? '' : age === now ? 'yours' : age === now + 1 ? 'the next Age' : 'not yet',
    tone: age < now ? 'past' : age === now ? 'now' : age === now + 1 ? 'next' : 'far',
  };
}

/**
 * How far up the ladder the list opens before it folds: this Age and the one
 * above it, exactly as the resource strip carries this Age and the one below.
 */
function ageHorizon(now: number): number {
  return Math.min(MAX_AGE, now + 1);
}

/** `the Gilded and the Hollow` — what is behind the fold, named rather than counted. */
function agesBeyond(horizon: number): string {
  const names: string[] = [];
  for (let a = horizon + 1; a <= MAX_AGE; a++) names.push(`the ${AGE_NAMES[a - 1] ?? `Age ${a}`}`);
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * The tab bar's marks. Drawn here rather than in `chromemarks.ts` because they
 * are this bar's own vocabulary — a hammer for the works, a banner for the
 * host, a folded chart for the ground — and only this bar uses them.
 */
const HAMMER_MARK =
  '<path fill="currentColor" d="M2.6 2.4h7.6v3.2H2.6z"/>' +
  '<path fill="currentColor" d="M5.6 5.6h1.9v8.2H5.6z" opacity="0.8"/>' +
  '<path fill="currentColor" d="M10.2 2.9 14 4.1v1.8l-3.8 1.2z" opacity="0.65"/>';
const BANNER_MARK =
  '<path fill="currentColor" d="M2.4 1.6h1.7v12.8H2.4z" opacity="0.8"/>' +
  '<path fill="currentColor" d="M4.1 2.4h9.8l-2.3 3.1 2.3 3.1H4.1z"/>';
/**
 * A framed chart with a mark on it, and *not* a folded map.
 *
 * The folded map this started as — three panels, alternating tone — is the same
 * silhouette as an open book at nineteen pixels, and the book is the tab
 * immediately beside it. Two neighbouring tabs that are the same shape are one
 * tab as far as a player is concerned.
 */
const CHART_MARK =
  '<path fill="currentColor" d="M1.6 2.4h12.8v11.2H1.6z" opacity="0.34"/>' +
  '<path fill="currentColor" d="M1.6 2.4h12.8v1.5H1.6zM1.6 12.1h12.8v1.5H1.6z' +
  'M1.6 2.4h1.5v11.2H1.6zM12.9 2.4h1.5v11.2h-1.5z"/>' +
  '<path fill="currentColor" d="M8 5.4a2.3 2.3 0 0 1 2.3 2.3c0 1.7-2.3 4.2-2.3 4.2S5.7 9.4 5.7 7.7A2.3 2.3 0 0 1 8 5.4z"/>';
const CHROME_BOOK =
  '<path fill="currentColor" d="M1.4 3.2c2.3 0 4.5.5 6.1 1.5v9.1c-1.6-1-3.8-1.5-6.1-1.5z"/>' +
  '<path fill="currentColor" d="M14.6 3.2c-2.3 0-4.5.5-6.1 1.5v9.1c1.6-1 3.8-1.5 6.1-1.5z" opacity="0.72"/>';
/**
 * An arched gate, standing open. The way out.
 *
 * Not a cog and not three bars: this tab is not a settings screen, it is the
 * door — put the run down, save where you stand, or leave — and a gate is the
 * one shape in this game's vocabulary that already means leaving somewhere.
 */
const GATE_MARK =
  '<path fill="currentColor" d="M2.2 13.2h1.9V5.6a3.9 3.9 0 0 1 7.8 0v7.6h1.9V5.6a5.8 5.8 0 0 0-11.6 0z"/>' +
  '<path fill="currentColor" d="M1.2 13.2h13.6v1.6H1.2z" opacity="0.7"/>';

const POWER_KEYS = POWER_KEY_LABELS;

/** Kept in step with `HERO_HOTKEYS` in `main.ts`. */
export const HERO_ABILITY_KEYS = HERO_KEY_LABELS;

export class Hud {
  private root: HTMLElement;
  private resBar!: HTMLElement;
  private buildList!: HTMLElement;
  private rosterList!: HTMLElement;
  private cardBar!: HTMLElement;
  private powerBar!: HTMLElement;
  private willFill!: HTMLElement;
  private willLabel!: HTMLElement;
  private taxDial!: HTMLElement;
  private taxNotches!: HTMLElement;
  private logEl!: HTMLElement;
  private tip!: HTMLElement;
  private scouting!: HTMLElement;
  private bossCard!: HTMLElement;
  private vignette!: HTMLElement;
  private worldText!: HTMLElement;
  private modal!: HTMLElement;
  private chronicleDialog?: EventChoiceDialog;
  private chronicleWorld: World | null = null;
  private chroniclePending: World['pendingEvent'] = null;
  private fpsEl!: HTMLElement;
  private hintEl!: HTMLElement;
  private phaseName!: HTMLElement;
  private phaseTimer!: HTMLElement;
  private waveLabel!: HTMLElement;

  private heroPanel!: HTMLElement;
  private heroSheetEl!: HTMLElement;
  private heroAbilityBar!: HTMLElement;
  private heroWheel!: HeroWheel;
  private heroRosterButton!: HTMLButtonElement;
  private armedCompanionId = -1;
  private inspectedCompanionId = -1;

  private buildPanel!: HTMLElement;
  private buildHdr!: HTMLElement;
  private cityTools!: HTMLElement;
  private buildDoor!: HTMLButtonElement;
  private armyDoor!: HTMLButtonElement;
  private rosterSummary!: HTMLElement;
  private rosterEmpty!: HTMLElement;
  private buildDrawerOpen = false;
  private armyDrawerOpen = false;
  private villageHtml = '';
  private villageReadAt = 0;
  private binfo!: HTMLElement;
  private ctlBar!: HTMLElement;
  private pauseBtn!: HTMLElement;
  private speedBtn!: HTMLElement;
  private muteBtn!: HTMLElement;
  private volBtn!: HTMLElement;
  private volPanel: HTMLElement | null = null;
  private swapBtn!: HTMLElement;
  private orderBar!: HTMLElement;
  private minimap!: Minimap;
  private alertBar!: HTMLElement;
  private tabBar!: HTMLElement;
  /**
   * Which sheet is up on a narrow screen: '' for none.
   *
   * Only one at a time, and that is the whole layout: on a phone a panel is
   * something pulled up over the map and put away again, never a column beside
   * it. Kept here rather than in CSS state so the tab bar and the panels cannot
   * disagree about which is open.
   */
  private sheet = '';
  /** The narrow layout is on. Driven by a media query, not by a device string. */
  private narrow = false;
  /**
   * The finger half of the tooltips. See `TouchTips` in `ui/touch.ts`.
   *
   * Constructed here rather than in `main.ts` because it is this class's own
   * `hover` and `hideTip` it drives, and because the HUD is the only thing that
   * knows which of its elements answer a hover.
   */
  private touchTips!: TouchTips;
  private alerts = new Alerts();
  /**
   * What the cluster is currently showing, by id.
   *
   * Held so the click handler can find the alert a chip stands for without the
   * chip having to carry its whole payload in `dataset` — and so the markup is
   * only rewritten when the *set* changes, not when a count inside one of them
   * ticks over.
   */
  private shownAlerts: Alert[] = [];
  /** The set the cluster's handlers were last bound for. */
  private alertKey = '';
  private chrome: HudChrome = { paused: false, timescale: 1, muted: false, hasRealm: false, onRealm: false };
  /**
   * The building slot the player has open, or −1.
   *
   * A slot index and not a handle, because the building store has no
   * generation-checked handle and the panel is redrawn from the world every
   * frame anyway: a slot that has been freed or reused under it is caught by
   * the same check that draws it, which is the only check that could be
   * correct at the moment of drawing.
   */
  private openBuilding = -1;
  private battleMode: boolean | null = null;
  private category = 'economy';
  /**
   * The fold at the bottom of the build list is open — the Ages past the
   * horizon are being shown.
   *
   * Deliberately *not* remembered per category and not persisted. It is a
   * "let me look ahead" gesture, and a player who opened it once on Economy
   * has not asked to have every list in the game permanently unfolded.
   */
  private showLater = false;
  /** The same fold, in the drill list inside a building panel. */
  private showLaterDrills = false;
  /**
   * What the build list was last built for.
   *
   * The list is now a function of the Age, the people and the Academy as well
   * as the category, and all four of those are things that change a handful of
   * times in a run. This file has been here before — the note on
   * `updateBuildAffordability` is about a list that was torn down sixty times a
   * second — so the rebuild is gated on a signature rather than run per frame,
   * and the per-frame pass still only writes a cost line when a figure moves.
   */
  private listKey = '';
  private buildSel: BuildSelection = { kind: 'none' };
  private armedPower = '';
  private armedHeroAbility = '';
  private selectedCompanies: number[] = [];
  private lastResSnapshot: Record<string, number> = {};
  private rateSample: Record<string, number> = {};
  private rateTimer = 0;
  private textPool: HTMLSpanElement[] = [];

  /**
   * The world being drawn this frame.
   *
   * `adoptWorld` in `main.ts` swaps the World object wholesale, so a handler
   * that closed over one would go on describing a settlement that no longer
   * exists — a tooltip full of a dead run's numbers, on a live run's screen.
   * Every persistent listener in this file (the ones on elements built once and
   * reused forever) reads the world through here instead. It is written at the
   * top of `update` and nowhere else, so it cannot be stale by more than the
   * frame it is read in, and a handler that fires before the first frame simply
   * finds `null` and does nothing.
   */
  private w: World | null = null;
  /**
   * Which world the per-second rate samples below were taken from.
   *
   * Weak, because the HUD has no business keeping a dead world alive. On a swap
   * the samples are thrown away rather than differenced against the previous
   * run's treasury, which used to be good for one frame of nonsense rates.
   */
  private rateWorld: WeakRef<World> | null = null;

  /**
   * The town, whichever screen is up — and the realm, likewise.
   *
   * The strip is now furniture on *both* maps rather than the settlement's own
   * bar, and the two facts that stay true wherever the player is standing come
   * from two different worlds: the crown's purse and its council clock are the
   * realm's, and the alerts, the tax band and the Age are the town's. `update`
   * is handed whatever is being drawn, so these are the other one.
   *
   * Written by `setWorlds` from the frame loop and read nowhere else, so they
   * cannot be staler than the frame they are read in — the same rule `this.w`
   * follows, and for the same reason: `adoptWorld` swaps the object wholesale.
   */
  private cityW: World | null = null;
  private realmW: World | null = null;

  constructor(
    private cb: HudCallbacks,
    private scene: Scene,
  ) {
    injectStyle();
    this.root = document.getElementById('ui-root') as HTMLElement;
    this.build();
  }

  private el(tag: string, cls?: string, parent?: HTMLElement): HTMLElement {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    (parent ?? this.root).appendChild(e);
    return e;
  }

  private build(): void {
    // Resource strip.
    this.resBar = this.el('div');
    this.resBar.id = 'res-bar';
    this.resBar.className = 'panel';

    const phase = this.el('div', '', this.resBar);
    phase.id = 'phase-box';
    // The three readouts share one hover — they are one thought — while the
    // horn keeps its own, because pressing it is a decision with a price.
    const clock = this.el('div', 'clock', phase);
    this.waveLabel = this.el('div', '', clock);
    this.waveLabel.id = 'wave-label';
    this.phaseName = this.el('div', '', clock);
    this.phaseName.id = 'phase-name';
    this.phaseTimer = this.el('div', '', clock);
    this.phaseTimer.id = 'phase-timer';
    clock.onmouseenter = (ev) => this.hover(ev, () => this.phaseTip());
    clock.onmouseleave = () => this.hideTip();
    const horn = this.el('div', 'seal', phase);
    horn.innerHTML = `Sound the horn<span class="kb">${esc(keyLabel(KEY_HORN))}</span>`;
    horn.onclick = () => this.cb.onStartWave();
    horn.onmouseenter = (ev) => this.hover(ev, () => this.hornTip());
    horn.onmouseleave = () => this.hideTip();

    /**
     * The chrome bar: pause, speed, sound, the sheet, the Ledger and the door
     * to the realm, each wearing its own key.
     *
     * It lives in the resource strip rather than in a corner of its own because
     * that strip is the one piece of furniture that is on screen in every mode
     * and never moves, and because a control the player cannot find is worth
     * about as much as a control that does not exist. Every button here is a
     * key that already worked; none of them is a new mechanism — the speed
     * buttons walk the same ladder the keys do, through the same callback.
     */
    this.ctlBar = this.el('div', '', this.resBar);
    this.ctlBar.id = 'ctl';

    /**
     * Three groups, not ten buttons in a row.
     *
     * Time, sound and the screens are three different kinds of decision, and a
     * player hunting for the Ledger should not have to read past the speed
     * ladder to find it. The grouping states the rule the old bar hid:
     * everything in the first group changes how fast the world runs, and
     * everything in the last one opens something.
     */
    const group = (id: string): HTMLElement => {
      const g = this.el('div', 'ctl-group', this.ctlBar);
      g.dataset.group = id;
      return g;
    };
    const gTime = group('time');
    const gSound = group('sound');
    const gScreens = group('screens');

    /**
     * `label` is markup — a transport character for the time group, a drawn
     * mark for everything else. `name` is the word under a screen button and ''
     * for the compact ones: a bar where every button is labelled is a menu, and
     * a bar where none of them is is a puzzle.
     */
    const ctl = (
      parent: HTMLElement,
      id: string,
      label: string,
      key: string,
      tip: [string, string, string],
      fire: () => void,
      name = '',
    ) => {
      const el = this.el('div', 'ctl' + (name ? ' named' : ''), parent);
      el.dataset.ctl = id;
      el.innerHTML =
        `<span class="cg">${label}</span>` +
        (name ? `<span class="cn">${esc(name)}</span>` : '') +
        `<span class="ck">${esc(key)}</span>`;
      el.onclick = fire;
      el.onmouseenter = (ev) => this.hover(ev, () => this.setTip(tip[0], tip[1], '', tip[2]));
      el.onmouseleave = () => this.hideTip();
      return el;
    };

    this.pauseBtn = ctl(gTime, 'pause', '❚❚', keyLabel(KEY_PAUSE), [
      'Pause',
      'Stops feeding the simulation. Nothing is skipped and nothing is lost.',
      'The only other thing that stops the world is a battle you are fighting by hand.',
    ], () => this.cb.onTogglePause?.());
    ctl(gTime, 'slower', '«', '−', [
      'Slower',
      'One rung down the ladder: 8, 4, 2, 1, a half.',
      'Speed lives in the frame loop, not in the world. A run at ×8 replays identically at ×1.',
    ], () => this.cb.onSpeed?.(-1));
    this.speedBtn = this.el('div', 'ctl speed', gTime);
    this.speedBtn.innerHTML = `<span class="cg">×1</span>`;
    this.speedBtn.onmouseenter = (ev) =>
      this.hover(ev, () =>
        this.setTip('Speed', 'How many ticks a second the loop asks for.', '', 'The world does not know it is hurrying.'),
      );
    this.speedBtn.onmouseleave = () => this.hideTip();
    ctl(gTime, 'faster', '»', '+', [
      'Faster',
      'One rung up the ladder, to ×8.',
      'Riders on the road to your gates take it back off you.',
    ], () => this.cb.onSpeed?.(1));
    this.muteBtn = ctl(gSound, 'mute', chromeMark('sound'), keyLabel(KEY_MUTE), [
      'Sound',
      'Everything you hear is generated; there are no audio files.',
      '',
    ], () => this.cb.onToggleMute?.());
    this.volBtn = ctl(gSound, 'vol', chromeMark('sliders'), '', [
      'Sound & display',
      'The mix on sliders, the screen-shake, and the floating numbers.',
      'All of it remembered between sessions.',
    ], () => this.toggleVolume());
    ctl(gScreens, 'sheet', chromeMark('helm'), keyLabel(KEY_SHEET), [
      'The character sheet',
      'Levels, the skill tree, and everything he is carrying.',
      'He is the only thing in the realm that gets better on purpose.',
    ], () => this.cb.onOpenSheet?.(), 'Sheet');
    ctl(gScreens, 'ledger', chromeMark('book'), keyLabel(KEY_LEDGER), [
      'The Ledger',
      'Tax and treasury, the other crowns, your people, and every chain in the town.',
      'Four pages of things somebody has already counted for you.',
    ], () => this.cb.onOpenManage?.(), 'Ledger');
    this.swapBtn = ctl(gScreens, 'swap', chromeMark('banner'), keyLabel(KEY_SWAP), [
      'The realm',
      'Out of the city and onto the map, and back again.',
      'Both halves are live. Neither of them waits for you.',
    ], () => this.cb.onSwapView?.(), 'Realm');
    ctl(gScreens, 'keys', '?', '', [
      'The keyboard',
      'Every binding in the game, and every one of them has a button too.',
      'Nothing here is the only way to do anything.',
    ], () => this.showKeys());

    // The tax dial. Built here so the resource chips have something stable to
    // insert themselves in front of, which is what keeps the strip in
    // `RESOURCE_KEYS` order instead of the reverse.
    this.taxDial = document.createElement('div');
    this.taxDial.className = 'taxdial';
    this.taxDial.innerHTML = `<span class="tk"></span><span class="tn"></span>`;
    this.resBar.insertBefore(this.taxDial, phase);
    this.taxNotches = this.taxDial.querySelector('.tn') as HTMLElement;
    for (let i = 0; i < TAX_BANDS.length; i++) {
      const notch = document.createElement('i');
      notch.dataset.band = String(i);
      notch.onclick = () => this.cb.onTaxPolicy?.(i);
      notch.onmouseenter = (ev) => this.hover(ev, () => this.taxTip(i));
      // Falling back to the dial's own tip rather than hiding: leaving a notch
      // for the gap beside it is not leaving the dial, and a tooltip that
      // blinks out between notches reads as a fault.
      notch.onmouseleave = (ev) => this.hover(ev, () => this.taxTip(-1));
      this.taxNotches.appendChild(notch);
    }
    this.taxDial.onmouseenter = (ev) => this.hover(ev, () => this.taxTip(-1));
    this.taxDial.onmouseleave = () => this.hideTip();

    /**
     * The alert cluster, between the readouts and the chrome.
     *
     * Deliberately on the strip rather than floating somewhere of its own: the
     * strip is the one piece of furniture that is on screen in every mode and
     * never moves, so this is the one place a warning is guaranteed to be
     * looked at. One listener, bound here and delegating on `data-alert`,
     * because the chips are rewritten whenever the set of them changes and a
     * handler on a chip would go with it.
     */
    this.alertBar = document.createElement('div');
    this.alertBar.id = 'alerts';
    this.alertBar.addEventListener('mousedown', (ev) => this.onAlertClick(ev));
    this.resBar.insertBefore(this.alertBar, phase);

    // The chrome sits between the readouts and the clock, which is where the
    // eye already goes for "what is the game doing" — and, mechanically, after
    // the tax dial in the DOM, because the resource chips insert themselves in
    // front of the dial and would otherwise land on the wrong side of it.
    this.resBar.insertBefore(this.ctlBar, phase);

    // Small, named doors leave the settlement visible until a player needs a
    // catalogue or a detailed army list. Unit cards stay available in battle.
    this.cityTools = this.el('nav');
    this.cityTools.id = 'city-tools';
    this.cityTools.setAttribute('aria-label', 'Settlement tools');
    this.buildDoor = this.el('button', 'city-tool', this.cityTools) as HTMLButtonElement;
    this.buildDoor.type = 'button';
    this.buildDoor.id = 'build-door';
    this.buildDoor.innerHTML = `<svg viewBox="0 0 16 16" aria-hidden="true">${HAMMER_MARK}</svg><span>Build</span>`;
    this.buildDoor.setAttribute('aria-controls', 'build-panel');
    this.buildDoor.setAttribute('aria-expanded', 'false');
    this.buildDoor.onclick = () => this.setBuildDrawer(!this.buildDrawerOpen);
    this.armyDoor = this.el('button', 'city-tool', this.cityTools) as HTMLButtonElement;
    this.armyDoor.type = 'button';
    this.armyDoor.id = 'army-door';
    this.armyDoor.innerHTML = `<svg viewBox="0 0 16 16" aria-hidden="true">${BANNER_MARK}</svg><span>Army</span><small>0</small>`;
    this.armyDoor.setAttribute('aria-controls', 'roster');
    this.armyDoor.setAttribute('aria-expanded', 'false');
    this.armyDoor.onclick = () => this.setArmyDrawer(!this.armyDrawerOpen);

    // Build panel.
    const bp = this.el('div', 'panel');
    bp.id = 'build-panel';
    this.buildPanel = bp;
    const worksHeader = this.el('div', 'hdr', bp);
    this.buildHdr = this.el('span', '', worksHeader);
    this.buildHdr.textContent = 'Construction';
    const buildClose = this.el('button', 'drawer-close', worksHeader) as HTMLButtonElement;
    buildClose.type = 'button';
    buildClose.textContent = '×';
    buildClose.setAttribute('aria-label', 'Close construction');
    buildClose.onclick = () => {
      this.setBuildDrawer(false);
      if (this.narrow) this.setSheet('');
    };
    const industry = this.el('button', 'industry-door', worksHeader) as HTMLButtonElement;
    industry.type = 'button';
    industry.id = 'industry-btn';
    industry.textContent = 'Industry & Ages';
    industry.title = 'Plan production chains, find shortages and advance the Age';
    industry.onclick = () => this.cb.onOpenIndustry?.();
    const cats = this.el('div', '', bp);
    cats.id = 'build-cats';
    for (const c of CATEGORIES) {
      const el = this.el('button', 'cat' + (c.id === this.category ? ' on' : ''), cats) as HTMLButtonElement;
      el.type = 'button';
      el.textContent = c.label;
      el.dataset.cat = c.id;
      el.onclick = () => {
        this.category = c.id;
        // A new category is a new list to look at, not a continuation of the
        // last one: the fold goes back to shut so the top of every list is the
        // Age the player is standing in.
        this.showLater = false;
        for (const other of cats.children) other.classList.toggle('on', (other as HTMLElement).dataset.cat === c.id);
        // The list itself is rebuilt by `syncBuildList` on the next frame, and
        // only there. Two places that may rebuild it is how it came to be
        // rebuilt sixty times a second the first time.
        this.listKey = '';
      };
    }
    this.buildList = this.el('div', '', bp);
    this.buildList.id = 'build-list';
    injectVillageStyle();
    this.buildList.addEventListener('click', (ev) => {
      const button = (ev.target as HTMLElement)?.closest<HTMLButtonElement>('[data-act="village-upgrade"]');
      if (button && !button.disabled) this.cb.onVillageUpgrade?.(Number(button.dataset.prov2));
    });

    /**
     * The building panel sits exactly on top of the build menu.
     *
     * They are the same question — "what goes here" and "what is here" — asked
     * of the same corner of the screen, and one is always the wrong one to be
     * looking at. Stacking them means no third column, no layout fight with the
     * card bar, and a Close that puts the build menu straight back.
     *
     * Redrawn wholesale each frame, so every control is found by delegation off
     * one listener bound here: a handler on a node inside would be discarded
     * with the node about sixteen milliseconds later.
     */
    this.binfo = this.el('div', 'panel');
    this.binfo.id = 'binfo';
    this.binfo.style.display = 'none';
    /**
     * `mousedown`, not `click`.
     *
     * A `click` only fires when the press and the release land on the *same*
     * element, and this panel's innards are replaced several times a second as
     * the numbers move — so a rebuild that happens between a player's press and
     * their release swallows the click entirely. It is not a rare race: the
     * screenshot harness hit it fifty-three times in a row trying to press a
     * muster button. Acting on the press is both correct and what a game UI
     * should do anyway.
     */
    this.binfo.addEventListener('mousedown', (ev) => this.onBinfoClick(ev));

    // The door to the Ledger, under the works menu where the player already is.
    const ledger = this.el('div', 'seal ghost', bp);
    ledger.id = 'ledger-btn';
    ledger.textContent = 'The Ledger';
    ledger.onclick = () => this.cb.onOpenManage?.();
    ledger.onmouseenter = (ev) =>
      this.hover(ev, () =>
        this.setTip(
          'The Ledger',
          'Tax, treasury, the other crowns, your people and every chain in the town.',
          `<span class="dim">L</span>`,
          'Four pages of things somebody has already counted for you.',
        ),
      );
    ledger.onmouseleave = () => this.hideTip();

    // Roster.
    const rp = this.el('div', 'panel');
    rp.id = 'roster';
    const rh = this.el('div', 'hdr', rp);
    this.rosterSummary = this.el('span', '', rh);
    this.rosterSummary.textContent = 'Your army';
    const armyClose = this.el('button', 'drawer-close', rh) as HTMLButtonElement;
    armyClose.type = 'button';
    armyClose.textContent = '×';
    armyClose.setAttribute('aria-label', 'Close army');
    armyClose.onclick = () => {
      this.setArmyDrawer(false);
      if (this.narrow) this.setSheet('');
    };
    this.rosterList = this.el('div', '', rp);
    this.rosterList.id = 'roster-list';
    this.rosterEmpty = this.el('div', 'roster-empty', rp);
    this.rosterEmpty.textContent = 'No companies here. Select a military building to recruit troops.';

    /**
     * The chart, under the roster.
     *
     * It steers the camera directly rather than through a callback on
     * `HudCallbacks`, because moving the view is not a decision the game has to
     * agree to — it posts no command, changes no world state, and `main.ts`
     * already hands this class the scene for exactly this kind of thing.
     */
    this.minimap = new Minimap(this.root, {
      onSeek: (wx, wy) => this.scene.camera.centreOn(wx, wy),
    });

    // The unit cards. One row along the bottom, one card per company.
    this.cardBar = this.el('div', 'panel');
    this.cardBar.id = 'cards';

    /**
     * Company orders, as buttons.
     *
     * `onFormation` and `onStance` have been in `HudCallbacks` since the day
     * companies were written and nothing has ever called them: formation and
     * stance were reachable on F and G and nowhere else, so a player who never
     * read the title screen never found out that a shieldwall exists. Shown
     * only when something is selected, because an order needs somebody to give
     * it to.
     */
    this.orderBar = this.el('div', 'panel');
    this.orderBar.id = 'orders';
    this.orderBar.style.display = 'none';

    // Powers.
    const willBar = this.el('div');
    willBar.id = 'will-bar';
    this.willFill = this.el('i', '', willBar);
    // The bar wears its name. It was the one gauge on the screen with no
    // visible label — a blue line a new player had no way to read, priced in
    // the same currency every seal beneath it charges. A *sibling*, not a
    // child: the bar's clip-path swallows anything drawn outside its 7px —
    // the wall-texture bake bug in miniature.
    this.willLabel = this.el('span');
    this.willLabel.id = 'will-label';
    willBar.onmouseenter = (ev) => this.hover(ev, () => this.willTip());
    willBar.onmouseleave = () => this.hideTip();
    this.powerBar = this.el('div', 'panel');
    this.powerBar.id = 'powers';

    // The character sheet, in miniature: who he is, how far along, and the
    // battle skills he has bought. Sits beside the roster rather than on the
    // Power bar, because they are paid for out of different pockets.
    this.heroPanel = this.el('div', 'panel');
    this.heroPanel.id = 'hero-panel';
    this.heroPanel.style.display = 'none';
    // Two children, made once: the sheet is redrawn wholesale every frame, the
    // ability row is diffed like the Power bar so its click handlers survive.
    this.heroSheetEl = this.el('div', '', this.heroPanel);
    this.heroSheetEl.id = 'hero-sheet';
    // The sheet's innards are rewritten every frame; the element itself is not,
    // so one listener here outlives every redraw and every world.
    this.heroSheetEl.onmouseenter = (ev) => this.hover(ev, () => this.heroTip());
    this.heroSheetEl.onmouseleave = () => this.hideTip();
    this.heroAbilityBar = this.el('div', '', this.heroPanel);
    this.heroAbilityBar.id = 'hero-abilities';
    this.heroPanel.classList.add('wheel-panel');
    this.heroWheel = new HeroWheel({
      onAbility: (id) => this.chooseHeroAbility(id),
      onFocus: () => this.focusHero(),
      onSheet: () => {
        if (!this.w) return;
        if (this.activeCompanion) this.showArmyHeroes(this.w);
        else this.showHeroSheet(this.w);
      },
    });
    this.heroAbilityBar.appendChild(this.heroWheel.el);
    this.heroRosterButton = this.el('button', 'hero-roster-door', this.cityTools) as HTMLButtonElement;
    this.heroRosterButton.type = 'button';
    this.heroRosterButton.textContent = 'Heroes';
    this.heroRosterButton.onclick = () => { if (this.w) this.showArmyHeroes(this.w); };

    // Log, tooltip, scouting, boss card, world text, modal.
    this.logEl = this.el('div', 'panel');
    this.logEl.id = 'log';
    this.tip = this.el('div');
    this.tip.id = 'tip';
    this.scouting = this.el('div', 'panel');
    this.scouting.addEventListener('mousedown', (ev) => this.onScoutingClick(ev));
    this.scouting.id = 'scouting';
    this.bossCard = this.el('div');
    this.bossCard.id = 'boss-card';
    this.vignette = this.el('div');
    this.vignette.id = 'vignette';
    this.worldText = this.el('div');
    this.worldText.id = 'world-text';
    this.modal = this.el('div');
    this.modal.id = 'modal';
    this.fpsEl = this.el('div');
    this.fpsEl.id = 'fps';
    this.hintEl = this.el('div');
    this.hintEl.id = 'hint';

    // `main.ts` only forwards the canvas's mouse moves, so a tooltip opened over
    // a panel used to freeze where the pointer entered. The panels are in this
    // subtree, so listening here keeps it under the pointer everywhere.
    this.root.addEventListener('mousemove', (ev) => this.onMouseMove(ev));

    // After `this.tip` exists, because a press that lands in the first frame
    // would otherwise close a tooltip that has not been built yet.
    this.touchTips = new TouchTips(this.root, () => this.releaseTip());

    this.buildTabBar();
    this.rebuildBuildList();
  }

  /**
   * The bottom tab bar, and the breakpoint that turns it on.
   *
   * A width and not a device test: a desktop window dragged narrow gets the
   * phone layout, which is the only way this stays testable without a phone in
   * the room. 820 is the point at which the three desktop columns plus a usable
   * strip of map stop fitting.
   */
  private buildTabBar(): void {
    this.tabBar = this.el('div', '', this.root);
    this.tabBar.id = 'tabbar';
    const tab = (id: string, label: string, mark: string, tip?: [string, string, string]): void => {
      const el = this.el('div', 'tb', this.tabBar);
      el.dataset.tab = id;
      el.innerHTML = `<svg viewBox="0 0 16 16" width="19" height="19" aria-hidden="true">${mark}</svg>` +
        `<span>${esc(label)}</span>`;
      // `mousedown`, for the reason every other bar here uses it: this is the
      // control a player reaches for while something is on fire.
      el.addEventListener('mousedown', (ev) => {
        ev.preventDefault();
        this.setSheet(this.sheet === id ? '' : id);
      });
      /**
       * Only the tab whose effect you cannot see before pressing it.
       *
       * Build, Army, Map and Ledger are nouns that pull up the panel they name,
       * and a tooltip explaining that is a tooltip nobody needs. Menu is the
       * odd one: it is Escape, so what it does depends on what is already open,
       * and a control whose behaviour has an order to it has to say so.
       */
      if (tip) {
        el.onmouseenter = (ev) => this.hover(ev, () => this.setTip(tip[0], tip[1], '', tip[2]));
        el.onmouseleave = () => this.hideTip();
      }
    };
    tab('build', 'Build', HAMMER_MARK);
    tab('army', 'Army', BANNER_MARK);
    tab('map', 'Map', CHART_MARK);
    tab('ledger', 'Ledger', CHROME_BOOK);
    tab('menu', 'Menu', GATE_MARK, [
      'Menu',
      'Escape, on a button. It closes whatever is open and lets go of whatever is ' +
        'selected first; press it with nothing to let go of and you get Stand Down — ' +
        'back to it, <b>Save</b>, or save and leave.',
      'The only way out of a run, and the only place a save is written by hand.',
    ]);

    const mq = window.matchMedia('(max-width: 820px)');
    const apply = (): void => {
      this.narrow = mq.matches;
      this.root.classList.toggle('narrow', this.narrow);
      // Coming back to a wide window must not leave a sheet class behind: the
      // desktop rules do not mention them, so a stale one is invisible until
      // the window is narrowed again and the wrong panel opens.
      if (!this.narrow) this.setSheet('');
      // Every inline geometry the sheet contradicts has to be rewritten here,
      // or the layout that is on screen is the one for the other breakpoint.
      this.slideForColumns();
    };
    mq.addEventListener('change', apply);
    apply();
  }

  /** Open one sheet, or none. */
  private setSheet(id: string): void {
    if (id === 'ledger') {
      // The Ledger is a full-screen modal of its own and always has been. It is
      // on the bar because that is where a player looks for it, not because it
      // is a sheet.
      this.cb.onOpenManage?.();
      return;
    }
    if (id === 'menu') {
      this.pressEscape();
      return;
    }
    this.sheet = id;
    for (const k of ['build', 'army', 'map']) this.root.classList.toggle(`sheet-${k}`, k === id);
    /**
     * *A* sheet is open, whichever one.
     *
     * The five floating bars along the bottom — cards, Powers, the Will gauge,
     * the hero's skills and the order bar — are all positioned above the tab
     * bar, which is exactly the half of the screen a sheet fills. Drawn over an
     * open build list they made it unreadable: the catalogue's rows and the
     * order bar's buttons were interleaved down the same three hundred pixels.
     * They go away while a sheet is up, and this class is what says so — one
     * name, rather than fifteen selectors pairing three sheets with five bars.
     */
    this.root.classList.toggle('sheet-on', id !== '');
    // 'menu' and 'ledger' return above, so neither ever lights.
    for (const el of Array.from(this.tabBar.children) as HTMLElement[]) {
      el.classList.toggle('on', el.dataset.tab === id);
    }
  }

  /**
   * Escape, pressed by a thumb.
   *
   * Escape is the busiest key in the game and, on a phone, the only one with no
   * door: it drops a selection, puts an armed tool away, closes whatever screen
   * is up — and, once there is nothing left to close, opens Stand Down, which
   * is where **Save** and the only way out of a run live. A touch player could
   * reach none of that. An hour into a campaign the only exit was the tab's
   * close button, and the save that would have made that survivable was behind
   * a key the device does not have.
   *
   * **A synthesised keydown rather than a callback**, for the reason a tap is a
   * synthesised click in `ui/touch.ts`: every one of those meanings is already
   * written, in order, in one handler in `main.ts`, and a second copy of that
   * ladder reachable only from the tab bar would be a second set of rules about
   * what "back" means. This adds a door and no rules. It also means the button
   * behaves *exactly* as the key does — including that the first press with a
   * company selected only lets go of the company — which is worth saying in the
   * tooltip and not worth papering over.
   */
  private pressEscape(): void {
    window.dispatchEvent(
      new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, cancelable: true }),
    );
  }

  /**
   * Put the building sheet up when a building is opened, and take it down again.
   *
   * On a desktop the building panel simply covers the build menu in the same
   * column. On a phone there is no column: tapping a granary has to *open*
   * something, or the tap does nothing visible at all.
   */
  private syncSheetForBuilding(open: boolean): void {
    this.root.classList.toggle('binfo-open', open);
    if (open && this.narrow && this.sheet !== 'build') this.setSheet('build');
  }

  private setBuildDrawer(open: boolean): void {
    this.buildDrawerOpen = open;
    this.buildDoor.setAttribute('aria-expanded', String(open));
    this.buildDoor.classList.toggle('on', open);
    if (open && this.openBuilding >= 0) {
      this.setBuildingSelection(-1);
      this.cb.onBuildingClose?.();
    }
    this.hideTip();
    this.slideForColumns();
    if (this.w) this.updateBuildingPanel(this.w);
  }

  private setArmyDrawer(open: boolean): void {
    this.armyDrawerOpen = open;
    this.root.classList.toggle('army-drawer-open', open);
    this.armyDoor.classList.toggle('on', open);
    this.armyDoor.setAttribute('aria-expanded', String(open));
    this.hideTip();
  }

  /** Choosing a tool returns the ground to the player for placement. */
  private closeCatalogueForPlacement(): void {
    this.setBuildDrawer(false);
    if (this.narrow) this.setSheet('');
  }

  /** Give an open drawer the first Escape before the pause menu. */
  closeDrawer(): boolean {
    if (this.narrow && this.sheet && this.openBuilding < 0) {
      this.setSheet('');
      return true;
    }
    if (this.narrow || this.chrome.onRealm) return false;
    if (this.buildDrawerOpen) {
      this.setBuildDrawer(false);
      return true;
    }
    if (this.armyDrawerOpen) {
      this.setArmyDrawer(false);
      return true;
    }
    return false;
  }

  // ── Build menu ───────────────────────────────────────────────────────────

  /**
   * The signature the list is built from. Rebuild only when one of these moves.
   *
   * `unlockedBuildings.length` rather than its contents because the list only
   * ever grows, and a length is a cheap thing to compare sixty times a second.
   */
  private syncBuildList(w: World): void {
    const village = isVillage(w);
    setText(this.buildDoor.querySelector('span'), village ? 'Village' : 'Build');
    this.buildPanel.classList.toggle('village-mode', village);
    if (village) {
      this.clearBuildSelection();
      this.cb.onBuildSelect({ kind: 'none' });
      const now = performance.now();
      if (this.listKey === 'village' && now < this.villageReadAt) return;
      this.villageReadAt = now + 500;
      const c = this.realmState(w)?.campaign;
      const html = c ? villagePanel(c, w.config.province ?? -1) : '<p>Village buildings are included in its level plan.</p>';
      if (html !== this.villageHtml || this.listKey !== 'village') {
        const open = this.buildList.querySelector('details')?.open ?? false;
        const focused = document.activeElement === this.buildList.querySelector('.vp-upgrade');
        const scroll = this.buildList.scrollTop;
        this.buildList.innerHTML = html;
        const details = this.buildList.querySelector('details');
        if (details) details.open = open;
        this.buildList.scrollTop = scroll;
        if (focused) this.buildList.querySelector<HTMLButtonElement>('.vp-upgrade')?.focus({ preventScroll: true });
        this.villageHtml = html;
      }
      this.listKey = 'village';
      return;
    }
    const key = `${this.category}|${w.age}|${w.raceId}|${w.unlockedBuildings.length}|${this.showLater ? 1 : 0}`;
    if (key === this.listKey) return;
    /**
     * The fold is the last field of the signature, so "only the fold moved" is
     * a string comparison. It matters because the two cases want opposite
     * things from the scroll box: a player who has just opened the fold asked
     * to see what is *below* them and must not be thrown somewhere else, while
     * any other rebuild — a new category, a new Age — should land them on the
     * Age they are standing in.
     */
    const cut = (s: string): string => s.slice(0, s.lastIndexOf('|'));
    const foldOnly = this.listKey !== '' && cut(this.listKey) === cut(key);
    const top = this.buildList.scrollTop;
    this.listKey = key;
    this.rebuildBuildList();
    if (foldOnly) this.buildList.scrollTop = top;
    else this.scrollToNow();
  }

  /**
   * Open the list on the Age the player is standing in.
   *
   * The groups run up the ladder, because that is the order the ladder is
   * *learned* in — but it means that at the Hollow the first thing in the
   * Economy column is nine Kindling sheds and the two things this Age actually
   * opened are four groups below the fold of the panel. The past belongs above
   * you; you should simply not have to walk back through it every time the
   * panel opens. Clamped by the scroll box itself, so a short list at the top
   * of the ladder just sits at its end rather than jumping somewhere odd.
   */
  private scrollToNow(): void {
    const nowHdr = this.buildList.querySelector('.bage.now') as HTMLElement | null;
    this.buildList.scrollTop = nowHdr ? nowHdr.offsetTop : 0;
  }

  private rebuildBuildList(): void {
    this.buildList.innerHTML = '';
    const w = this.w;
    if (this.category === 'works') {
      setText(this.buildHdr, 'Works');
      this.addWorksItem('wall', WALL_DEF.name, WALL_DEF.cost, WALL_DEF.flavour, 'Drag to lay a line.');
      this.addWorksItem('gate', GATE_DEF.name, GATE_DEF.cost, GATE_DEF.flavour, 'Your people pass; theirs do not.');
      this.addWorksItem('road', 'Road', { stone: 2 }, 'Downhill in the useful direction.', 'Everything on it moves a third faster.');
      this.addWorksItem(
        'bridge',
        BRIDGE_DEF.name,
        BRIDGE_DEF.cost,
        BRIDGE_DEF.flavour,
        'Click the river, or drag towards the opposite bank. The full crossing is planned for you.',
      );
      this.addWorksItem('demolish', 'Demolish', {}, 'Undoing is also a craft.', 'Refunds two fifths of what went in.');
      return;
    }
    const now = w ? Math.max(1, Math.min(MAX_AGE, w.age)) : 1;
    // The category and the Age, not the word "Works" — which is also one of the
    // tabs three lines below, and a title that repeats one of its own tabs is
    // telling the player nothing at the exact moment they are choosing between
    // them.
    setText(this.buildHdr, `${categoryLabel(this.category)} · ${AGE_NAMES[now - 1] ?? ''}`);

    /**
     * Another people's building is *dropped*, not greyed.
     *
     * The other two gates are things to go and do — wait for an Age, unlock a
     * node — so they teach something by being visible. "Not ours to build" is
     * permanent, and twenty of the twenty-two race buildings in the catalogue
     * belong to peoples this run will never be. `buildings.ts` says the gate is
     * checked "again when the build menu is assembled"; it was not, so an Ashen
     * player has been scrolling past the Drowned's tide-mill since the day
     * eleven races landed.
     */
    const byAge: number[][] = [];
    for (let a = 0; a <= MAX_AGE; a++) byAge.push([]);
    for (let i = 0; i < BUILDING_TYPES.length; i++) {
      const def = BUILDING_TYPES[i];
      if (def.category !== this.category) continue;
      if (w && def.race && def.race !== w.raceId) continue;
      byAge[Math.max(1, Math.min(MAX_AGE, def.tier))].push(i);
    }

    const horizon = ageHorizon(now);
    const shownTo = this.showLater ? MAX_AGE : horizon;
    for (let a = 1; a <= shownTo; a++) {
      if (byAge[a].length === 0) continue;
      this.addAgeHeader(a, now);
      for (const i of byAge[a]) this.addBuildItem(i, w ? buildRefusal(w, i) : '');
    }

    let hidden = 0;
    for (let a = horizon + 1; a <= MAX_AGE; a++) hidden += byAge[a].length;
    if (hidden > 0 || this.showLater) this.addFoldRow(this.buildList, hidden, horizon);
    this.markBuildSelection();
  }

  /** A heading over one Age's worth of a category. The Age gate lives here. */
  private addAgeHeader(age: number, now: number): void {
    const h = ageHeading(age, now);
    const el = this.el('div', `bage ${h.tone}`, this.buildList);
    el.innerHTML =
      `<span class="ba-n">${esc(h.numeral)}</span>` +
      `<span class="ba-a">${esc(h.name)}</span>` +
      (h.state ? `<span class="ba-s">${esc(h.state)}</span>` : '');
  }

  /**
   * The fold. Named rather than numbered on the way in — "the Gilded and the
   * Hollow" is a thing a player can want; "9 more" is a chore.
   */
  private addFoldRow(parent: HTMLElement, hidden: number, horizon: number): void {
    const el = this.el('div', 'bmore', parent);
    const beyond = agesBeyond(horizon);
    el.textContent = this.showLater
      ? '▾  Enough — show what I can build'
      : `▸  ${hidden} more, in ${beyond}`;
    el.onclick = () => {
      this.showLater = !this.showLater;
      this.listKey = '';
    };
  }

  private addWorksItem(
    kind: 'wall' | 'gate' | 'road' | 'bridge' | 'demolish',
    name: string,
    cost: Record<string, number | undefined>,
    flavour: string,
    rules: string,
  ): void {
    const item = this.el('div', 'bitem', this.buildList);
    const body = this.el('div', 'bbody', item);
    (this.el('div', 'bname', body) as HTMLElement).textContent = name;
    (this.el('div', 'bcost', body) as HTMLElement).textContent = costText(cost);
    // The works keep their flavour on the row: there are five of them, they do
    // not scroll, and "drag to lay a line" is the instruction rather than a
    // colour note. Only the catalogue was long enough to pay for the cut.
    (this.el('div', 'bflav', body) as HTMLElement).textContent = flavour;
    item.dataset.kind = kind;
    item.onclick = () => {
      const sel: BuildSelection = { kind };
      this.buildSel = sel;
      this.armedPower = '';
      this.cb.onBuildSelect(sel);
      this.markBuildSelection();
      this.closeCatalogueForPlacement();
    };
    item.onmouseenter = (e) => this.hover(e, () => this.worksTip(kind, name, cost, rules, flavour));
    item.onmouseleave = () => this.hideTip();
  }

  /**
   * The works: wall, gate, road, demolition. Their numbers live in
   * `buildings.ts` next to the things they build, so they are quoted from there
   * rather than restated here.
   */
  private worksTip(
    kind: 'wall' | 'gate' | 'road' | 'bridge' | 'demolish',
    name: string,
    cost: Record<string, number | undefined>,
    rules: string,
    flavour: string,
  ): void {
    const w = this.w;
    const rows: string[] = [];
    rows.push(`Costs ${w ? costTextRich(cost, w) : costText(cost)} per tile`);
    if (kind === 'wall') {
      rows.push(`<b>${WALL_DEF.hp}</b> health a section · <b>${WALL_DEF.buildTime}s</b> of one villager's work each`);
      rows.push(`Walls can be climbed and battered. They buy time, not safety`);
    }
    if (kind === 'gate') {
      rows.push(`<b>${GATE_DEF.hp}</b> health · <b>${GATE_DEF.buildTime}s</b> of work`);
      rows.push(`Friendly and allied troops pass through during war. Enemies must break through`);
      rows.push(`The walkway above the gate remains available to defenders`);
    }
    if (kind === 'road') {
      rows.push(`Finished buildings automatically receive a <b>free access road</b> where a clear route exists`);
      rows.push(`Lay extra roads here for custom routes. Everything on a road moves about <b>a third</b> faster — yours and theirs`);
    }
    if (kind === 'bridge') {
      rows.push(`A <b>two-tile-wide deck</b> crosses rivers, deep channels and moats, with clear road landings on both banks`);
      rows.push(`The preview shows the <b>whole crossing and total price</b>. Nothing is built or spent unless the full bridge is possible`);
      rows.push(`Troops on either side can use the crossing`);
    }
    if (kind === 'demolish') rows.push(`Returns <b>two fifths</b> of what went in, rounded down`);
    this.setTip(name, rules, rows.join('<br>'), flavour);
  }

  /**
   * One entry. `refusal` is `buildRefusal`'s sentence, or `''`.
   *
   * The Age refusal is *not* printed on the row — the heading above it already
   * says which Age this is and whether it is yours, and a list where every one
   * of nine rows repeats "Not yet this Age." is the wall this pass exists to
   * pull down. What does get printed is the refusal a heading cannot carry:
   * "Not learned. Ask the Academy.", which is a thing to go and do, and which
   * is quoted from the simulation rather than written here.
   */
  /**
   * One row of the catalogue: a picture, a name, and a price.
   *
   * **The flavour line is gone from the row and lives only in the tooltip.**
   * Two lines of italic under every entry made a row ninety pixels tall, so
   * nine of a forty-building catalogue were on screen and scrolling was the
   * main verb of the panel. The tooltip has carried the same sentence all along
   * — `buildingTip` ends on `def.flavour` — so nothing was lost and the list
   * went from nine visible to twenty-one.
   *
   * The picture is the building's own art in the settlement's own vernacular,
   * which is the thing the panel was never able to answer: what this is going
   * to look like when it is standing there.
   */
  private addBuildItem(typeIdx: number, refusal: string): void {
    const def = BUILDING_TYPES[typeIdx];
    const locked = refusal !== '';
    const ageOnly = locked && refusal === 'Not yet this Age.';
    const item = this.el('div', 'bitem' + (locked ? ' locked' : ''), this.buildList);
    const thumb = buildingThumb(this.scene.bank, typeIdx);
    if (thumb) {
      const box = this.el('div', 'bthumb', item);
      const img = document.createElement('img');
      img.src = thumb;
      img.alt = '';
      box.appendChild(img);
    }
    const body = this.el('div', 'bbody', item);
    (this.el('div', 'bname', body) as HTMLElement).textContent = def.name;
    const costEl = this.el('div', 'bcost', body);
    costEl.textContent = costText(def.cost);
    if (locked && !ageOnly) (this.el('div', 'block', body) as HTMLElement).textContent = refusal;
    item.dataset.build = String(typeIdx);
    // A locked entry is still a thing to read — the tooltip is where the ladder
    // is actually taught — but arming it would only buy a refused click and a
    // granary the player did not want under their cursor.
    if (!locked) {
      item.onclick = () => {
        const sel: BuildSelection = { kind: 'building', typeIdx };
        this.buildSel = sel;
        this.armedPower = '';
        this.cb.onBuildSelect(sel);
        this.markBuildSelection();
        this.closeCatalogueForPlacement();
      };
    }
    item.onmouseenter = (e) => this.hover(e, () => this.buildingTip(typeIdx));
    item.onmouseleave = () => this.hideTip();
  }

  private markBuildSelection(): void {
    for (const child of this.buildList.children) {
      const e = child as HTMLElement;
      const isBuild = this.buildSel.kind === 'building' && e.dataset.build === String(this.buildSel.typeIdx);
      const isWorks = e.dataset.kind !== undefined && e.dataset.kind === this.buildSel.kind;
      e.classList.toggle('on', isBuild || isWorks);
    }
  }

  clearBuildSelection(): void {
    this.buildSel = { kind: 'none' };
    this.markBuildSelection();
  }

  /** A recipe's Plan button uses the same placement selection as the catalogue. */
  planBuilding(typeId: string, w: World): boolean {
    const typeIdx = BUILDING_TYPES.findIndex((t) => t.id === typeId);
    if (typeIdx < 0) return false;
    const refusal = buildRefusal(w, typeIdx);
    if (refusal) { this.hint(refusal, 5); return false; }
    this.openBuilding = -1;
    this.buildSel = { kind: 'building', typeIdx };
    this.clearArmedPower();
    this.cb.onBuildSelect(this.buildSel);
    this.markBuildSelection();
    this.closeCatalogueForPlacement();
    return true;
  }

  clearArmedPower(): void {
    this.armedPower = '';
    this.armedHeroAbility = '';
  }

  // ── The building panel ───────────────────────────────────────────────────

  /** Open the panel on a building slot, or −1 to close it. */
  setBuildingSelection(idx: number): void {
    this.openBuilding = idx;
    if (idx >= 0) {
      // Opening a building is choosing to look rather than to place. Leaving a
      // build selection armed underneath means the next click on the map drops
      // a granary on the thing the player was reading about.
      this.buildSel = { kind: 'none' };
      this.markBuildSelection();
    }
  }

  get selectedBuilding(): number {
    return this.openBuilding;
  }

  private onBinfoClick(ev: MouseEvent): void {
    const el = (ev.target as HTMLElement | null)?.closest('[data-bact]') as HTMLElement | null;
    if (!el || el.classList.contains('off')) return;
    const w = this.w;
    const i = this.openBuilding;
    if (!w || i < 0) return;
    const a = Number(el.dataset.a ?? '0');
    switch (el.dataset.bact) {
      case 'close':
        this.setBuildingSelection(-1);
        this.cb.onBuildingClose?.();
        break;
      case 'upgrade':
        this.cb.onBuildingUpgrade?.(i);
        break;
      case 'repair':
        this.cb.onRepair?.(i, isRepairOrdered(w, i));
        break;
      case 'demolish':
        this.cb.onBuildingDemolish?.(w.buildings.tx[i], w.buildings.ty[i]);
        this.setBuildingSelection(-1);
        this.cb.onBuildingClose?.();
        break;
      case 'recruit':
        this.cb.onRecruit(a);
        break;
      case 'drills':
        // The drill list's fold. The panel is compared against its own last
        // HTML before it is written, so flipping this is enough to redraw it.
        this.showLaterDrills = !this.showLaterDrills;
        break;
      default:
        break;
    }
  }

  /**
   * Which of the two stacked panels is up — the catalogue or the building.
   *
   * **On a narrow screen this writes no inline `display` at all**, and that is
   * the point of the method. The sheet classes decide what is open there, and
   * an inline style beats a class: setting one here pinned the building panel
   * open over the map with no tab able to shut it, which is the sort of fault
   * that only appears on the layout nobody is looking at.
   */
  private showBuildingPanel(open: boolean, w: World): void {
    this.syncSheetForBuilding(open);
    const hasColumn = !w.battle && (open || this.buildDrawerOpen);
    if (this.root.classList.contains('city-column-open') !== hasColumn) {
      this.root.classList.toggle('city-column-open', hasColumn);
      this.slideForColumns();
    }
    if (this.narrow) {
      this.binfo.style.display = '';
      this.buildPanel.style.display = '';
      return;
    }
    this.binfo.style.display = open ? 'flex' : 'none';
    this.buildPanel.style.display = !open && !w.battle && this.buildDrawerOpen ? 'flex' : 'none';
  }

  private updateBuildingPanel(w: World): void {
    const i = this.openBuilding;
    const live = i >= 0 && w.buildings.slots.alive[i] === 1 && !w.battle && !isVillage(w);
    if (!live) {
      if (this.openBuilding !== -1) this.openBuilding = -1;
      this.showBuildingPanel(false, w);
      return;
    }
    this.showBuildingPanel(true, w);
    // Only when it actually says something different. Most frames it does not,
    // and replacing a subtree the pointer is sitting on is not free — in
    // attention as much as in layout.
    const html = buildingPanelHtml(w, i, this.showLaterDrills);
    if (this.binfoHtml !== html) {
      this.binfoHtml = html;
      this.binfo.innerHTML = html;
    }
  }

  private binfoHtml = '';

  /**
   * Where the three floating panels start, given whether there is a build menu
   * beside them.
   *
   * **On a narrow screen this writes no inline `left` at all**, for the reason
   * `showBuildingPanel` writes no inline `display`: an inline style beats a
   * class, so a hard `314px` written here pinned the log, the scouting report
   * and the card bar into a 76-pixel gutter down the right edge of a 390-pixel
   * phone — the scouting panel measured 72 wide and 330 tall, a column of
   * unreadable text sat on top of the map, swallowing every touch aimed at it.
   * The sheet already says `left: 4px` for all three; this simply stops
   * contradicting it.
   *
   * Called again whenever the breakpoint moves, because a window dragged wide
   * needs its columns back.
   */
  private slideForColumns(): void {
    const column = !this.battleMode && (this.openBuilding >= 0 || this.buildDrawerOpen);
    const x = this.narrow ? '' : column ? '344px' : '12px';
    this.root.style.setProperty('--hud-left', column ? '344px' : '12px');
    this.logEl.style.left = x;
    this.scouting.style.left = x;
    // No build menu on a field, so the cards get that ground too.
    this.cardBar.style.left = x;
  }

  // ── Per-frame update ─────────────────────────────────────────────────────

  update(w: World, dt: number, fps: number, showFps: boolean): void {
    this.w = w;

    // Before the battle-mode switch below, because that switch hides resource
    // chips by querying for them — and on the first frame of a run that *opens*
    // in battle mode there were none to find yet, so a battlefield showed a
    // full row of settlement resources for the rest of its life.
    this.updateResources(w, dt);

    // Battle mode has no economy, so it gets none of the economy UI. Showing an
    // empty build menu and a row of zeroes is worse than showing nothing.
    //
    // Which resource chips are on screen is decided in one place only —
    // `updateResources`, above, which runs every frame and already knows about
    // both the battle and the Age. This block used to set their display too,
    // and two authorities for one style property is how a chip ends up hidden
    // in a settlement that has just entered its second Age.
    const battle = !!w.battle;
    if (this.battleMode !== battle) {
      this.battleMode = battle;
      this.root.classList.toggle('battle-mode', battle);
      this.buildDoor.hidden = battle;
      const pop = this.resBar.querySelector('[data-res="pop"]') as HTMLElement | null;
      if (pop) pop.style.display = battle ? 'none' : 'flex';
      // The dial's visibility is `updateTax`'s and only `updateTax`'s — it was
      // written from here too, and an inline `display: flex` set once on the
      // first frame is what beat the realm screen's stylesheet rule and left a
      // tax band on a screen with no town on it.
      this.slideForColumns();
    }

    // One read of the company list and one pass over its members, shared by the
    // roster and the cards. They show the same companies and must never
    // disagree either about which those are or about how they are doing.
    const mine = w.companies.filter((c) => c.team === Team.Player && !c.dead);
    const reads = mine.map((c) => readCompany(w, c));

    // The town-facing readouts read the town, wherever the player is standing.
    // See `townWorld`.
    const town = this.townWorld(w);
    this.updateTax(town);
    this.updatePhase(w);
    this.updatePowers(w);
    this.updateHero(w);
    this.updateRoster(mine, reads);
    this.updateCards(w, mine, reads);
    this.updateLog();
    this.updateScouting(w);
    this.updateBossCard(w);
    this.updateWorldText();
    this.updateFps(w, fps, showFps);
    // Before the affordability pass, which walks whatever the list currently
    // holds — and which would otherwise spend a frame pricing a list built for
    // the Age before last.
    this.syncBuildList(w);
    this.updateBuildAffordability(w);
    this.updateBuildingPanel(w);
    this.updateChrome();
    this.updateOrders(w, mine);
    this.updateAlerts(town, dt);
    // After `updateChrome`, which is what knows whether we are looking at the
    // realm — there is no ground to chart on the strategic map, and the world
    // map draws its own inset.
    this.minimap.setVisible(!this.chrome.onRealm);
    if (!this.chrome.onRealm) this.minimap.update(w, this.scene.camera, dt);
    this.refreshTip(dt);
  }

  /**
   * What the frame loop is doing, pushed in before the frame is drawn.
   *
   * Separate from `update` because none of it is world state: the HUD is being
   * *told* whether the loop is paused, not reading it off a simulation that has
   * no idea. Called from `main.ts` every frame.
   */
  setChrome(c: HudChrome): void {
    this.chrome = c;
  }

  /**
   * The other world. See `cityW`.
   *
   * Both may be null (a standalone siege, a battle opened from the menu), in
   * which case every reader below falls back to the world being drawn and the
   * strip behaves exactly as it did before this existed.
   */
  setWorlds(city: World | null, realm: World | null): void {
    this.cityW = city;
    this.realmW = realm;
  }

  /**
   * Which world the *town* half of the strip is about.
   *
   * On the realm map the world being drawn is the realm, and a settlement
   * alert, a tax band or an Age read off it is a reading of a world with no
   * settlement in it. Everywhere else this is the world being drawn, so the
   * settlement screen and the battlefield are untouched by any of this.
   */
  private townWorld(w: World): World {
    return this.chrome.onRealm && this.cityW ? this.cityW : w;
  }

  /** The campaign state the clock and the purse read, or null outside one. */
  private realmState(w: World): World | null {
    return w.campaign ? w : this.realmW;
  }

  /**
   * The mix panel: three sliders over the settings `Audio.setSettings` has
   * always taken and, until this button, nothing but the mute key ever called.
   * Built on open and left alone — a panel rebuilt per frame would drop the
   * slider mid-drag, the same lesson as the market form's steppers.
   */
  private toggleVolume(): void {
    if (this.volPanel) {
      this.volPanel.remove();
      this.volPanel = null;
      return;
    }
    const current = this.cb.audioSettings?.();
    if (!current) return;
    const p = this.el('div', 'vol-panel', this.root);
    const row = (label: string, key: 'master' | 'effects' | 'ambience', value: number): void => {
      const r = this.el('div', 'vol-row', p);
      const l = this.el('span', '', r);
      l.textContent = label;
      const input = document.createElement('input');
      input.type = 'range';
      input.min = '0';
      input.max = '1';
      input.step = '0.05';
      input.value = String(value);
      input.oninput = () => this.cb.onAudioSettings?.({ [key]: Number(input.value) });
      r.appendChild(input);
    };
    row('Master', 'master', current.master);
    row('Effects', 'effects', current.effects);
    row('Ambience', 'ambience', current.ambience);

    // Display feel, in the same panel: the settings NEXT.md #8 said were
    // "implemented but only reachable from code". Shake is a slider because
    // intensity is a taste; the numbers are a yes or a no.
    const disp = this.cb.displaySettings?.();
    if (disp) {
      const cap = this.el('div', 'vol-cap', p);
      cap.textContent = 'Display';
      const r = this.el('div', 'vol-row', p);
      const l = this.el('span', '', r);
      l.textContent = 'Shake';
      const shake = document.createElement('input');
      shake.type = 'range';
      shake.min = '0';
      shake.max = '2';
      shake.step = '0.1';
      shake.value = String(disp.shake);
      shake.oninput = () => this.cb.onDisplaySettings?.({ shake: Number(shake.value) });
      r.appendChild(shake);
      const nr = this.el('div', 'vol-row', p);
      const nl = this.el('span', '', nr);
      nl.textContent = 'Numbers';
      const numbers = document.createElement('input');
      numbers.type = 'checkbox';
      numbers.checked = disp.numbers;
      numbers.onchange = () => this.cb.onDisplaySettings?.({ numbers: numbers.checked });
      nr.appendChild(numbers);
    }
    const rect = this.volBtn.getBoundingClientRect();
    const host = this.root.getBoundingClientRect();
    p.style.left = `${Math.max(8, rect.left - host.left - 130)}px`;
    p.style.top = `${rect.bottom - host.top + 6}px`;
  }

  private updateChrome(): void {
    const c = this.chrome;
    this.pauseBtn.classList.toggle('on', c.paused);
    setText(this.pauseBtn.querySelector('.cg'), c.paused ? '▶' : '❚❚');
    setText(this.speedBtn.querySelector('.cg'), `×${c.timescale === 0.5 ? '½' : c.timescale}`);
    this.speedBtn.classList.toggle('on', c.timescale > 1);
    this.muteBtn.classList.toggle('off', c.muted);
    // Markup, not text: the mark is drawn now, and `setHtml` leaves the node
    // alone on every frame where the state has not moved.
    setHtml(this.muteBtn.querySelector('.cg'), chromeMark(c.muted ? 'mute' : 'sound'));
    this.swapBtn.style.display = c.hasRealm ? 'flex' : 'none';
    this.swapBtn.classList.toggle('on', c.onRealm);
    /**
     * The strip is on both maps now, and the rest of the HUD is not.
     *
     * One class, and the stylesheet decides what a realm screen keeps: the
     * resource chips, the alert cluster, the clock and the chrome bar stay,
     * and the town's own controls — the tax dial and the horn — go, because
     * both of them post a command to the world being drawn and the world being
     * drawn is the realm. See the `on-realm` block in `style.ts`.
     */
    this.root.classList.toggle('on-realm', c.onRealm);
    /**
     * The horn is a siege clock's control, and a campaign has no siege clock.
     *
     * In a campaign it has always been drawn disabled with the word "No siege"
     * on it — a button that exists to say it does nothing, on the one bar that
     * has to carry the whole of both screens. It goes on *both* maps rather
     * than only on the realm, because "the same furniture in the same places"
     * has to include the furniture that is not there.
     *
     * Not in a battle: the horn is what ends deployment, and that is the one
     * place in a campaign where it is the most important control on the screen.
     */
    this.root.classList.toggle('no-horn', c.hasRealm && !this.battleMode);
  }

  /**
   * The alert cluster.
   *
   * `Alerts.read` is throttled to twice a second and this pass only rewrites
   * the markup when the *set* of ids changes — which is the same lesson as the
   * order bar and the hero panel. A chip replaced between a player's press and
   * their release is a chip that cannot be clicked, and these are the chips a
   * player clicks while something is on fire.
   */
  private updateAlerts(w: World, dt: number): void {
    const list = this.alerts.read(w, dt);
    this.shownAlerts = list;
    const key = list.map((a) => `${a.id}:${a.tone}:${a.title}`).join('|');
    setHtml(
      this.alertBar,
      list
        .map(
          (a) =>
            `<div class="alert ${a.tone}" data-alert="${a.id}" title="">` +
            `<svg viewBox="0 0 16 16" width="17" height="17" aria-hidden="true">${MARKS[a.mark]}</svg>` +
            `</div>`,
        )
        .join(''),
    );
    // The hovers are rebound whenever the markup was rewritten, and only then.
    // `setHtml` says whether it wrote, which is exactly the signal needed.
    if (key !== this.alertKey) {
      this.alertKey = key;
      for (const el of Array.from(this.alertBar.children) as HTMLElement[]) {
        const a = list.find((x) => x.id === el.dataset.alert);
        if (!a) continue;
        el.onmouseenter = (ev) =>
          this.hover(ev, () =>
            this.setTip(
              a.title,
              esc(a.body),
              a.fix ? `<span class="dim">${esc(a.fix)}</span>` : '',
              a.at ? 'Click to go there.' : a.open ? 'Click to open it.' : '',
            ),
          );
        el.onmouseleave = () => this.hideTip();
      }
    }
  }

  /**
   * A chip was pressed. Go to the thing, or open the screen that mends it.
   *
   * `mousedown` for the reason the building panel uses it: this bar is rewritten
   * whenever the set of warnings moves, which during a siege is every half
   * second, and a `click` needs the press and the release to land on the same
   * element.
   */
  private onAlertClick(ev: Event): void {
    const el = (ev.target as HTMLElement).closest('[data-alert]') as HTMLElement | null;
    if (!el) return;
    ev.preventDefault();
    const a = this.shownAlerts.find((x) => x.id === el.dataset.alert);
    if (!a) return;
    if (a.at) {
      /**
       * The cluster is on the realm map now, and every one of its chips is
       * about the town — so a chip that says "go there" has to go *home* first.
       *
       * The swap is synchronous and `adoptWorld` resets the camera, so the
       * centring has to come after it or it is undone on the same frame. That
       * ordering is the whole of why this is two statements and not one.
       */
      if (this.chrome.onRealm) this.cb.onSwapView?.();
      this.scene.camera.centreOn(a.at[0], a.at[1]);
      return;
    }
    if (a.open === 'ledger') this.cb.onOpenManage?.();
    else if (a.open === 'sheet') this.cb.onOpenSheet?.();
  }

  /**
   * Formation, stance and hold, on buttons, for whatever is selected.
   *
   * The labels say what the order *is* now and clicking cycles it, which is
   * what F and G have always done — the button is the same door, not a second
   * mechanism, and every one of them posts the same command the key posts.
   */
  private updateOrders(w: World, mine: Company[]): void {
    const sel = mine.filter((c) => this.selectedCompanies.includes(c.id));
    /**
     * `orders-on` is the authority and the inline `display` is only the desktop
     * shorthand for it — the fourth time on this layout that an inline style
     * has had to be handed back to the stylesheet.
     *
     * A narrow sheet fills the bottom half of the screen, so the order bar has
     * to be *hidden while one is open* even though a company is still selected;
     * an inline `flex` written here beat that rule and drew the five order
     * buttons straight through the middle of the build catalogue.
     */
    this.root.classList.toggle('orders-on', sel.length > 0);
    if (sel.length === 0) {
      this.orderBar.style.display = this.narrow ? '' : 'none';
      return;
    }
    this.orderBar.style.display = this.narrow ? '' : 'flex';
    const lead = sel[0];
    const one = sel.length === 1;
    const label = one ? lead.name : `${sel.length} companies`;
    let mounted = 0, assigned = 0;
    for (const company of sel) for (const handle of company.members) {
      const i = w.units.resolve(handle);
      if (i < 0) continue;
      if (w.units.wallTile[i] >= 0) assigned++;
      if (standingOn(w, i) >= 0) mounted++;
    }
    const atWall = mounted > 0 || assigned > 0;

    // Through `setHtml`, for the reason the hero panel is: these are three
    // buttons on a bar that was rebuilt every frame, and a button replaced
    // between a player's press and their release is a button that cannot be
    // pressed. The labels change only when an order does, which is exactly when
    // the markup should be rewritten and no more often.
    setHtml(
      this.orderBar,
      `<span class="ow">${esc(label)}</span>` +
        `<span class="obtn" data-ord="form">${esc(FORMATION_NAMES[lead.formation] ?? 'Formation')}` +
        `<i>${esc(keyLabel(KEY_FORMATION))}</i></span>` +
        `<span class="obtn" data-ord="stance">${esc(STANCE_NAMES[lead.stance] ?? 'Stance')}` +
        `<i>${esc(keyLabel(KEY_STANCE))}</i></span>` +
        `<span class="obtn" data-ord="hold">Hold<i>${esc(keyLabel(KEY_HOLD))}</i></span>` +
        /**
         * Shieldwall and Rally, which had a key each and no button at all.
         *
         * They are not new orders and they are not a shortcut this bar invents:
         * V and B post exactly these two commands, and the two cycling buttons
         * beside them already walk past both. What they were missing is a
         * *door* — on a phone, "take the shieldwall" meant tapping Formation
         * until it came round, which is a thing you do while being charged.
         */
        `<span class="obtn${lead.formation === Formation.Shieldwall ? ' on' : ''}" data-ord="wall">` +
        `Shieldwall<i>${esc(keyLabel(KEY_SHIELDWALL))}</i></span>` +
        `<span class="obtn${lead.stance === Stance.Rally ? ' on' : ''}" data-ord="rally">` +
        `Rally<i>${esc(keyLabel(KEY_RALLY))}</i></span>` +
        (atWall ? '<span class="wall-count" aria-live="polite"></span>' +
          '<button type="button" class="obtn" data-ord="leave-wall">Leave wall</button>' : '') +
        /**
         * The hint is about the pointer the player actually has. It read
         * "right-click to move" on a device with no right button and no click,
         * which is not a small thing to get wrong: it is the only place the
         * game says how to order a company anywhere.
         */
        `<span class="ohint">${
          this.narrow
            ? 'Hold a wall to climb · hold open ground to move'
            : 'Right-click a wall to climb · open ground to move'
        }</span>`,
    );
    const wallCount = this.orderBar.querySelector('.wall-count');
    if (wallCount) setText(wallCount as HTMLElement, assigned > 0
      ? `On wall: ${mounted}/${Math.max(assigned, mounted)}` : `${mounted} coming down`);

    const bind = (ord: string, fire: () => void, tip: () => void) => {
      const el = this.orderBar.querySelector(`[data-ord="${ord}"]`) as HTMLElement | null;
      if (!el) return;
      el.onclick = fire;
      el.onmouseenter = (ev) => this.hover(ev, tip);
      el.onmouseleave = () => this.hideTip();
    };
    const list = (names: Record<number, string>, at: number) =>
      Object.keys(names)
        .map(Number)
        .sort((a, b) => a - b)
        .map((i) => `${i === at ? '▸' : '·'} ${esc(names[i])}`)
        .join('<br>');
    const count = (names: Record<number, string>) => Object.keys(names).length;

    bind('leave-wall', () => this.cb.onLeaveWall?.(), () => this.setTip(
      'Leave wall', 'Walk down from the rampart and regroup on the ground.',
      'You can also right-click open ground to come down and move there.', '',
    ));

    bind(
      'form',
      () => this.cb.onFormation((lead.formation + 1) % count(FORMATION_NAMES)),
      () =>
        this.setTip(
          `Formation — ${FORMATION_NAMES[lead.formation]}`,
          'How they stand. Click to take the next one.',
          list(FORMATION_NAMES, lead.formation),
          'A line that is the wrong shape is a line that is already losing.',
        ),
    );
    bind(
      'stance',
      () => this.cb.onStance((lead.stance + 1) % count(STANCE_NAMES)),
      () =>
        this.setTip(
          `Stance — ${STANCE_NAMES[lead.stance]}`,
          'What they do when nobody is telling them. Click to take the next one.',
          list(STANCE_NAMES, lead.stance),
          '',
        ),
    );
    bind(
      'hold',
      () => this.cb.onStance(Stance.Hold),
      () => this.setTip('Hold', 'They stand where they are and do not chase.', '', 'The hardest order to give and the one that wins sieges.'),
    );
    bind(
      'wall',
      () => this.cb.onFormation(Formation.Shieldwall),
      () =>
        this.setTip(
          'Shieldwall',
          'Shoulder to shoulder, shields up. Slow, and very hard to move.',
          list(FORMATION_NAMES, lead.formation),
          'Every arrow that hits a shield is an arrow that hit nobody.',
        ),
    );
    bind(
      'rally',
      () => this.cb.onStance(Stance.Rally),
      () =>
        this.setTip(
          'Rally',
          'Back to the banner, and stand there. Breaks off whatever they are chasing.',
          list(STANCE_NAMES, lead.stance),
          'A company that has run too far is a company that is already surrounded.',
        ),
    );
    void w;
  }

  /** Every binding in the game, and the promise that none of them is required. */
  showKeys(): void {
    const groups = KEY_HELP.map(
      (g) =>
        `<div class="kgroup"><div class="kg">${esc(g.group)}</div>` +
        g.rows
          .map(([k, what]) => `<div class="krow"><span class="kk">${esc(k)}</span><span>${esc(what)}</span></div>`)
          .join('') +
        `</div>`,
    ).join('');
    this.showSheet(
      `<h1>The Keyboard</h1>` +
        `<div class="sub">Every one of these is also a button. Nothing in this game needs a key.</div>` +
        `<div class="kgrid">${groups}</div>` +
        `<div style="margin-top:18px"><span class="seal ghost" data-close>Close</span></div>`,
    );
    const close = this.modal.querySelector('[data-close]') as HTMLElement | null;
    if (close) close.onclick = () => this.hideModal();
  }

  private updateResources(w: World, dt: number): void {
    // A swap means the previous run's stocks are not a baseline for this one's.
    if (this.rateWorld?.deref() !== w) {
      this.rateWorld = new WeakRef(w);
      this.lastResSnapshot = {};
      this.rateSample = {};
      this.rateTimer = 0;
    }
    this.rateTimer += dt;
    const sampleNow = this.rateTimer >= 1;
    if (sampleNow) this.rateTimer = 0;

    // Will is sampled on the same clock as the resources. The rate it regathers
    // at is a private constant of `systems/powers.ts` scaled by a faction bonus,
    // a relic or three and the phase — measuring it is both simpler than
    // restating that sum and correct when the sum changes.
    if (sampleNow) {
      const prev = this.lastResSnapshot.will ?? w.will;
      this.rateSample.will = w.will - prev;
      this.lastResSnapshot.will = w.will;
    }

    /**
     * The strip is filtered by Age.
     *
     * The recipe ladder runs to eighteen resources and eighteen bars is not a
     * readout. `hudResources` decides which ones are a live decision at this
     * Age — see `data/ages.ts` for the rule and the argument for it — and a
     * battlefield keeps only Souls, because a field has no economy.
     *
     * Every chip is still *built*, and every one is still sampled for its rate,
     * so a resource that comes back into view brings its history with it rather
     * than a dash. Only `display` moves.
     *
     * **The race is passed, and it is not optional.** The one-argument form is
     * the generic settlement's list and deliberately shows no private currency,
     * so a race whose whole economy turns on one — the mana races have all four
     * of their sources wired — would have played the entire run unable to see
     * the number it was spending. The ceiling moves from twelve bars to thirteen
     * for exactly those races, at one Age each, which `hudResources` allows on
     * purpose and pins with a test.
     */
    /**
     * The crown's purse, when the strip is looking at the realm.
     *
     * > *"the recourses menu should also appear in the campaign map, the same
     * > way it gets confusing that its different"*
     *
     * The same chips — the same DOM nodes, the same drawn icons, the same
     * order, the same corner of the glass — reading the other purse. A realm
     * and a town do not share a treasury and never have: the crown's gold is
     * `nations[me].treasury.gold` and the town's is `res.gold`, and they move
     * on entirely different clocks. What the player was owed is not one number
     * but one *bar*, so that "how much gold have I" is the same glance on both
     * screens rather than a stat row on one and a chip on the other.
     *
     * The rate is a **month**, not a second, because a council is what pays a
     * crown. `realmPurse` reads `projectedIncome` for it rather than measuring
     * a delta, since a figure that only moves eight times a year cannot be
     * sampled per second without reading zero almost always.
     */
    const rs = this.realmState(w);
    const purse: RealmPurse | null =
      (this.chrome.onRealm || isVillage(w)) && rs?.campaign ? realmPurse(rs.campaign, rs.campaign.playerNation) : null;
    const shown = new Set(hudResources(w.age, w.raceId, w.res.souls, w.res));
    const battle = !!w.battle;

    for (const k of RESOURCE_KEYS) {
      let el = this.resBar.querySelector(`[data-res="${k}"]`) as HTMLElement | null;
      if (!el) {
        el = document.createElement('div');
        el.className = 'res';
        el.dataset.res = k;
        el.innerHTML = `<span class="glyph"></span><span><span class="val"></span> <span class="rate"></span></span>`;
        // In front of the tax dial, so the strip reads in `RESOURCE_KEYS` order
        // — timber first, souls last — instead of the reverse, which is what
        // inserting each new chip at the head of the bar used to give.
        this.resBar.insertBefore(el, this.taxDial);
        el.onmouseenter = (ev) => this.hover(ev, () => this.resourceTip(k));
        el.onmouseleave = () => this.hideTip();
      }
      // The mark is a drawing and is written once, whichever purse is showing.
      setHtml(el.querySelector('.glyph'), resourceIcon(k));
      if (purse) {
        const chip = purse.chips.find((c) => c.key === k);
        el.style.display = chip ? 'flex' : 'none';
        if (chip) {
          setText(el.querySelector('.val'), String(Math.floor(chip.have)));
          setText(
            el.querySelector('.rate'),
            Math.abs(chip.perMonth) < 0.5
              ? ''
              : `${chip.perMonth > 0 ? '+' : ''}${Math.round(chip.perMonth)}/mo`,
          );
          // Neither warehouses nor famine are realm quantities. A crown that is
          // out of timber buys it in at a price; it is not "low", it is dearer.
          el.classList.remove('low');
          el.classList.remove('capped');
        }
        continue;
      }
      // A battlefield keeps only Souls — and only for a settlement that has
      // them on its strip at all; spoils read the same rule as the town.
      el.style.display = (battle ? k === 'souls' && shown.has('souls') : shown.has(k)) ? 'flex' : 'none';
      const val = w.res[k];
      if (sampleNow) {
        const prev = this.lastResSnapshot[k] ?? val;
        this.rateSample[k] = val - prev;
        this.lastResSnapshot[k] = val;
      }
      const cap = capOf(w, k);
      setText(el.querySelector('.val'), String(Math.floor(val)));
      const rate = this.rateSample[k] ?? 0;
      setText(
        el.querySelector('.rate'),
        Math.abs(rate) < 0.05 ? '' : `${rate > 0 ? '+' : ''}${rate.toFixed(1)}`,
      );
      el.classList.toggle('low', val < 20 && (k === 'grain' || k === 'timber'));
      // Gold is not held in warehouses, so it can never be at the brim: it used
      // to light up as capped the moment it passed `resCap`, which is a lie the
      // simulation has never told.
      el.classList.toggle('capped', Number.isFinite(cap) && val >= cap - 1);
    }

    // Population and morale live on the same strip.
    let pop = this.resBar.querySelector('[data-res="pop"]') as HTMLElement | null;
    if (!pop) {
      pop = document.createElement('div');
      pop.className = 'res';
      pop.dataset.res = 'pop';
      pop.innerHTML = `<span class="glyph">${folkIcon()}</span><span><span class="val"></span> <span class="rate"></span></span>`;
      this.resBar.insertBefore(pop, this.taxDial);
      pop.onmouseenter = (ev) => this.hover(ev, () => this.popTip());
      pop.onmouseleave = () => this.hideTip();
    }
    /**
     * The people chip carries the levies on the realm.
     *
     * The same chip and the same drawn folk mark, because it is the same fact
     * at the other scale: how many of yours there are, and which way the number
     * is going. A crown's is men in the bank and what the countryside sends a
     * month — two quantities of different kinds, which is the confusion the
     * realm column's own note is written about, and the chip says which is
     * which by putting `/mo` on exactly one of them.
     */
    if (purse) {
      pop.style.display = 'flex';
      setText(pop.querySelector('.val'), String(Math.floor(purse.levy.have)));
      setText(pop.querySelector('.rate'), `+${Math.round(purse.levy.perMonth)}/mo`);
    } else {
      setText(pop.querySelector('.val'), `${w.pop}/${w.popCap}`);
      setText(pop.querySelector('.rate'), moraleWord(w.morale));
    }

    /**
     * The binding, on the strip.
     *
     * Every race's muster answers to one constraint — beds, the mana pool, the
     * consecrated names, the pens — and `bindingReport` has known how to say so
     * since the bindings landed, while no screen ever asked it. The Litany was
     * playing blind against its own pool. One chip, only for the races whose
     * binding is a number of its own: `housing` is already the pop chip.
     */
    const report =
      !purse && w.raceId && race(w.raceId).binding !== 'housing' ? bindingReport(w) : null;
    let bind = this.resBar.querySelector('[data-res="binding"]') as HTMLElement | null;
    if (!bind && report) {
      bind = document.createElement('div');
      bind.className = 'res';
      bind.dataset.res = 'binding';
      bind.innerHTML = `<span class="glyph">⚭</span><span><span class="val"></span> <span class="rate"></span></span>`;
      this.resBar.insertBefore(bind, this.taxDial);
      bind.onmouseenter = (ev) =>
        this.hover(ev, () => {
          const r = bindingReport(w);
          return `<b>${esc(r.name)}</b><br>${esc(r.line)}${r.blocked ? `<br><b style="color:#d1352c">${esc(r.blocked)}</b>` : ''}`;
        });
      bind.onmouseleave = () => this.hideTip();
    }
    if (bind) {
      // A binding is a muster constraint of the *town*. The realm musters out
      // of levies, which the people chip above is already showing.
      bind.style.display = report && !battle && !purse ? 'flex' : 'none';
      if (report) {
        setText(bind.querySelector('.val'), `${Math.floor(report.have)}/${Math.floor(report.cap)}`);
        setText(bind.querySelector('.rate'), report.name.toLowerCase());
        bind.classList.toggle('low', !!report.blocked);
      }
    }
  }

  /**
   * The tax dial: five notches, the lit one is the band you are on.
   *
   * Notches above Fair are lit in oxblood because they are the ones bought with
   * goodwill, and the colour is the fastest way to say so.
   */
  private updateTax(w: World): void {
    /**
     * One authority for whether the dial is on screen, and this is it.
     *
     * A battlefield has no economy and the realm map has no town — and the
     * notches post `Cmd.SetTaxPolicy` onto the queue of the world being drawn,
     * which on the map is the realm. Hidden rather than disabled, because a
     * control that is present and refuses is worse than one that is elsewhere:
     * the Ledger's Realm page carries the same five bands with the projection
     * on each, and it is one button away from either screen now.
     */
    this.taxDial.style.display = this.battleMode || this.chrome.onRealm || isVillage(w) ? 'none' : 'flex';
    const band = taxBand(w.taxPolicy);
    setText(this.taxDial.querySelector('.tk'), band.name);
    this.taxDial.classList.toggle('live', typeof this.cb.onTaxPolicy === 'function');
    for (let i = 0; i < this.taxNotches.children.length; i++) {
      const notch = this.taxNotches.children[i] as HTMLElement;
      notch.classList.toggle('on', i <= w.taxPolicy);
      notch.classList.toggle('hard', i > 1);
    }
  }

  private updatePhase(w: World): void {
    if (w.battle) {
      const b = w.battle;
      setText(this.waveLabel, b.scenario.name);
      setText(this.phaseName, b.deploying ? 'Deploy' : b.outcome !== 0 ? 'Done' : 'Battle');
      const m = Math.floor(b.elapsed / 60);
      const s = Math.floor(b.elapsed % 60);
      setText(this.phaseTimer, `${m}:${s.toString().padStart(2, '0')}`);
      this.phaseTimer.classList.remove('urgent');
      const horn = this.resBar.querySelector('#phase-box .seal') as HTMLElement | null;
      if (horn) {
        setText(horn, b.deploying ? 'Sound the horn' : 'Engaged');
        horn.classList.toggle('disabled', !b.deploying);
      }
      return;
    }
    /**
     * The clock, in a campaign, is the realm's — on both screens.
     *
     * > *"the time and legeer should be openable as well"*
     *
     * These three readouts were two different clocks. The city's said
     * `Hollow · 3 held · Reapmonth 4 / Build / —` — a dash, because a capital
     * has no wave being counted down at it — and the realm's said the date and
     * the council countdown, in a stat row a third of the way down a column
     * that is a shut sheet on a phone. They are the same clock: one Long
     * Ledger drives both halves of the game, and the council is the only thing
     * either screen is actually counting down to.
     *
     * So in a campaign the box reads the same on both maps: the Age and the
     * town's tally (off the *town*, wherever the player is standing), the
     * realm's date, and how long until the council sits. Outside a campaign —
     * a standalone siege, a practice battle — nothing here changes at all.
     */
    const rs = this.realmState(w);
    const rc = rs?.campaign;
    if (rc && rs) {
      const town = this.townWorld(w);
      const climb =
        town.researchingAge > 0
          ? ` · ${AGE_NAMES[town.researchingAge - 1] ?? 'Hollow'} ${Math.floor(
              (town.ageResearch / Math.max(1, ageResearchSeconds(town.researchingAge))) * 100,
            )}%`
          : '';
      setText(
        this.waveLabel,
        `${AGE_NAMES[town.age - 1] ?? 'Hollow'} · ${town.wave} held${climb} · ${formatDateShort(rs)}`,
      );
      setText(this.phaseName, 'Council');
      const days = daysToNextMonth(rs);
      setText(
        this.phaseTimer,
        rc.phase !== TurnPhase.Orders
          ? `${TURN_PHASE_NAMES[rc.phase] ?? 'sitting'}…`
          : `${days}d`,
      );
      // Urgent when the council is in the room, not when it is a week out: this
      // is the one moment of the month in which an order lands differently.
      this.phaseTimer.classList.toggle('urgent', rc.phase !== TurnPhase.Orders);
      const seal = this.resBar.querySelector('#phase-box .seal') as HTMLElement | null;
      if (seal) {
        const siegeNow = town.phase === Phase.Siege;
        setText(seal, siegeNow ? 'They are here' : 'No siege');
        seal.classList.add('disabled');
      }
      return;
    }
    const siege = w.phase === Phase.Siege;
    const capital = w.config.homeCity === true;
    // While the Age is being researched, say so here rather than in a panel of
    // its own: it is the same fact as "which Age is this", and the player is
    // already reading this line for the wave number.
    const climbing =
      w.researchingAge > 0
        ? ` · ${AGE_NAMES[w.researchingAge - 1] ?? 'Hollow'} ${Math.floor(
            (w.ageResearch / Math.max(1, ageResearchSeconds(w.researchingAge))) * 100,
          )}%`
        : '';
    /**
     * The date rides the line the player is already reading for the Age and the
     * wave, because it is the same kind of fact and does not deserve a panel of
     * its own. Short form only — `Reapmonth 4` — with the year, the season and
     * what the season is doing to the economy one hover away, in `phaseTip`.
     */
    const date = ` · ${formatDateShort(w)}`;
    setText(
      this.waveLabel,
      capital
        ? `${AGE_NAMES[w.age - 1] ?? 'Hollow'} · ${w.wave} held${climbing}${date}`
        : `${AGE_NAMES[w.age - 1] ?? 'Hollow'} · Wave ${w.wave}${climbing}${date}`,
    );
    setText(this.phaseName, w.gameOver ? 'The dark' : siege ? 'Siege' : 'Build');
    if (siege) {
      const left = remainingThreat(w);
      const total = Math.max(1, w.incomingBudget);
      setText(this.phaseTimer, `${Math.max(0, Math.round((left / total) * 100))}%`);
      this.phaseTimer.classList.toggle('urgent', left / total > 0.6);
    } else if (capital) {
      /**
       * A capital has no build timer — its sieges arrive from the strategic map
       * rather than from a clock, so `phaseT` is deliberately Infinity. Fed to
       * the clock formatter that produced "Infinity:NaN" on screen, which is
       * both wrong and slightly alarming.
       */
      setText(this.phaseTimer, '—');
      this.phaseTimer.classList.remove('urgent');
    } else {
      const t = Math.max(0, w.phaseT);
      const m = Math.floor(t / 60);
      const s = Math.floor(t % 60);
      setText(this.phaseTimer, `${m}:${s.toString().padStart(2, '0')}`);
      this.phaseTimer.classList.toggle('urgent', t < 20);
    }
    // The horn belongs to the realm in a campaign; there is nothing to call in.
    const horn = this.resBar.querySelector('#phase-box .seal') as HTMLElement | null;
    if (horn && capital) {
      setText(horn, siege ? 'They are here' : 'No siege');
      horn.classList.add('disabled');
    }
  }

  private updatePowers(w: World): void {
    this.willFill.style.width = `${(w.will / Math.max(1, w.willMax)) * 100}%`;
    setText(this.willLabel, `Will ${Math.floor(w.will)} / ${w.willMax}`);
    const list = w.unlockedPowers.slice(0, 10);

    while (this.powerBar.children.length > list.length) {
      this.powerBar.removeChild(this.powerBar.lastChild as Node);
    }
    for (let i = 0; i < list.length; i++) {
      const id = list[i];
      const def = power(id);
      let el = this.powerBar.children[i] as HTMLElement | undefined;
      if (!el) {
        el = document.createElement('div');
        el.className = 'power';
        el.innerHTML = `<span class="key"></span><span class="glyph"></span><span class="cost"></span><span class="cd"></span>`;
        this.powerBar.appendChild(el);
      }
      const r = resolvePower(w, def);
      const cd = w.powerCooldowns[id] ?? 0;
      setText(el.querySelector('.key'), POWER_KEYS[i] ?? '');
      setText(el.querySelector('.glyph'), def.glyph);
      setText(el.querySelector('.cost'), String(Math.round(r.cost)));
      const cdEl = el.querySelector('.cd') as HTMLElement;
      cdEl.style.display = cd > 0 ? 'flex' : 'none';
      setText(cdEl, cd > 0 ? Math.ceil(cd).toString() : '');
      el.classList.toggle('armed', this.armedPower === id);
      el.classList.toggle('cant', w.will < r.cost || cd > 0);
      el.onclick = () => {
        this.armedPower = this.armedPower === id ? '' : id;
        this.buildSel = { kind: 'none' };
        this.markBuildSelection();
        this.cb.onPowerSelect(this.armedPower);
      };
      el.onmouseenter = (e) => this.hover(e, () => this.powerTip(id));
      el.onmouseleave = () => this.hideTip();
    }
  }

  armPower(id: string): void {
    this.armedPower = id;
    this.armedHeroAbility = '';
    this.buildSel = { kind: 'none' };
    this.markBuildSelection();
  }

  get armed(): string {
    return this.armedPower;
  }

  // ── The hero ─────────────────────────────────────────────────────────────

  armHeroAbility(id: string): void {
    this.armedHeroAbility = id;
    this.armedCompanionId = this.activeCompanion?.id ?? -1;
    this.armedPower = '';
    this.buildSel = { kind: 'none' };
    this.markBuildSelection();
  }

  clearArmedHeroAbility(): void {
    this.armedHeroAbility = '';
    this.armedCompanionId = -1;
  }

  get armedHero(): string {
    return this.armedHeroAbility;
  }

  get heroCasterId(): number { return this.armedCompanionId; }

  get heroCasterName(): string {
    const w = this.w;
    return w?.companions.find((h) => h.id === this.armedCompanionId)?.name ?? w?.hero.name ?? 'Hero';
  }

  get heroCasterUnit(): number {
    const w = this.w;
    if (!w) return -1;
    const h = w.companions.find((p) => p.id === this.armedCompanionId);
    return h ? companionUnit(w, h) : heroUnit(w);
  }

  private get activeCompanion(): CompanionHero | undefined {
    const w = this.w;
    if (!w) return undefined;
    for (const id of this.selectedCompanies) {
      const h = companionByCompany(w, id);
      if (h && this.companionIsMine(w, h)) return h;
      if (id === w.hero.companyId && this.hasLocalLeader(w)) return undefined;
    }
    return w.companions.find((h) => h.id === this.inspectedCompanionId && this.companionIsMine(w, h))
      ?? (this.hasLocalLeader(w) ? undefined : w.companions.find((h) => this.companionIsMine(w, h)));
  }

  private companionIsMine(w: World, h: CompanionHero): boolean {
    const c = w.companies.find((v) => v.id === h.companyId);
    return c ? c.team === (this.cb.getControlledTeam?.() ?? Team.Player) : !w.battle && h.armyId < 0;
  }

  private hasLocalLeader(w: World): boolean {
    const i = heroUnit(w);
    return i >= 0 ? w.units.team[i] === (this.cb.getControlledTeam?.() ?? Team.Player) : !!w.hero.classId && !w.battle;
  }

  /** Number keys and wheel buttons resolve the same selected character. */
  armHeroSlot(index: number): void {
    const w = this.w;
    if (!w) return;
    const h = this.activeCompanion;
    if (!h && !this.hasLocalLeader(w)) return;
    const slots = h ? companionAbilitySlots(w, h) : heroAbilitySlots(w);
    const slot = slots[index];
    if (!slot) { this.hint('No ability in that slot.', 2); return; }
    this.chooseHeroAbility(slot.powerId);
  }

  private chooseHeroAbility(id: string): void {
    const w = this.w;
    if (!w) return;
    const h = this.activeCompanion;
    if (!h && !this.hasLocalLeader(w)) return;
    const slots = h ? companionAbilitySlots(w, h) : heroAbilitySlots(w);
    const slot = slots.find((s) => s.powerId === id);
    if (!slot) return;
    if (!slot.learned) {
      if (h) this.hint(`${slot.name} unlocks at level ${slot.minLevel}.`, 3);
      else this.showHeroSheet(w);
      return;
    }
    const err = h ? companionAbilityError(w, h.id, id) : heroAbilityError(w, id);
    if (err) { this.hint(err, 3); return; }
    if (this.armedHeroAbility === id && this.armedCompanionId === (h?.id ?? -1)) this.clearArmedHeroAbility();
    else this.armHeroAbility(id);
    this.cb.onPowerSelect('');
  }

  private focusHero(): void {
    const w = this.w;
    if (!w) return;
    const h = this.activeCompanion;
    const i = h ? companionUnit(w, h) : heroUnit(w);
    if (i < 0) { this.showArmyHeroes(w); return; }
    this.cb.onCompanySelect(h?.companyId ?? w.hero.companyId, false);
    this.scene.camera.centreOn(w.units.x[i], w.units.y[i]);
  }

  private updateHero(w: World): void {
    const h = this.activeCompanion;
    const cls = h ? HERO_CLASSES.find((c) => c.id === h.classId) : this.hasLocalLeader(w) ? heroClassOrNull(w) : null;
    const count = w.companions.filter((v) => this.companionIsMine(w, v)).length + (this.hasLocalLeader(w) ? 1 : 0);
    this.heroRosterButton.textContent = count ? `Heroes · ${count}` : 'Recruit heroes';
    this.root.classList.toggle('hero-on', !!cls);
    this.root.classList.toggle('hero-down', !!cls && (h ?? w.hero).fallen);
    this.heroPanel.style.display = cls ? '' : 'none';
    this.heroSheetEl.style.display = 'none';
    if (cls) this.updateHeroAbilities(w);
  }

  private updateHeroAbilities(w: World): void {
    const companion = this.activeCompanion;
    const h = companion ?? w.hero;
    const cls = HERO_CLASSES.find((c) => c.id === h.classId);
    if (!cls) return;
    const i = companion ? companionUnit(w, companion) : heroUnit(w);
    const slots = companion ? companionAbilitySlots(w, companion) : heroAbilitySlots(w);
    this.heroWheel.update({
      identity: companion ? `companion-${companion.id}` : `leader-${h.classId}`,
      archetype: cls.id, name: h.name, className: cls.name, level: h.level,
      hp: i >= 0 ? w.units.hp[i] / Math.max(1, w.units.maxHp[i]) : 0,
      status: h.fallen ? (h.reviveT > 0 ? `Wounded · ${Math.ceil(h.reviveT)}s` : 'Wounded · open Heroes')
        : i < 0 ? 'Away from this field' : companion ? 'Army hero' : 'Army leader',
      points: companion ? 0 : heroPointsLeft(w),
      abilities: slots.map((slot, n) => {
        const def = power(slot.powerId);
        const reason = slot.learned
          ? companion ? companionAbilityError(w, companion.id, slot.powerId) : heroAbilityError(w, slot.powerId)
          : companion ? `Unlocks at level ${slot.minLevel}` : heroSkillError(w, (slot as ReturnType<typeof heroAbilitySlots>[number]).skillId);
        return {
          id: slot.powerId, name: slot.name, glyph: def.glyph, key: HERO_ABILITY_KEYS[n] ?? '',
          rules: `${def.rules} Cast within ${HERO_ABILITY_RANGE} tiles of this hero. No Will cost.`,
          cooldown: h.cooldowns[slot.powerId] ?? 0,
          cooldownMax: resolvePower(w, def).cooldown * HERO_ABILITY_COOLDOWN_MUL,
          locked: !slot.learned, lockLabel: `L${slot.minLevel}`, disabledReason: reason,
          armed: this.armedHeroAbility === slot.powerId && this.armedCompanionId === (companion?.id ?? -1),
        };
      }),
    });
  }

  showArmyHeroes(w: World): void {
    const portrait = (id: string) => heroPortrait(id as HeroArchetype, { size: 72 });
    const members = w.companions.filter((h) => this.companionIsMine(w, h)).map((h) => {
      const cls = HERO_CLASSES.find((c) => c.id === h.classId)!;
      const abilities = companionAbilitySlots(w, h).map((slot) =>
        `<span class="hero-skill-summary${slot.learned ? '' : ' locked'}">${esc(slot.name)}${slot.learned ? '' : ` · L${slot.minLevel}`}</span>`).join('');
      return `<article class="hero-roster-card">${portrait(h.classId)}<div><h3>${esc(h.name)}</h3>
        <div class="sub">${esc(cls.name)} · Level ${h.level}${h.armyId >= 0 ? ' · Embedded in army' : ' · City guard'}</div>
        <div>${abilities}</div>
        ${h.fallen ? `<p>Wounded${h.reviveT > 0 ? ` · returns in ${Math.ceil(h.reviveT)}s` : ''}</p>
          <button class="seal ghost" data-revive-hero="${h.id}" ${companionReviveError(w, h.id) ? 'disabled' : ''}
            title="${esc(companionReviveError(w, h.id))}">Recover · ${COMPANION_REVIVE_GOLD} gold</button>`
          : `<button class="seal ghost" data-focus-hero="${h.id}">Select hero</button>`}</div></article>`;
    }).join('');
    const choices = HERO_CLASSES.map((cls) => {
      const reason = companionRecruitError(w, cls.id);
      return `<article class="hero-recruit-card">${portrait(cls.id)}<div><h3>${esc(cls.name)}</h3>
        <p>${esc(cls.flavour)}</p><button class="seal" data-recruit-hero="${cls.id}" ${reason ? 'disabled' : ''}
          title="${esc(reason || 'Join the city guard as a single character')}">Recruit · ${COMPANION_RECRUIT_COST} gold</button>
        ${reason ? `<small>${esc(reason)}</small>` : ''}</div></article>`;
    }).join('');
    this.showSheet('<h1>Heroes of the army</h1><div class="sub">Individual characters who fight alongside your companies.</div>'
      + '<p>Select a hero to open their ability wheel. Use 1–5, then click a target. Each hero has separate cooldowns; further abilities unlock with experience.</p>'
      + (this.hasLocalLeader(w) ? `<button class="seal ghost" data-leader-sheet>Leader · ${esc(w.hero.name)}</button>`
        + (w.hero.fallen ? `<button class="seal ghost" data-revive-leader ${w.hero.reviveT > 0 || w.res.souls < HERO_REVIVE_SOULS ? 'disabled' : ''}>Recover leader · ${HERO_REVIVE_SOULS} souls${w.hero.reviveT > 0 ? ` · ${Math.ceil(w.hero.reviveT)}s` : ''}</button>` : '') : '')
      + '<div class="hero-roster-grid">' + (members || '<p class="dim">No supporting heroes in this city or battle.</p>') + '</div>'
      + (w.battle ? '' : '<h2>Recruit a supporting hero</h2><p>Up to three heroes may join the city guard. Recruit travelling heroes from an army on the campaign map.</p><div class="hero-recruit-grid">' + choices + '</div>')
      + '<button class="seal ghost" data-heroes-close>Close</button>', 'army-heroes');
    const refresh = () => window.setTimeout(() => { if (this.modalOpen && this.w) this.showArmyHeroes(this.w); }, 150);
    this.modal.querySelector('[data-heroes-close]')?.addEventListener('click', () => this.hideModal());
    this.modal.querySelector('[data-revive-leader]')?.addEventListener('click', () => { this.cb.onHeroRevive(); refresh(); });
    this.modal.querySelector('[data-leader-sheet]')?.addEventListener('click', () => this.showHeroSheet(w));
    this.modal.querySelectorAll<HTMLElement>('[data-recruit-hero]').forEach((el) => {
      el.onclick = () => { if (!companionRecruitError(w, el.dataset.recruitHero!)) { this.cb.onRecruitHero?.(el.dataset.recruitHero!); refresh(); } };
    });
    this.modal.querySelectorAll<HTMLElement>('[data-focus-hero]').forEach((el) => {
      el.onclick = () => {
        const h = w.companions.find((p) => p.id === Number(el.dataset.focusHero));
        if (!h) return;
        this.inspectedCompanionId = h.id;
        this.cb.onCompanySelect(h.companyId, false); this.hideModal(); this.focusHero();
      };
    });
    this.modal.querySelectorAll<HTMLElement>('[data-revive-hero]').forEach((el) => {
      el.onclick = () => { this.cb.onReviveCompanion?.(Number(el.dataset.reviveHero)); refresh(); };
    });
  }

  /**
   * The skill tree, laid out on the `row`/`col` the data already carries. Every
   * purchase leaves as a `Cmd.HeroSkill` — the sheet reads the world and writes
   * nothing.
   */
  showHeroSheet(w: World): void {
    const cls = heroClassOrNull(w);
    /**
     * A run with no character still has to answer the key.
     *
     * `I` and the ✦ button both landed here and returned in silence when
     * `hero.classId` is empty — which is what "No one" on the setup board gives
     * you, and what every deep link that is not `?mode=campaign` gives you
     * whether you asked for it or not. A control that does nothing and says
     * nothing is indistinguishable from a control that is broken, and this one
     * was being read as "the character sheet was never built". It was built. It
     * had nobody to be about.
     */
    if (!cls) {
      this.showSheet(
        `<h1>No character</h1>` +
          `<div class="sub">This run was begun without one.</div>` +
          `<div class="cf">The skill tree, the five kit slots and the cart all belong to a ` +
          `character, and one is chosen on the setup board at the start of a run — or ` +
          `deliberately not, which is the old way: a crown, and no face under it.</div>` +
          `<div class="cf" style="margin-top:10px">Begin a new run and pick somebody, and this ` +
          `sheet is where his levels, his tree and everything he is carrying live.</div>` +
          `<div style="margin-top:18px"><span class="seal ghost" data-close>Close</span></div>`,
        'nohero',
      );
      const shut = this.modal.querySelector('[data-close]') as HTMLElement | null;
      if (shut) shut.onclick = () => this.hideModal();
      return;
    }
    const h = w.hero;
    const points = heroPointsLeft(w);
    const p = heroXpProgress(w);

    const cols = Math.max(...cls.skills.map((s) => s.col)) + 1;
    const rows = Math.max(...cls.skills.map((s) => s.row)) + 1;
    const grid: string[] = [];
    for (const s of cls.skills) {
      const owned = h.skills.includes(s.id);
      const err = heroSkillError(w, s.id);
      const state = owned ? 'owned' : err === '' ? 'open' : 'shut';
      grid.push(
        `<div class="hskill ${state}" data-skill="${esc(s.id)}"` +
          ` style="grid-row:${s.row + 1};grid-column:${s.col + 1}">` +
          `<div class="sn">${esc(s.name)}</div>` +
          `<div class="sr">${esc(s.rules)}</div>` +
          `<div class="sm">${owned ? 'Learned' : err === '' ? `${s.cost} point${s.cost > 1 ? 's' : ''}` : esc(err)}</div>` +
          `<div class="sf">${esc(s.flavour)}</div>` +
          `</div>`,
      );
    }

    this.showSheet(
      `<h1>${esc(h.name)}</h1>` +
        `<div class="sub">${esc(cls.name)} — ${esc(cls.epithet)}</div>` +
        `<div class="hsheet-top">` +
        `<span>Level <b>${h.level}</b></span>` +
        `<span>Experience <b>${Math.floor(p.into)}</b> / ${Math.floor(p.need)}</span>` +
        `<span>Skill points <b>${points}</b></span>` +
        `</div>` +
        `<div class="hpassive">${esc(cls.startingPassive.name)} — ${esc(cls.startingPassive.rules)}</div>` +
        this.kitHtml(w) +
        `<h2 class="ksec">The tree</h2>` +
        `<div class="htree" style="grid-template-columns:repeat(${cols},1fr);grid-template-rows:repeat(${rows},auto)">` +
        grid.join('') +
        `</div>` +
        `<div style="margin-top:18px"><span class="seal ghost" data-close>Close</span></div>`,
      'hero',
    );

    for (const el of this.modal.querySelectorAll('[data-skill]')) {
      (el as HTMLElement).onclick = () => {
        const id = (el as HTMLElement).dataset.skill as string;
        if (heroSkillError(w, id) !== '') return;
        this.cb.onHeroSkill(id);
        // Re-read on the next frame; the purchase happens in the simulation.
        setTimeout(() => {
          if (this.modalOpen) this.showHeroSheet(w);
        }, 60);
      };
    }
    this.wireKit(w);
    const close = this.modal.querySelector('[data-close]') as HTMLElement | null;
    if (close) close.onclick = () => this.hideModal();
  }

  /**
   * The kit, on the same sheet as the tree rather than on a screen of its own.
   *
   * Five slots and the cart, and every card states its exact numbers *at the
   * level it actually is* — `describeItem` resolves the catalogue's per-level
   * deltas against the modifier bag's own rules, so a player never has to do the
   * multiplication that decides whether the thing is worth carrying. The reforge
   * button carries its own price for the same reason: the whole late game is a
   * decision about which of these to feed, and a decision needs a number.
   */
  private kitHtml(w: World): string {
    const forge = hasForge(w);
    const slots: string[] = [];
    for (let s = 0; s < ITEM_SLOT_COUNT; s++) {
      const st = equippedStack(w, s);
      const def = st ? itemDef(st.defId) : undefined;
      if (!st || !def) {
        slots.push(
          `<div class="kitslot empty"><div class="ks">${esc(ITEM_SLOT_NAMES[s])}</div>` +
            `<div class="kn dim">${esc(ITEM_SLOT_EMPTY[s] ?? 'Empty.')}</div></div>`,
        );
        continue;
      }
      slots.push(
        `<div class="kitslot"><div class="ks">${esc(ITEM_SLOT_NAMES[s])}</div>` +
          `<div class="kn">${esc(def.name)} <span class="klv">${ITEM_RARITY_NAMES[def.rarity]} · level ${st.level}</span></div>` +
          `<div class="kfx">${describeItem(def, st.level).map(esc).join(' · ')}</div>` +
          `<div class="kf">${esc(def.flavour)}</div>` +
          `<div class="kbtn">` +
          `<span class="seal ghost tiny" data-unequip="${s}">Take off</span>` +
          this.reforgeButton(w, st.uid, def, st.level, forge) +
          `</div></div>`,
      );
    }

    const loose = w.inventory.filter((s) => !w.equipped.includes(s.uid));
    const cart = loose.map((st) => {
      const def = itemDef(st.defId);
      if (!def) return '';
      return (
        `<div class="kititem"><div class="kn">${esc(def.name)}` +
        ` <span class="klv">${esc(ITEM_SLOT_NAMES[def.slot])} · ${ITEM_RARITY_NAMES[def.rarity]} · level ${st.level}</span></div>` +
        `<div class="kfx">${describeItem(def, st.level).map(esc).join(' · ')}</div>` +
        `<div class="kbtn">` +
        `<span class="seal ghost tiny" data-equip="${st.uid}">Wear</span>` +
        this.reforgeButton(w, st.uid, def, st.level, forge) +
        `<span class="seal ghost tiny" data-scrap="${st.uid}">Melt · ${scrapValue(def, st.level)}${RESOURCE_GLYPHS.souls}</span>` +
        `</div></div>`
      );
    });

    return (
      `<h2 class="ksec">Kit <span class="dim">— what he carries, and what the army feels</span></h2>` +
      `<div class="kitslots">${slots.join('')}</div>` +
      `<h2 class="ksec">The cart <span class="dim">— ${w.inventory.length} of ${INVENTORY_MAX}` +
      (forge ? '' : '. No smelter, so nothing can be reforged') +
      `</span></h2>` +
      (cart.length > 0
        ? `<div class="kitcart">${cart.join('')}</div>`
        : `<div class="kitcart empty dim">Nothing loose. Hold a wave, or win a field.</div>`)
    );
  }

  private reforgeButton(w: World, uid: number, def: ReturnType<typeof itemDef>, level: number, forge: boolean): string {
    if (!def) return '';
    const cost = reforgeCost(def, level);
    const price = (Object.keys(cost) as ResourceKey[])
      .filter((k) => (cost[k] ?? 0) > 0)
      .map((k) => `${Math.round(cost[k] as number)}${RESOURCE_GLYPHS[k]}`)
      .join(' ');
    const err = reforgeError(w, uid);
    return (
      `<span class="seal ghost tiny${err === '' ? '' : ' disabled'}" data-reforge="${uid}"` +
      ` title="${esc(err === '' ? `Raise to level ${level + 1}` : err)}">` +
      `Reforge · ${price}${forge ? '' : ' ⨯'}</span>`
    );
  }

  /** Every kit button posts a command and re-reads the sheet a frame later. */
  private wireKit(w: World): void {
    const again = () => {
      setTimeout(() => {
        // Re-read through the HUD's own world reference, so a sheet left open
        // across an `adoptWorld` swap describes the run that is actually there.
        if (this.modalOpen) this.showHeroSheet(this.w ?? w);
      }, 60);
    };
    const bind = (attr: string, fire: (n: number) => void) => {
      for (const el of this.modal.querySelectorAll(`[data-${attr}]`)) {
        const e = el as HTMLElement;
        if (e.classList.contains('disabled')) continue;
        e.onclick = () => {
          fire(Number(e.dataset[attr]));
          again();
        };
      }
    };
    bind('equip', (n) => this.cb.onHeroEquip(n));
    bind('unequip', (n) => this.cb.onHeroUnequip(n));
    bind('reforge', (n) => this.cb.onHeroReforge(n));
    bind('scrap', (n) => this.cb.onHeroScrap(n));
  }

  private updateRoster(companies: Company[], reads: CompanyReadout[]): void {
    setText(this.armyDoor.querySelector('small'), String(companies.length));
    setText(this.rosterSummary, companies.length ? `Army · ${companies.length} companies` : 'Your army');
    this.rosterEmpty.hidden = companies.length > 0;
    while (this.rosterList.children.length > companies.length) {
      this.rosterList.removeChild(this.rosterList.lastChild as Node);
    }
    for (let i = 0; i < companies.length; i++) {
      const c = companies[i];
      let el = this.rosterList.children[i] as HTMLElement | undefined;
      if (!el) {
        el = document.createElement('div');
        el.className = 'comp';
        el.innerHTML =
          `<div class="cchip"></div>` +
          `<div class="cbody">` +
          `<div class="crow"><span class="cname"></span><span class="crank"></span></div>` +
          `<div class="ctype"></div><div class="bar"><i></i></div><div class="bar hp"><i></i></div>` +
          `</div>`;
        this.rosterList.appendChild(el);
      }
      const t = unitType(c.typeIdx);
      const r = reads[i];
      // A head crop beside the name. The roster is the words version of the card
      // bar and had no picture at all, so telling two companies apart in it meant
      // reading — which in a siege is exactly the thing there is no time for.
      // Keyed on the type like the card art, so it is written once per row.
      if (el.dataset.unit !== t.id) {
        el.dataset.unit = t.id;
        setHtml(el.querySelector('.cchip'), unitCardArt(t.id, { frame: false, crop: 'head' }));
      }
      setText(el.querySelector('.cname'), c.name);
      setText(el.querySelector('.crank'), RANK_NAMES[c.rank] ?? '');
      // Everything a Total War player wants on a unit card, in one line.
      const bits = [`${t.name}`, `${c.members.length}/${c.maxSize}`];
      if (r.ammoMax > 0) bits.push(`${Math.round(r.ammo)} shot`);
      if (r.fatigue > 0.25) bits.push(r.fatigue > 0.65 ? 'exhausted' : 'tiring');
      if (r.routing > 0) bits.push(`${r.routing} running`);
      setText(el.querySelector('.ctype'), bits.join(' · '));
      (el.querySelector('.bar i') as HTMLElement).style.width =
        `${(c.members.length / Math.max(1, c.maxSize)) * 100}%`;
      (el.querySelector('.bar.hp i') as HTMLElement).style.width = `${r.health * 100}%`;
      el.classList.toggle('on', this.selectedCompanies.includes(c.id));
      el.classList.toggle('routing', r.routing > c.members.length * 0.4);
      // Reassigned every frame on purpose: the handler must close over the
      // company this row shows *now*, not the one it showed when it was made.
      el.onclick = (ev) => this.cb.onCompanySelect(c.id, ev.shiftKey);
      el.onmouseenter = (ev) => this.hover(ev, () => this.companyTip(c.id));
      el.onmouseleave = () => this.hideTip();
    }
  }

  // ── The unit card bar ────────────────────────────────────────────────────

  /**
   * One card per company, along the bottom, in the order the companies were
   * raised. Everything on a card is legible without hovering, which is the
   * whole argument for the row existing next to a roster that says the same
   * things in words: in a siege there is no time to read words.
   *
   * The row is diffed by index like every other list in this file, and every
   * handler is rebound each frame, so a world swap under it is just a shorter
   * or longer list of companies it has never seen before.
   */
  private updateCards(w: World, companies: Company[], reads: CompanyReadout[]): void {
    this.root.classList.toggle('cards-on', companies.length > 0);
    if (companies.length === 0) {
      this.cardBar.textContent = '';
      return;
    }
    // The realm's colour, under every silhouette. Recomputed from the world
    // every frame — so a new run in a different faction repaints without being
    // told — but only written when it differs, since writing a custom property
    // invalidates the style of every card under it.
    const lit = toCss(shade(w.faction.accent, 0.58));
    if (this.cardBar.dataset.fac !== lit) {
      this.cardBar.dataset.fac = lit;
      this.cardBar.style.setProperty('--fac-lit', lit);
      this.cardBar.style.setProperty('--fac-dark', toCss(shade(w.faction.accentDark, 0.3)));
    }

    while (this.cardBar.children.length > companies.length) {
      this.cardBar.removeChild(this.cardBar.lastChild as Node);
    }
    for (let i = 0; i < companies.length; i++) {
      const c = companies[i];
      let el = this.cardBar.children[i] as HTMLElement | undefined;
      if (!el) {
        el = document.createElement('div');
        el.className = 'ucard';
        // The fatigue tint goes straight after the silhouette: it dims the man,
        // not the chevrons and the tally that have to stay readable through it.
        el.innerHTML =
          `<div class="uport">` +
          `<span class="sil"></span>` +
          `<i class="ufat"></i>` +
          `<svg class="uchev" width="0" height="8" viewBox="0 0 1 8"></svg>` +
          `<span class="uammo"><i></i></span>` +
          `<span class="ureinf">+</span>` +
          `<span class="ucount"></span>` +
          `</div>` +
          `<div class="uname"></div>` +
          `<div class="ubar hp"><i></i></div>` +
          `<div class="ubar mor"><i></i></div>`;
        this.cardBar.appendChild(el);
      }
      const t = unitType(c.typeIdx);
      const r = reads[i];

      // The silhouette and the chevrons are the only parts of a card that cost
      // anything to make, so they are only remade when what they say changes —
      // which for a given card is when the row shifts under a dead company, or
      // when a company is promoted in the field.
      // The art is per unit *type* now rather than per role, so a spearman and a
      // pikeman are no longer the same picture. The cache key moves with it and
      // changes no more often than the old one did: a card's type is fixed for
      // its life, so this writes once per card and never again.
      if (el.dataset.unit !== t.id) {
        el.dataset.unit = t.id;
        setHtml(el.querySelector('.sil'), unitCardArt(t.id, { frame: false }));
      }
      if (el.dataset.rank !== String(c.rank)) {
        el.dataset.rank = String(c.rank);
        const chev = el.querySelector('.uchev') as SVGElement;
        chev.setAttribute('width', String(c.rank * 7));
        chev.setAttribute('viewBox', `0 0 ${Math.max(1, c.rank * 7)} 8`);
        let d = '';
        for (let k = 0; k < c.rank; k++) {
          const x = k * 7;
          d += `<path d="M${x + 0.8} 6.4 L${x + 3.5} 1.6 L${x + 6.2} 6.4 L${x + 4.9} 6.4 L${x + 3.5} 4 L${x + 2.1} 6.4 Z"/>`;
        }
        chev.innerHTML = d;
      }

      setText(el.querySelector('.ucount'), `${c.members.length}/${c.maxSize}`);
      const hpBar = el.querySelector('.ubar.hp i') as HTMLElement;
      hpBar.style.width = `${r.health * 100}%`;
      hpBar.style.background = toCss(r.health > 0.6 ? C.hpGood : r.health > 0.3 ? C.hpMid : C.hpBad);
      (el.querySelector('.ubar.mor i') as HTMLElement).style.width = `${clamp01(r.morale) * 100}%`;
      setText(el.querySelector('.uname'), c.name);

      const ammoEl = el.querySelector('.uammo') as HTMLElement;
      ammoEl.style.display = r.ammoMax > 0 ? 'block' : 'none';
      if (r.ammoMax > 0) {
        const frac = clamp01(r.ammo / r.ammoMax);
        (ammoEl.querySelector('i') as HTMLElement).style.height = `${frac * 100}%`;
        ammoEl.classList.toggle('dry', frac < 0.2);
      }
      // Fatigue as a heat haze over the portrait: it is a tint, not a number,
      // because it is a thing you should feel rather than read.
      (el.querySelector('.ufat') as HTMLElement).style.opacity = (clamp01(r.fatigue) * 0.75).toFixed(2);

      const missing = c.maxSize - c.members.length;
      const reinf = el.querySelector('.ureinf') as HTMLElement;
      // No replacements to be had on a field: a battle army is what marched.
      const canReinforce = missing > 0 && !w.battle;
      reinf.style.display = canReinforce ? 'flex' : 'none';
      if (canReinforce) {
        const cost = reinforceCost(w, t, missing);
        const afford = canAfford(w.res, cost);
        reinf.classList.toggle('cant', !afford);
        reinf.onclick = (ev) => {
          // The chip sits on the card; without this, taking replacements would
          // also change the selection under the player's hand.
          ev.stopPropagation();
          if (afford) this.cb.onReinforce(c.id);
        };
        reinf.onmouseenter = (ev) => this.hover(ev, () => this.reinforceTip(c.id));
        reinf.onmouseleave = (ev) => this.hover(ev, () => this.companyTip(c.id));
      }

      el.classList.toggle('on', this.selectedCompanies.includes(c.id));
      el.classList.toggle('routing', r.routing > c.members.length * 0.4);
      el.classList.toggle('thin', c.members.length < c.maxSize * 0.5);
      el.onclick = (ev) => this.cb.onCompanySelect(c.id, ev.shiftKey);
      el.onmouseenter = (ev) => this.hover(ev, () => this.companyTip(c.id));
      el.onmouseleave = () => this.hideTip();
    }
  }

  setSelection(ids: number[]): void {
    this.clearArmedHeroAbility();
    this.inspectedCompanionId = -1;
    this.selectedCompanies = ids;
  }

  private updateLog(): void {
    const entries = this.scene.log;
    while (this.logEl.children.length > entries.length) {
      this.logEl.removeChild(this.logEl.lastChild as Node);
    }
    for (let i = 0; i < entries.length; i++) {
      const e = entries[entries.length - 1 - i];
      let el = this.logEl.children[i] as HTMLElement | undefined;
      if (!el) {
        el = document.createElement('div');
        this.logEl.appendChild(el);
      }
      setText(el, e.text);
      el.className = ['', 'good', 'bad', 'dire', 'lore'][e.tone] ?? '';
      el.style.opacity = String(clamp01(e.t / 2));
    }
  }

  /**
   * In Battle mode the scouting panel becomes the resolve board: two nerve
   * bars and a company tally. Resolve, not health, is what you are playing.
   */
  private updateBattlePanel(w: World): void {
    const b = w.battle;
    if (!b) return;
    this.scouting.style.display = 'block';
    const bar = (v: number, col: string) =>
      `<div style="height:7px;background:rgba(0,0,0,.6);margin:3px 0 7px">` +
      `<i style="display:block;height:100%;width:${(clamp01(v) * 100).toFixed(0)}%;background:${col}"></i></div>`;

    /**
     * One row a banner, not one a side.
     *
     * A 2v2 fields four armies and this panel named two of them, so a player
     * whose ally was being destroyed learned about it from the chronicle line
     * and nowhere else. `playerAlly` and `enemyAlly` are null in a 1v1 — which
     * is every siege, every campaign battle and every skirmish that did not ask
     * for four — so the extra rows simply do not appear there.
     *
     * The allies keep their side's colour and are marked rather than recoloured:
     * the read the player needs first is *whose side is this*, and a third and
     * fourth hue would fight the friend-or-foe rim the whole renderer is built
     * on.
     */
    const banner = (
      army: typeof b.player,
      grad: string,
      ally: boolean,
    ): string => {
      const st = armyStrength(w, army);
      return (
        `<div class="scout-row"><span class="k">${esc(army.name)}${ally ? ' <span class="dim">· ally</span>' : ''}</span>` +
        `<span class="v">${st.alive}/${st.total} · ${army.broken} companies gone</span></div>` +
        bar(army.resolve, grad)
      );
    };
    const OURS = 'linear-gradient(90deg,#8a6c26,#e6cf92)';
    const THEIRS = 'linear-gradient(90deg,#3a4a6b,#8fa6d8)';

    setHtml(
      this.scouting,
      `<div class="hdr">${esc(b.scenario.name)}${b.deploying ? ' — Deployment' : ''}</div><div class="body">` +
        banner(b.player, OURS, false) +
        (b.playerAlly ? banner(b.playerAlly, OURS, true) : '') +
        banner(b.enemy, THEIRS, false) +
        (b.enemyAlly ? banner(b.enemyAlly, THEIRS, true) : '') +
        (b.deploying
          ? `<div class="scout-mod">Place your companies inside the marked ground, then sound the horn.<br>` +
            `Right-click to move a company. Right-drag to set its frontage and facing.</div>` +
            this.loadoutSection(w)
          : `<div class="scout-comp"><span class="note">${esc(b.scenario.objective)}</span></div>`) +
        (b.outcome !== 0
          ? `<div class="scout-champ"><div class="cn">${b.outcome > 0 ? 'The field is yours' : 'Your army breaks'}</div>` +
            `<div class="cb">${b.outcome > 0 ? 'They are running. Let them.' : 'It is over. It was over some minutes ago.'}</div></div>`
          : '') +
        `</div>`,
    );
  }

  /**
   * The muster list on the deployment screen.
   *
   * Only while `deploying` — `applyLoadout` refuses once the horns have gone,
   * and a panel that offers a button the simulation will refuse is worse than
   * no panel. The offer comes from `loadoutReport`, which reads the *battle's*
   * own roster and its own purse, so this cannot drift from what a click will
   * actually do: the greyed row and the refused click are the same arithmetic.
   *
   * No `<input>` anywhere, deliberately. This panel is rebuilt from `innerHTML`
   * every frame, and a text field in that markup loses its value and its caret
   * mid-typing — the lesson the market book's form paid for. Everything here is
   * a button.
   */
  private loadoutSection(w: World): string {
    const r = loadoutReport(w);
    if (r.budget <= 0) return '';
    /**
     * A list nobody may edit is not a list, it is a lie with buttons on it.
     *
     * Only a skirmish sets `freeMuster` — a campaign battle's army is the
     * crown's own muster and a shared field belongs to two people. Rather than
     * greying twenty rows, the section simply is not there, and the army the
     * player is about to command is already on the companies rail beside them.
     */
    if (!r.freeMuster) return '';

    const row = (
      label: string,
      right: string,
      act: string,
      a: number,
      off: boolean,
    ): string =>
      `<div class="scout-row lo${off ? ' off' : ''}" data-lact="${act}" data-a="${a}">` +
      `<span class="k">${esc(label)}</span><span class="v">${esc(right)}</span></div>`;

    const picks =
      r.picks.length === 0
        ? `<div class="scout-mod">Nothing mustered. The field is no place to notice that.</div>`
        : r.picks
            .map((p) =>
              row(`${p.count}× ${p.name}`, `${p.points * p.count} pts  ✕`, 'remove', p.typeIdx, false),
            )
            .join('');

    const offer = r.offer
      .map((o) => row(o.name, `${o.points} pts  +`, 'add', o.typeIdx, !o.affordable))
      .join('');

    /**
     * Say plainly when the army is already over the purse.
     *
     * `createBattle` takes the loadout from *whatever army is standing* —
     * "however it got there", a campaign muster included — and that muster runs
     * with `MUSTER_SLACK`, which the player's own purse deliberately does not
     * have. So `spent` can genuinely exceed `budget`, `remaining` clamps at
     * zero, and the two do not sum. Written as "150 left" that reads as a
     * rounding bug; written as "over by 222" it reads as what it is, which is
     * that you have been handed more army than you asked for and may only give
     * some back. `Add` refuses either way — that part was always right.
     */
    const purse =
      r.spent > r.budget
        ? `<b>${r.spent}</b> against a purse of <b>${r.budget}</b> — over by ${r.spent - r.budget}.`
        : `<b>${r.spent}</b> of <b>${r.budget}</b> spent, ${r.remaining} left.`;

    return (
      `<div class="scout-comp"><span class="note">Your order of battle — ${purse}</span></div>` +
      picks +
      `<div class="scout-row lo" data-lact="auto" data-a="0"><span class="k">Spend the rest</span>` +
      `<span class="v">let the marshal choose</span></div>` +
      `<div class="scout-row lo${r.picks.length === 0 ? ' off' : ''}" data-lact="clear" data-a="0">` +
      `<span class="k">Send them home</span><span class="v">start again</span></div>` +
      `<div class="scout-comp"><span class="note">Who is on offer</span></div>` +
      offer
    );
  }

  private onScoutingClick(ev: MouseEvent): void {
    const el = (ev.target as HTMLElement | null)?.closest('[data-lact]') as HTMLElement | null;
    if (!el || el.classList.contains('off')) return;
    const w = this.w;
    if (!w?.battle?.deploying) return;
    const a = Number(el.dataset.a ?? '0');
    switch (el.dataset.lact) {
      case 'add':
        this.cb.onLoadout?.(LoadoutAct.Add, a);
        break;
      case 'remove':
        this.cb.onLoadout?.(LoadoutAct.Remove, a);
        break;
      case 'auto':
        this.cb.onLoadout?.(LoadoutAct.Auto, 0);
        break;
      case 'clear':
        this.cb.onLoadout?.(LoadoutAct.Clear, 0);
        break;
      default:
        break;
    }
  }

  private updateScouting(w: World): void {
    if (w.battle) {
      this.updateBattlePanel(w);
      return;
    }
    /**
     * A capital has nothing to scout. The panel reports what the *director*
     * intends to send, and a campaign capital has no director — its besiegers
     * are hosts on the world map, which the travel bar reports instead. Left
     * on, it showed a confident forecast of a wave that was never coming.
     */
    if (w.phase !== Phase.Build || w.gameOver || w.config.homeCity) {
      this.scouting.style.display = 'none';
      return;
    }
    this.scouting.style.display = 'block';
    const r = scoutingReport(w);
    const stats = statsOf(w);
    void stats;
    setHtml(
      this.scouting,
      `<div class="hdr">Scouting — Wave ${w.wave + 1}</div><div class="body">` +
        `<div class="scout-row"><span class="k">Who</span><span class="v">${esc(r.faction)}</span></div>` +
        `<div class="scout-row"><span class="k">Strength</span><span class="v">${esc(r.strength)}</span></div>` +
        `<div class="scout-row"><span class="k">Vector${r.gates.length > 1 ? 's' : ''}</span><span class="v">${r.gates.map(esc).join('<br>')}</span></div>` +
        r.composition
          .map(
            (c) =>
              `<div class="scout-comp"><span class="n">${c.count} × ${esc(c.name)}</span>` +
              (c.note ? ` <span class="note">— ${esc(c.note)}</span>` : '') +
              `</div>`,
          )
          .join('') +
        r.modifiers.map((m) => `<div class="scout-mod">${esc(m.name)} — ${esc(m.text)}</div>`).join('') +
        (r.champion
          ? `<div class="scout-champ"><div class="cn">${esc(r.champion.name)}</div>` +
            `<div class="ct">${esc(r.champion.title)}</div>` +
            `<div class="cb">${esc(r.champion.blurb)}</div></div>`
          : '') +
        `</div>`,
    );
  }

  private updateBossCard(w: World): void {
    void w;
    const card = this.scene.bossCard;
    if (card) {
      setHtml(
        this.bossCard,
        `<div class="bn">${esc(card.name)}</div><div class="bt">${esc(card.title)}</div>`,
      );
      this.bossCard.classList.add('show');
      this.vignette.classList.add('show');
    } else {
      this.bossCard.classList.remove('show');
      this.vignette.classList.remove('show');
    }
  }

  private updateWorldText(): void {
    const nums = this.scene.fx.numbers;
    const cam = this.scene.camera;
    for (let i = 0; i < Math.max(nums.length, this.textPool.length); i++) {
      let el = this.textPool[i];
      if (!el) {
        el = document.createElement('span');
        this.worldText.appendChild(el);
        this.textPool[i] = el;
      }
      const n = nums[i];
      if (!n) {
        el.style.display = 'none';
        continue;
      }
      el.style.display = 'block';
      el.textContent = n.text;
      el.style.left = `${cam.worldToScreenX(n.x)}px`;
      el.style.top = `${cam.worldToScreenY(n.y)}px`;
      el.style.color = toCss(n.color);
      el.style.fontSize = `${11 * n.scale}px`;
      el.style.opacity = String(clamp01(n.life / 0.5));
    }
  }

  private updateFps(w: World, fps: number, show: boolean): void {
    this.fpsEl.classList.toggle('show', show);
    if (!show) return;
    const stats = statsOf(w);
    this.fpsEl.textContent =
      `${fps.toFixed(0)} fps\n` +
      `units ${w.units.slots.count}  (${w.counts.player}/${w.counts.enemy})\n` +
      `bldg ${w.buildings.slots.count}  proj ${w.proj.slots.count}\n` +
      `tick ${w.tick}  zoom ${this.scene.camera.zoom.toFixed(0)}\n` +
      `kills ${stats.kills}  lost ${stats.losses}\n` +
      `rubble ${w.rubbleCount}  zones ${w.zones.length}`;
  }

  /**
   * Which works the town can pay for, and what each of them costs *now*.
   *
   * The cost line is rich markup — a glyph and a figure per resource, coloured
   * by whether the store will stand it — so it is rebuilt rather than retyped.
   * That made this the single most expensive thing in the HUD: twenty-odd
   * buildings in a category, several spans each, torn down and re-inserted sixty
   * times a second. A `MutationObserver` over the whole UI counted sixteen
   * thousand node insertions and removals in five seconds and eight thousand of
   * them were in this one loop, for a list whose figures change perhaps once a
   * minute. It goes through `setHtml`, and now writes when a number moves.
   */
  private updateBuildAffordability(w: World): void {
    if (isVillage(w)) return;
    for (const child of this.buildList.children) {
      const e = child as HTMLElement;
      const idxAttr = e.dataset.build;
      if (idxAttr === undefined) continue;
      const idx = Number(idxAttr);
      const cost = buildCostFor(w, idx);
      /**
       * The rich cost line is drawn for locked entries too, and that is the
       * point of drawing them at all: a Bell converter whose whole bill is red
       * because the player has never held a runestone is the tech ladder
       * explaining itself. What a locked entry does *not* get is `cant` on top
       * of `locked` — one refusal, one dimming.
       */
      const locked = e.classList.contains('locked');
      let can = true;
      for (const k of RESOURCE_KEYS) if ((cost[k] ?? 0) > w.res[k]) can = false;
      e.classList.toggle('cant', !locked && !can);
      setHtml(e.querySelector('.bcost'), costTextRich(cost, w));
    }
  }

  // ── Tooltips ─────────────────────────────────────────────────────────────

  /**
   * The open tooltip's renderer, and where the pointer left it.
   *
   * Tips here are live: the closure that drew one is kept and re-run four times
   * a second, so a rate, a cooldown, a company's losses or a build cost go on
   * counting while the pointer sits still. Every closure captures an id or an
   * index and reads the world through `this.w` — never a World — which is what
   * lets a tip survive `adoptWorld` swapping the world out from under it.
   */
  private tipRender: (() => void) | null = null;
  private tipX = 0;
  private tipY = 0;
  private tipTimer = 0;

  /**
   * A renderer may close the tip on itself — asked about a company that has
   * just been wiped, there is nothing honest left to say. Read through a method
   * so the compiler does not assume the field cannot have changed under it.
   */
  private tipOpen(): boolean {
    return this.tipRender !== null;
  }

  private hover(ev: MouseEvent, render: () => void): void {
    /**
     * A finger is not a hover, however hard the browser insists it is.
     *
     * The panels are DOM, so a mobile browser fires a *compatibility*
     * `mouseenter` after every tap on one — which meant a tooltip opened on the
     * tap that armed a building, and then stayed up for the rest of the run,
     * because the `mouseleave` that would close it needs a pointer to leave and
     * there is not one. Measured on a 390px phone: a 330×182 box, permanently
     * over the map, following each subsequent tap around the screen.
     *
     * `isTrusted` is the discriminator, and it is exact: the browser's echo is
     * trusted, the `mouseenter` `TouchTips` synthesises for a deliberate long
     * press is not, and a real mouse on a hybrid machine is trusted but is not
     * within `ECHO_MS` of a finger. So hover keeps working everywhere it was
     * ever the right answer, and stops being guessed at where it is not.
     */
    if (ev.isTrusted && this.touchTips?.recent) return;
    this.tipRender = render;
    this.tipX = ev.clientX;
    this.tipY = ev.clientY;
    this.tipTimer = 0;
    // Cleared before the render, so that a renderer which closes the tip on
    // itself — asked about a company that has just been wiped — is allowed to.
    this.tipHeld = false;
    render();
    if (this.tipOpen()) {
      this.tipHeld = !ev.isTrusted;
      /**
       * A tip a finger opened catches the finger that dismisses it.
       *
       * "Tap anywhere to close" has to mean only that. The docked box covers a
       * good part of the map, and with the default `pointer-events: none` the
       * dismissing tap would fall straight through it and place a building
       * under the panel the player was reading. Only for the held case: a
       * pointer-driven tip must stay untouchable, or a docked box drawn over
       * the element being hovered would take the pointer off it, close itself,
       * and reopen, forty times a second.
       */
      this.tip.classList.toggle('held', this.tipHeld);
      this.tip.style.display = 'block';
      this.positionTip();
    }
  }

  /**
   * This tip was opened by a press-and-hold, and only the next touch may close
   * it.
   *
   * The lift at the end of that press produces the compatibility burst, and the
   * burst carries a `mouseleave` for whatever the browser *last* believed was
   * hovered — which is the element the player questioned a moment ago, on the
   * other side of the screen. That `mouseleave` reached `hideTip` and shut the
   * tooltip about a tenth of a second after it opened: measured, the box was
   * built with the right text in it and was gone before it could be read.
   *
   * Refusing the close here rather than passing the event down to thirty-odd
   * `onmouseleave` closures keeps the rule in one place, where the reason for
   * it is written.
   */
  private tipHeld = false;

  private refreshTip(dt: number): void {
    if (!this.tipRender || this.tip.style.display !== 'block') return;
    this.tipTimer += dt;
    if (this.tipTimer < 0.25) return;
    this.tipTimer = 0;
    // The hold is lifted across the render and put back only if the render did
    // not close the tip itself — a renderer with nothing honest left to say
    // must still be able to take it down, finger or no finger.
    const held = this.tipHeld;
    this.tipHeld = false;
    this.tipRender();
    if (this.tipOpen()) {
      this.tipHeld = held;
      this.positionTip();
    }
  }

  private setTip(name: string, rules: string, stats: string, flavour: string): void {
    this.tip.innerHTML =
      `<div class="tname">${esc(name)}</div>` +
      (rules ? `<div class="trule">${rules}</div>` : '') +
      (stats ? `<div class="tstat">${stats}</div>` : '') +
      (flavour ? `<div class="tflav">${esc(flavour)}</div>` : '');
  }

  private positionTip(): void {
    /**
     * On a narrow screen the tip is not placed, it is docked — and the way to
     * dock it is to write no inline geometry at all.
     *
     * A 330px box put at the finger is most of a phone's width and sits under
     * the hand that asked for it, and `left`/`top` written here would beat the
     * stylesheet's rules the way three other inline styles have already beaten
     * them on this layout. So the sheet gets the whole decision: under the
     * strip, full width, capped and scrolling. One place a tooltip ever
     * appears is also one less thing to learn.
     */
    if (this.narrow) {
      this.tip.style.left = '';
      this.tip.style.top = '';
      return;
    }
    const pad = 14;
    const r = this.tip.getBoundingClientRect();
    let x = this.tipX + pad;
    let y = this.tipY + pad;
    if (x + r.width > window.innerWidth - 8) x = this.tipX - r.width - pad;
    if (y + r.height > window.innerHeight - 8) y = this.tipY - r.height - pad;
    this.tip.style.left = `${Math.max(4, x)}px`;
    this.tip.style.top = `${Math.max(4, y)}px`;
  }

  private hideTip(): void {
    if (this.tipHeld) return;
    this.tip.style.display = 'none';
    this.tip.classList.remove('held');
    this.tipRender = null;
  }

  /** The next touch, taking down a tip a press-and-hold put up. */
  private releaseTip(): void {
    this.tipHeld = false;
    this.hideTip();
  }

  // ── Tooltip content ──────────────────────────────────────────────────────

  /**
   * A resource chip.
   *
   * Where it comes from and where it goes is read out of `BUILDING_TYPES`
   * rather than written down here, so a new building that makes iron says so on
   * the iron chip the day it is added.
   */
  private resourceTip(k: ResourceKey): void {
    const w = this.w;
    if (!w) return;
    /**
     * On the realm the chip is about the crown's purse, so the card has to be
     * too — a tooltip that answered with the town's warehouses under a number
     * that is not the town's would be worse than no tooltip.
     */
    const rs = this.realmState(w);
    if ((this.chrome.onRealm || isVillage(w)) && rs?.campaign) {
      const c = rs.campaign;
      const purse = realmPurse(c, c.playerNation);
      const chip = purse.chips.find((x) => x.key === k);
      if (chip) {
        const rows = [
          `Banked <b>${Math.floor(chip.have)}</b> — the crown's, not the town's`,
          `The council brings <b>${signed(chip.perMonth, 0)}</b> a month`,
        ];
        if (k === 'gold') {
          rows.push(`Hosts and hulls are paid out of this before anything else is`);
        }
        // Kept from the realm column's own row, which is where this sentence
        // used to live: a harbour wants timber and so does every hull, and a
        // shortage is a price rather than a wall.
        if (k === 'timber') {
          rows.push(
            `Timber you have not got is bought in at <b>${GOLD_PER_MISSING_TIMBER}</b> gold the plank`,
          );
        }
        this.setTip(
          RESOURCE_LABELS[k],
          '',
          rows.join('<br>'),
          'Your capital keeps its own stores. These are the realm’s.',
        );
        return;
      }
    }
    const cap = capOf(w, k);
    const perMin = (this.rateSample[k] ?? 0) * 60;
    const rows: string[] = [];
    rows.push(
      Number.isFinite(cap)
        ? `Held <b>${Math.floor(w.res[k])}</b> of <b>${Math.floor(cap)}</b> storage`
        : `Held <b>${Math.floor(w.res[k])}</b> — no warehouse holds it`,
    );
    rows.push(`Moving <b>${signed(perMin, 1)}</b>/min at the last second's pace, everything counted`);
    // Which rung of the ladder this is. The strip is filtered by Age, so a
    // player is owed the reason a bar arrived — or, later, went quiet.
    if (RESOURCE_AGE[k] > 1) {
      rows.push(`A good of the <b>${esc(AGE_NAMES[RESOURCE_AGE[k] - 1] ?? String(RESOURCE_AGE[k]))}</b> Age`);
    }
    if (Number.isFinite(cap) && w.res[k] >= cap - 1) {
      rows.push(`<b style="color:#d1352c">At the brim — every further load is wasted.</b>`);
    }

    if (k === 'gold') {
      rows.push(
        `Tax <b>+${w.econ.taxIncome.toFixed(1)}</b>/min · Trade <b>+${w.econ.tradeIncome.toFixed(1)}</b>/min`,
      );
      rows.push(
        `Wages <b>−${w.econ.wageBill.toFixed(1)}</b>/min for <b>${w.econ.onPayroll}</b> on the payroll`,
      );
      rows.push(`Net <b>${signed(w.econ.net, 1)}</b>/min`);
      if (w.arrears > 0) {
        rows.push(`<b style="color:#d1352c">Purse short for ${w.arrears.toFixed(0)}s. They are counting.</b>`);
      }
    }
    if (k === 'grain' || k === 'bread') {
      rows.push(
        `The settlement is <b>${(w.fedFraction * 100).toFixed(0)}%</b> fed` +
          (w.breadShare > 0 ? `, <b>${(w.breadShare * 100).toFixed(0)}%</b> of that on bread` : ''),
      );
    }
    /**
     * What the season is doing to *this* pile, said in the place the player is
     * already looking when they wonder why the number moved. Exact percentages,
     * both ways round — a season that only announced good news would be worse
     * than no announcement at all.
     */
    const sy = seasonYield(w, k);
    if (sy !== 1) {
      rows.push(
        `${esc(season(w).name)}: production <b>${signed((sy - 1) * 100, 0)}%</b> while it lasts`,
      );
    }
    if ((k === 'grain' || k === 'bread') && seasonUpkeep(w) !== 1) {
      rows.push(
        `${esc(season(w).name)}: the town eats <b>${signed((seasonUpkeep(w) - 1) * 100, 0)}%</b>`,
      );
    }

    const made = BUILDING_TYPES.filter((d) => (d.produces[k] ?? 0) > 0);
    if (made.length > 0) {
      rows.push(
        `Made at ` +
          made
            .slice(0, 4)
            .map((d) => `${esc(d.name)} <b>${((d.produces[k] ?? 0) * 60).toFixed(1)}</b>/min`)
            .join(', ') +
          (made.length > 4 ? ` and ${made.length - 4} more` : ''),
      );
    }
    const eaten = BUILDING_TYPES.filter((d) => (d.consumes[k] ?? 0) > 0);
    if (eaten.length > 0) {
      rows.push(
        `Eaten by ` +
          eaten
            .slice(0, 4)
            .map((d) => {
              const out = RESOURCE_KEYS.filter((o) => (d.produces[o] ?? 0) > 0).map((o) => RESOURCE_LABELS[o]);
              return `${esc(d.name)}${out.length > 0 ? ` → ${out.join(', ')}` : ''}`;
            })
            .join(', ') +
          (eaten.length > 4 ? ` and ${eaten.length - 4} more` : ''),
      );
    }
    if (Number.isFinite(cap)) rows.push(`Storage rises with every granary and warehouse you raise`);
    this.setTip(RESOURCE_LABELS[k], '', rows.join('<br>'), '');
  }

  private popTip(): void {
    const w = this.w;
    if (!w) return;
    const rs = this.realmState(w);
    if (this.chrome.onRealm && rs?.campaign) {
      const c = rs.campaign;
      const purse = realmPurse(c, c.playerNation);
      this.setTip(
        'The Levies',
        '',
        [
          `<b>${Math.floor(purse.levy.have)}</b> men in the bank`,
          `<b>+${Math.round(purse.levy.perMonth)}</b> a month from the countryside`,
          `<b>${purse.hosts}</b> host${purse.hosts === 1 ? '' : 's'} under arms · <b>${Math.round(purse.points)}</b> pts`,
          `<b>${purse.holdings}</b> province${purse.holdings === 1 ? '' : 's'} held`,
        ].join('<br>'),
        'Raising a host spends the bank. It does not spend the income.',
      );
      return;
    }
    const villagerCap = Math.max(6, Math.floor(w.popCap * VILLAGER_SHARE));
    const rows = [
      `Population <b>${w.pop}</b> of <b>${w.popCap}</b>`,
      `Villagers <b>${w.counts.villagers}</b> of <b>${villagerCap}</b> — the rest of the cap is held for soldiers`,
      `<b>${w.counts.idleVillagers}</b> standing idle · <b>${w.econ.onPayroll}</b> drawing a wage`,
      `Morale <b>${(w.morale * 100).toFixed(0)}%</b> — ${moraleWord(w.morale)}`,
      `Fed <b>${(w.fedFraction * 100).toFixed(0)}%</b>` +
        (w.breadShare > 0 ? `, <b>${(w.breadShare * 100).toFixed(0)}%</b> of it on bread` : ''),
      `Tax ${esc(taxBand(w.taxPolicy).name)} — <b>${signed(taxBand(w.taxPolicy).morale * 100, 0)}%</b> morale`,
    ];
    if (w.rubbleCount > 0) rows.push(`<b>${w.rubbleCount}</b> tiles of rubble still to clear`);
    this.setTip('The People', '', rows.join('<br>'), 'Counted at dawn, counted again at dusk.');
  }

  /**
   * The tax dial. `band` is the notch under the pointer, or −1 for the dial.
   *
   * The whole table is printed either way: the band you are on is only a
   * decision if you can see what the next one costs.
   */
  private taxTip(band: number): void {
    const w = this.w;
    if (!w) return;
    const rows = TAX_BANDS.map((b, i) => {
      const head = i === w.taxPolicy ? '▸' : '·';
      const gold = b.goldPerHeadPerMin;
      const mark = i === band && i !== w.taxPolicy ? ' ←' : '';
      return (
        `${head} <b>${esc(b.name)}</b> — ` +
        `${gold === 0 ? 'no tithe' : `<b>${signed(gold, 2)}</b> gold per head per minute`}` +
        `, morale <b>${signed(b.morale * 100, 0)}%</b>${mark}`
      );
    });
    rows.push(
      `At <b>${w.pop}</b> souls that is <b>+${w.econ.taxIncome.toFixed(1)}</b> gold/min now, ` +
        `against a wage bill of <b>${w.econ.wageBill.toFixed(1)}</b>`,
    );
    if (!this.cb.onTaxPolicy) {
      rows.push(`<span class="dim">Nothing here turns it yet — only the Chronicle moves the band.</span>`);
    }
    const shown = taxBand(band >= 0 ? band : w.taxPolicy);
    this.setTip(`Tax — ${shown.name}`, '', rows.join('<br>'), shown.note);
  }

  private willTip(): void {
    const w = this.w;
    if (!w) return;
    const rate = this.rateSample.will ?? 0;
    const affordable = w.unlockedPowers.filter((id) => w.will >= resolvePower(w, power(id)).cost).length;
    const rows = [
      `Will <b>${Math.floor(w.will)}</b> of <b>${Math.floor(w.willMax)}</b>`,
      `<b>${signed(rate, 1)}</b>/s over the last second, net of anything you spent in it`,
      `Regathers faster while a wave is on the field than between them`,
      `<b>${affordable}</b> of <b>${w.unlockedPowers.length}</b> Powers are within reach right now`,
    ];
    this.setTip(
      'Will',
      'What the Hearth can still ask of the world.',
      rows.join('<br>'),
      'It is not magic. It is attention, and there is only so much of it.',
    );
  }

  /**
   * The date, the season and exactly what the season is doing — the same two
   * lines whether a wave is on the field or not, because winter does not stop
   * for a siege. `rules` is the mechanical text verbatim, per the tooltip rule
   * at the top of this file: the player reads it and knows the number.
   */
  private seasonRows(w: World): string[] {
    const s = season(w);
    const left = daysToNextSeason(w);
    return [
      `${esc(formatDateLong(w))} · <b>${left}</b> ${left === 1 ? 'day' : 'days'} of it left`,
      esc(s.rules),
    ];
  }

  private phaseTip(): void {
    const w = this.w;
    if (!w) return;
    const b = w.battle;
    if (b) {
      const ps = armyStrength(w, b.player);
      const es = armyStrength(w, b.enemy);
      const rows = [
        b.deploying
          ? `Deployment — place your companies, then sound the horn`
          : `Fighting for <b>${Math.floor(b.elapsed / 60)}m ${Math.floor(b.elapsed % 60)}s</b>`,
        `Yours <b>${ps.alive}</b>/${ps.total} standing · resolve <b>${(b.player.resolve * 100).toFixed(0)}%</b>`,
        `Theirs <b>${es.alive}</b>/${es.total} standing · resolve <b>${(b.enemy.resolve * 100).toFixed(0)}%</b>`,
        `An army that loses its resolve leaves the field, whatever is left of it`,
      ];
      this.setTip(b.scenario.name, b.scenario.objective, rows.join('<br>'), b.scenario.flavour);
      return;
    }
    /**
     * In a campaign the clock is the council's, so its card is too — and it is
     * the same card on both maps, which is the whole of what "one set of
     * furniture" means here.
     */
    const rs = this.realmState(w);
    const rc = rs?.campaign;
    if (rc && rs) {
      const town = this.townWorld(w);
      const days = daysToNextMonth(rs);
      const rows = [
        `<b>${esc(formatDateLong(rs))}</b>`,
        rc.phase === TurnPhase.Orders
          ? `The council sits in <b>${days}</b> day${days === 1 ? '' : 's'}`
          : `<b>${esc(TURN_PHASE_NAMES[rc.phase] ?? 'The council')}</b> — it is sitting now`,
        `Age <b>${town.age}</b> — ${esc(AGE_NAMES[town.age - 1] ?? 'Hollow')} · <b>${town.wave}</b> assaults held`,
        ...this.seasonRows(town),
        `Both halves run on this one clock. Neither of them waits for you.`,
      ];
      this.setTip(
        'The Long Ledger',
        '',
        rows.join('<br>'),
        'A month is a council. Everything the crowns decide, they decide there.',
      );
      return;
    }
    if (w.phase === Phase.Siege) {
      const left = remainingThreat(w);
      const total = Math.max(1, w.incomingBudget);
      const rows = [
        `<b>${Math.max(0, Math.round((left / total) * 100))}%</b> of the wave is still coming or still standing`,
        `Threat left <b>${Math.round(left)}</b> of <b>${Math.round(total)}</b>`,
        `<b>${w.counts.enemy}</b> of them on the field · <b>${w.spawnQueue.length}</b> still to arrive`,
        `<b>${w.counts.player}</b> of yours alive`,
        ...this.seasonRows(w),
      ];
      this.setTip(`Siege — Wave ${w.wave}`, '', rows.join('<br>'), 'It ends when the last of them is down.');
      return;
    }
    const t = Math.max(0, w.phaseT);
    const rows = [
      `Age <b>${w.age}</b> — ${esc(AGE_NAMES[w.age - 1] ?? 'Hollow')} · wave <b>${w.wave}</b> survived`,
      ...this.seasonRows(w),
    ];
    // A capital has no director planning a wave at it, so it has nothing to
    // forecast: what comes for it comes off the world map, on its own clock.
    if (w.config.homeCity) {
      rows.unshift(`No wave is being counted down here — the realm decides who marches`);
    } else {
      const r = scoutingReport(w);
      rows.unshift(`<b>${Math.floor(t / 60)}m ${Math.floor(t % 60).toString().padStart(2, '0')}s</b> until the horn sounds itself`);
      rows.push(`Next: <b>${esc(r.faction)}</b>, ${esc(r.strength)}`);
      rows.push(`Coming by <b>${r.gates.map(esc).join(', ')}</b>`);
    }
    this.setTip('Build', '', rows.join('<br>'), 'Every second here is a second they are also using.');
  }

  private hornTip(): void {
    const w = this.w;
    if (!w) return;
    if (w.battle) {
      this.setTip(
        w.battle.deploying ? 'Sound the horn' : 'Engaged',
        w.battle.deploying
          ? 'Ends deployment and starts the battle. Nothing moves until you do.'
          : 'The lines are already committed.',
        '',
        '',
      );
      return;
    }
    if (w.config.homeCity) {
      this.setTip(
        'Sound the horn',
        'There is nobody at the gates to call in. A capital is besieged from the map, not on a timer.',
        '',
        '',
      );
      return;
    }
    const t = Math.max(0, w.phaseT);
    this.setTip(
      'Sound the horn',
      w.phase === Phase.Build
        ? `Calls the wave in now instead of in ${Math.ceil(t)}s.`
        : 'The wave is already here.',
      w.phase === Phase.Build
        ? `<b>${Math.ceil(t)}s</b> of building given up · nothing is refunded for the hurry`
        : '',
      'They were always going to come. This only decides whether you were ready.',
    );
  }

  /** The hero panel: the whole sheet in numbers, without opening the sheet. */
  private heroTip(): void {
    const w = this.w;
    if (!w) return;
    const cls = heroClassOrNull(w);
    if (!cls) return;
    const h = w.hero;
    const p = heroXpProgress(w);
    const i = heroUnit(w);
    const rows = [
      `Level <b>${h.level}</b> · experience <b>${Math.floor(p.into)}</b> of <b>${Math.floor(p.need)}</b>`,
      `Skill points unspent <b>${heroPointsLeft(w)}</b>`,
    ];
    if (h.fallen) {
      rows.push(
        h.reviveT > 0
          ? `<b style="color:#d1352c">Down.</b> <b>${Math.ceil(h.reviveT)}s</b> before he can be called back`
          : `<b style="color:#d1352c">Down.</b> <b>${HERO_REVIVE_SOULS}</b> Souls to call him back — you hold <b>${Math.floor(w.res.souls)}</b>`,
      );
    } else if (i >= 0) {
      rows.push(`Health <b>${Math.round(w.units.hp[i])}</b> of <b>${Math.round(w.units.maxHp[i])}</b>`);
      rows.push(`Abilities cast from where he stands, within <b>${HERO_ABILITY_RANGE}</b> tiles`);
    } else {
      rows.push(`Not on the field yet`);
    }
    rows.push(`${esc(cls.startingPassive.name)} — ${esc(cls.startingPassive.rules)}`);
    // No key named here: the sheet's hotkey lives in `main.ts` and this file
    // has no way to be told when it moves. The button below says it instead.
    rows.push(`<span class="dim">Click for the character sheet</span>`);
    this.setTip(h.name, `${cls.name} — ${cls.epithet}`, rows.join('<br>'), '');
  }

  /**
   * A build-menu entry. Priced against the treasury as it stands, because "20
   * stone" and "20 stone, of which you have 6" are different sentences.
   */
  private buildingTip(typeIdx: number): void {
    const w = this.w;
    const def = buildingType(typeIdx);
    const rows: string[] = [];
    if (w) {
      const cost = buildCostFor(w, typeIdx);
      const short = RESOURCE_KEYS.filter((k) => (cost[k] ?? 0) > w.res[k]);
      rows.push(`Costs ${costTextRich(cost, w)} · build time <b>${def.buildTime}s</b> of one villager's work`);
      rows.push(`A <b>free access road</b> connects this building when finished, where a clear route exists`);
      /**
       * The Age is stated as a *fact* on every entry, not only on the refused
       * ones — "opened by the Bell" is the sentence that teaches the ladder,
       * and a player standing in the Bell deserves to know that this is what
       * they climbed for. The refusal underneath it is the simulation's own,
       * word for word: the greyed button and the refused click say the same
       * thing because they ask the same function.
       */
      rows.push(
        `Opened by the <b>${esc(AGE_NAMES[def.tier - 1] ?? '')}</b>` +
          `<span class="dim"> · Age ${ROMAN[def.tier] ?? def.tier}</span>`,
      );
      const refusal = buildRefusal(w, typeIdx);
      if (refusal !== '') {
        rows.push(`<b style="color:#d1352c">${esc(refusal)}</b>`);
      } else if (short.length > 0) {
        rows.push(
          `<b style="color:#d1352c">Short ` +
            short.map((k) => `${Math.ceil((cost[k] ?? 0) - w.res[k])} ${RESOURCE_LABELS[k]}`).join(', ') +
            `.</b>`,
        );
      }
    } else {
      rows.push(`Build time <b>${def.buildTime}s</b> of one villager's work`);
    }
    rows.push(`<b>${def.w}×${def.h}</b> footprint · <b>${def.hp}</b> health`);
    if (def.workers > 0) rows.push(`<b>${def.workers}</b> worker slots`);
    for (const k of RESOURCE_KEYS) {
      const p = def.produces[k] ?? 0;
      if (p > 0) rows.push(`Produces <b>${(p * 60).toFixed(1)}</b> ${RESOURCE_LABELS[k]}/min at full staffing`);
    }
    for (const k of RESOURCE_KEYS) {
      const c = def.consumes[k] ?? 0;
      if (c > 0) rows.push(`Consumes <b>${(c * 60).toFixed(1)}</b> ${RESOURCE_LABELS[k]}/min`);
    }
    if (def.popCap > 0) rows.push(`Houses <b>${def.popCap}</b>`);
    if (def.storage > 0) rows.push(`Storage <b>+${def.storage}</b>`);
    if (def.morale !== 0) rows.push(`Morale <b>${def.morale > 0 ? '+' : ''}${(def.morale * 100).toFixed(0)}%</b>`);
    if (def.attack) {
      rows.push(
        `Attack <b>${def.attack.damage}</b> at <b>${def.attack.range}</b> tiles, <b>${def.attack.rate.toFixed(2)}</b>/s` +
          (def.attack.splash > 0 ? `, splash <b>${def.attack.splash}</b>` : ''),
      );
    }
    if (def.needsNearTerrain) {
      rows.push(
        `Must stand <b>beside ${def.needsNearTerrain.map((t) => TERRAIN_NAMES[t] ?? 'somewhere else').join(' or ')}</b>`,
      );
    }
    const harvest = siteHarvestKind(def);
    if (harvest >= 0) {
      const source = harvest === 0 ? 'standing trees' : harvest === 1 ? 'a stone outcrop' : 'an iron outcrop';
      rows.push(`Place on clear ground <b>beside ${source}</b>, within ${HARVEST_RADIUS} tiles.`);
      rows.push('Workers walk to the source and carry loads home. Leave a clear path to it.');
    } else if (def.needsTerrain) {
      rows.push(
        `Must stand on <b>${def.needsTerrain.map((t) => TERRAIN_NAMES[t] ?? 'somewhere else').join(' or ')}</b>`,
      );
    }
    if (def.grants.length > 0) {
      rows.push(`Grants <b>${def.grants.map((g) => power(g).name).join(', ')}</b>`);
    }
    if (def.trains.length > 0) rows.push(`Drills <b>${def.trains.join(', ')}</b>`);
    this.setTip(def.name, '', rows.join('<br>'), def.flavour);
  }

  private powerTip(id: string): void {
    const w = this.w;
    if (!w) return;
    const def = power(id);
    const r = resolvePower(w, def);
    const tier = w.powerTiers[id] ?? 0;
    const cd = w.powerCooldowns[id] ?? 0;
    const slot = w.unlockedPowers.indexOf(id);
    const rows = [
      `Will <b>${Math.round(r.cost)}</b> of <b>${Math.floor(w.will)}</b> held · Cooldown <b>${r.cooldown.toFixed(0)}s</b>`,
      r.radius > 0 ? `Radius <b>${r.radius.toFixed(1)}</b> tiles` : '',
      r.damage > 0 ? `Damage <b>${Math.round(r.damage)}</b>` : '',
      r.duration > 0 ? `Duration <b>${r.duration.toFixed(1)}s</b>` : '',
      def.delay > 0 ? `Lands after <b>${def.delay.toFixed(1)}s</b>` : '',
      def.friendlyFire ? `<b style="color:#d1352c">Hits your own.</b>` : '',
      cd > 0 ? `<b style="color:#d1352c">Cooling — ${Math.ceil(cd)}s.</b>` : '',
      cd <= 0 && w.will < r.cost
        ? `<b style="color:#d1352c">Short ${Math.ceil(r.cost - w.will)} Will.</b>`
        : '',
      `Tier <b>${tier}/3</b>` + (tier < 3 ? ` — next: ${esc(def.tiers[tier].text)} (${def.tiers[tier].soulCost} Souls)` : ''),
      slot >= 0 && slot < POWER_KEYS.length
        ? `<span class="dim">Press ${POWER_KEYS[slot]}, then click the ground</span>`
        : '',
    ].filter(Boolean);
    this.setTip(def.name, def.rules, rows.join('<br>'), def.flavour);
  }

  /**
   * A company, from the roster row or from its card. Everything the card cannot
   * fit: what it is, what it has done, what it hits for, and how tired it is.
   */
  private companyTip(id: number): void {
    const w = this.w;
    if (!w) return;
    const c = w.companies.find((cc) => cc.id === id);
    if (!c) {
      // The company died under the pointer. Nothing left to describe.
      this.hideTip();
      return;
    }
    const t = unitType(c.typeIdx);
    const r = readCompany(w, c);
    const rows = [
      `${esc(t.name)} · ${ROLE_NAMES[roleOf(t)]} · <b>${c.members.length}/${c.maxSize}</b> standing`,
      `Health <b>${Math.round(r.hp)}</b> of <b>${Math.round(r.maxHp)}</b> (<b>${(r.health * 100).toFixed(0)}%</b>)`,
      `Rank <b>${RANK_NAMES[c.rank]}</b> · <b>${c.kills}</b> killed · <b>${c.losses}</b> lost`,
      `Damage <b>${t.damage}</b> ${damageWord(t.dmgType)} · Reach <b>${t.range.toFixed(2)}</b> · <b>${t.attacksPerSec.toFixed(2)}</b>/s`,
      `Armour <b>${armourWord(t.armour)}</b> · Speed <b>${t.speed.toFixed(2)}</b> tiles/s`,
      r.ammoMax > 0
        ? `Ammunition <b>${Math.round(r.ammo)}</b> of <b>${Math.round(r.ammoMax)}</b> shots` +
          (r.ammo < 1 ? ` — <b style="color:#d1352c">dry</b>` : '')
        : '',
      `Nerve <b>${(r.morale * 100).toFixed(0)}%</b>` +
        (r.routing > 0 ? ` — <b style="color:#d1352c">${r.routing} running</b>` : '') +
        ` · Fatigue <b>${(r.fatigue * 100).toFixed(0)}%</b>`,
      `Cohesion <b>${(c.cohesion * 100).toFixed(0)}%</b> · ${FORMATION_NAMES[c.formation]} · ${STANCE_NAMES[c.stance]}`,
      c.members.length < c.maxSize && !w.battle
        ? `Replacements for <b>${c.maxSize - c.members.length}</b>: ${costText(reinforceCost(w, t, c.maxSize - c.members.length))}`
        : '',
      `<span class="dim">Click to select, shift-click to add · F formation · G stance · H hold</span>`,
    ].filter(Boolean);
    this.setTip(c.name, '', rows.join('<br>'), t.flavour);
  }

  private reinforceTip(id: number): void {
    const w = this.w;
    if (!w) return;
    const c = w.companies.find((cc) => cc.id === id);
    if (!c) {
      this.hideTip();
      return;
    }
    const t = unitType(c.typeIdx);
    const missing = c.maxSize - c.members.length;
    const cost = reinforceCost(w, t, missing);
    const rows = [
      `Brings <b>${missing}</b> back to the standard for ${costText(cost)}`,
      `Replacements come <b>15%</b> under the price of raising them, and keep the company's veterancy`,
      canAfford(w.res, cost) ? '' : `<b style="color:#d1352c">Not in the stores.</b>`,
    ].filter(Boolean);
    this.setTip(`Reinforce ${c.name}`, '', rows.join('<br>'), 'New names, on an old list.');
  }

  // ── Modal ────────────────────────────────────────────────────────────────

  showRelicOffer(w: World): void {
    if (w.pendingEvent || w.relicOffer.length === 0) return;
    this.chronicleDialog?.hide();
    const cards = w.relicOffer
      .map((id) => {
        const r = relic(id);
        if (!r) return '';
        const rarity = ['', 'Common', 'Uncommon', 'Rare'][r.rarity] ?? '';
        return (
          `<div class="card" data-relic="${esc(id)}">` +
          `<div class="cn">${esc(r.name)}</div>` +
          `<div class="cr">${esc(r.rules)}</div>` +
          `<div class="cf">${esc(r.flavour)}</div>` +
          `<div class="crar">${rarity}</div></div>`
        );
      })
      .join('');
    this.modal.innerHTML =
      `<div class="sheet"><h1>Spoils</h1>` +
      `<div class="sub">The realm keeps what you choose. It keeps it for good.</div>` +
      `<div class="cards">${cards}</div></div>`;
    this.modal.classList.add('show');
    for (const el of this.modal.querySelectorAll('[data-relic]')) {
      (el as HTMLElement).onclick = () => {
        this.cb.onRelicPick((el as HTMLElement).dataset.relic as string);
        this.hideModal();
      };
    }
  }

  /**
   * The Chronicle.
   *
   * There is no dismiss button and Escape does not close it, because there is
   * nothing behind it to go back to: the simulation is frozen on
   * `world.pendingEvent` and the only thing that unfreezes it is an answer. A
   * choice the treasury cannot cover is shown, greyed, with the price on it —
   * seeing the door you cannot afford is part of the decision.
   */
  showChronicle(w: World): void {
    const p = w.pendingEvent, def = p && chronicleEvent(p.id);
    if (!p || !def) return;
    this.chronicleWorld = w;
    this.chroniclePending = { ...p, choices: [...p.choices] };
    this.chronicleDialog ??= new EventChoiceDialog(this.modal, index => {
      const current = this.chronicleWorld?.pendingEvent;
      if (!current || current.id !== this.chroniclePending?.id || current.wave !== this.chroniclePending.wave ||
        current.choices[index] !== this.chroniclePending.choices[index]) return false;
      const choice = chronicleEvent(current.id)?.choices.find(c => c.id === current.choices[index]);
      if (!choice || (choice.cost && !crownAfford(this.chronicleWorld!, choice.cost))) return false;
      this.cb.onEventChoice(current.id, index);
      return true;
    });
    // Prices use named resources, and the same pooled purse as doResolveEvent.
    const choices = p.choices.flatMap((id, index) => {
      const choice = def.choices.find(c => c.id === id);
      if (!choice) return [];
      const price = choice.cost ? RESOURCE_KEYS.filter(k => (choice.cost![k] ?? 0) > 0)
        .map(k => `${choice.cost![k]} ${RESOURCE_LABELS[k]}`).join(' · ') : '';
      return [{ index, label: choice.label, rules: choice.rules, price: price ? `Cost: ${price}` : '',
        affordable: !choice.cost || crownAfford(w, choice.cost) }];
    });
    this.chronicleDialog.show({ key: `${p.id}#${p.wave}`, title: def.title,
      subtitle: `Wave ${p.wave} · the settlement is waiting`, flavour: def.flavour, choices });
  }

  get chronicleOpen(): boolean { return this.chronicleDialog?.isOpen ?? false; }

  /**
   * A full-screen sheet, and the reading position the player was at.
   *
   * The character sheet redraws itself after every purchase — it has to, because
   * the tree it is showing has just changed — and the tree sits below the kit, so
   * a sheet that reopens at the top throws the player back past everything they
   * were reading each time they spend a point. Buying three skills in a row meant
   * scrolling down to the tree three times. The offset is carried across the
   * rebuild instead.
   *
   * `key` is what stops that from becoming a different bug. The offset is only
   * carried when the *same* sheet is being redrawn; opening the key list after
   * reading the bottom of the character sheet must open the key list at the top,
   * not halfway down. Sheets without a key never restore.
   */
  showSheet(html: string, key = ''): void {
    if (this.chronicleOpen) return;
    const same = key !== '' && key === this.sheetKey && this.modalOpen;
    this.sheetKey = key;
    keepScroll(this.modal, same ? ['.sheet'] : [], () => {
      this.modal.innerHTML = `<div class="sheet">${html}</div>`;
    });
    this.modal.classList.add('show');
  }

  /** Which sheet `showSheet` last drew, so a redraw of it can keep its place. */
  private sheetKey = '';

  hideModal(): void {
    this.chronicleDialog?.hide();
    this.modal.classList.remove('show');
    this.sheetKey = '';
  }

  get modalOpen(): boolean {
    return this.modal.classList.contains('show');
  }

  get modalEl(): HTMLElement {
    return this.modal;
  }

  /**
   * The hint line.
   *
   * `hintText()` is recomputed and pushed here every single frame, so anything
   * announced from outside the ticker — "Saved at wave 7", "Nothing saved",
   * "Sound off" — was overwritten about sixteen milliseconds later and never
   * actually seen. A message asked for with `hold` seconds outranks the ambient
   * per-frame hint until it expires.
   */
  hint(text: string, hold = 0): void {
    if (hold > 0) {
      this.hintHoldUntil = performance.now() + hold * 1000;
    } else if (performance.now() < this.hintHoldUntil) {
      return;
    }
    if (!text) {
      this.hintEl.classList.remove('show');
      return;
    }
    this.hintEl.textContent = text;
    this.hintEl.classList.toggle('bridge-plan', this.buildSel.kind === 'bridge');
    this.hintEl.classList.add('show');
  }

  private hintHoldUntil = 0;

  onMouseMove(ev: MouseEvent): void {
    if (this.tip.style.display !== 'block') return;
    this.tipX = ev.clientX;
    this.tipY = ev.clientY;
    this.positionTip();
  }
}

// ── The building panel ─────────────────────────────────────────────────────

/**
 * What is actually here, and why it is doing what it is doing.
 *
 * The whole panel is a read. `productionReadout` is `stepProduction`'s own
 * arithmetic asked rather than performed — including the throttle, which the
 * simulation has been computing and discarding on every tick since converters
 * existed — and `districtReasons` has been returning finished English sentences
 * about placement that nothing in the game has ever printed.
 *
 * Three things a player can *do* live at the bottom: raise its tier, pull it
 * down, and — for a trainer — muster a company. That last one is not a
 * convenience. Until now `onRecruit` was a callback with no caller: there was
 * no way to raise a company from the interface at all.
 */
function buildingPanelHtml(w: World, i: number, showLaterDrills: boolean): string {
  const b = w.buildings;
  const def = buildingType(b.typeId[i]);
  const r = productionReadout(w, i);
  const hp = b.hp[i] / Math.max(1, b.maxHp[i]);
  const rows: string[] = [];

  if (b.build[i] < 1) {
    const stage = { foundations: 'Foundations', frame: 'Timber frame', walls: 'Walls', roof: 'Roofing', complete: 'Complete' }[constructionStage(b.build[i])];
    rows.push(
      `<div class="bi-row"><span class="k">${stage}</span>` +
        `<span class="v">${(b.build[i] * 100).toFixed(0)}% · ${b.workers[i]} building</span></div>`,
      `<div class="bi-note">${def.buildTime}s of one villager's work in all. Construction outranks every other job in the town.</div>`,
    );
  }

  // ── Staffing and placement ───────────────────────────────────────────────
  if (def.workers > 0) {
    const tone = r.workers === 0 ? 'bad' : r.workers < def.workers ? 'mid' : 'good';
    rows.push(
      `<div class="bi-row"><span class="k">Hands</span>` +
        `<span class="v ${tone}">${r.workers} of ${def.workers}` +
        (r.workers < def.workers ? ` — ${(r.staffing * 100).toFixed(0)}% of its output` : '') +
        `</span></div>`,
    );
  }
  if (b.tier[i] > 1) {
    rows.push(
      `<div class="bi-row"><span class="k">Tier</span><span class="v">${b.tier[i]} — ×${r.tierMul.toFixed(2)} output</span></div>`,
    );
  }

  if (r.harvest >= 0) {
    const resource = nodeKind(r.harvest).resource;
    const sources = r.harvest === 0 ? 'trees' : 'outcrops';
    const countLabel = r.nodesInReach === 1 ? (r.harvest === 0 ? 'tree' : 'outcrop') : sources;
    rows.push(
      `<div class="bi-sec">Working the land</div>`,
      `<div class="bi-row"><span class="k">In reach</span><span class="v ${r.nodesInReach > 0 ? 'good' : 'bad'}">` +
        `${r.nodesInReach} ${countLabel}</span></div>`,
      `<div class="bi-row"><span class="k">Remaining</span><span class="v">` +
        `${Math.floor(r.amountInReach).toLocaleString()} ${esc(RESOURCE_LABELS[resource])}</span></div>`,
      r.nodesInReach > 0
        ? `<div class="bi-note">Workers gather from nearby ${sources} and carry each load back here. ` +
          `Shorter, open routes improve deliveries. The rate below is an estimate while hauling.</div>`
        : `<div class="bi-choke"><b>No reachable ${sources}.</b> Open a path or build a new camp beside a fresh source. ` +
          `Production stops when the nearby supply runs out.</div>`,
    );
    if (b.build[i] >= 1 && r.workers > 0 && r.nodesInReach > 0 && !r.hauling && !r.storageFull) {
      rows.push(`<div class="bi-choke">The crew is retrying its route. Keep the camp entrance and resource faces clear.</div>`);
    }
  }

  // ── What it makes, and what is stopping it ───────────────────────────────
  if (r.inputs.length > 0 || r.outputs.length > 0) {
    for (const o of r.outputs) {
      const pct = o.bestPerMin > 0 ? o.perMin / o.bestPerMin : 1;
      rows.push(
        `<div class="bi-row"><span class="k">Makes</span>` +
          `<span class="v ${pct < 0.6 ? 'bad' : pct < 0.95 ? 'mid' : 'good'}">` +
          `${RESOURCE_GLYPHS[o.k]} <b>${o.perMin.toFixed(2)}</b>/min` +
          `<span class="dim"> of ${o.bestPerMin.toFixed(2)}</span></span></div>`,
      );
    }
    for (const inp of r.inputs) {
      const starved = inp.perMin < inp.wantPerMin - 0.005;
      rows.push(
        `<div class="bi-row"><span class="k">Eats</span>` +
          `<span class="v ${starved ? 'bad' : ''}">${RESOURCE_GLYPHS[inp.k]} <b>${inp.perMin.toFixed(2)}</b>/min` +
          (starved ? `<span class="dim"> of ${inp.wantPerMin.toFixed(2)} wanted</span>` : '') +
          `<span class="dim"> · ${Math.floor(inp.held)} in store</span></span></div>`,
      );
    }
    if (r.starvedOf !== '' && r.throttle < 0.999) {
      rows.push(
        `<div class="bi-choke"><b>Throttled to ${(r.throttle * 100).toFixed(0)}%</b> — ` +
          `no ${esc(RESOURCE_LABELS[r.starvedOf])}. It will run at whatever the stores can pay for and no faster.</div>`,
      );
    }
    if (r.storageFull) {
      rows.push(`<div class="bi-choke"><b>${esc(r.limitReason)}</b> Free storage or use the finished goods to resume.</div>`);
    }
    if (r.famine && r.outputs.some((o) => o.k === 'grain')) {
      rows.push(`<div class="bi-choke"><b>Famine Wind.</b> Nothing comes off the fields while the wave stands.</div>`);
    }
  }

  // ── Where it stands ──────────────────────────────────────────────────────
  const why = districtReasons(w, i);
  const producer = r.outputs.length > 0;
  if (producer) {
    rows.push(
      `<div class="bi-row"><span class="k">Placement</span>` +
        `<span class="v ${r.placeMul > 1.001 ? 'good' : r.placeMul < 0.999 ? 'bad' : ''}">×${r.placeMul.toFixed(2)}</span></div>`,
    );
    if (why.length > 0) {
      rows.push(`<div class="bi-why">${why.map((s) => `<span>${esc(s)}</span>`).join('')}</div>`);
    } else {
      rows.push(
        `<div class="bi-note">Nothing about where it stands is helping. A road alongside, a warehouse ` +
          `within a short haul, or its supplier next door would each pay.</div>`,
      );
    }
    const seasoned = r.outputs.filter((o) => o.seasonMul !== 1);
    if (seasoned.length > 0) {
      rows.push(
        `<div class="bi-row"><span class="k">${esc(season(w).name)}</span><span class="v">` +
          seasoned.map((o) => `${RESOURCE_LABELS[o.k]} ${signed((o.seasonMul - 1) * 100, 0)}%`).join(' · ') +
          `</span></div>`,
      );
    }
  }

  // ── Standing facts ───────────────────────────────────────────────────────
  if (def.popCap > 0) {
    rows.push(`<div class="bi-row"><span class="k">Beds</span><span class="v">${def.popCap}</span></div>`);
  }
  if (def.storage > 0) {
    rows.push(`<div class="bi-row"><span class="k">Storage</span><span class="v">+${def.storage}</span></div>`);
  }
  if (def.morale !== 0) {
    rows.push(
      `<div class="bi-row"><span class="k">Morale</span>` +
        `<span class="v ${def.morale > 0 ? 'good' : 'bad'}">${signed(def.morale * 100, 0)}%</span></div>`,
    );
  }
  if (def.attack) {
    rows.push(
      `<div class="bi-row"><span class="k">Shoots</span><span class="v">` +
        `<b>${Math.round(def.attack.damage * r.tierMul)}</b> ${damageWord(def.attack.dmgType)} · ` +
        `${def.attack.range} tiles · ${def.attack.rate.toFixed(2)}/s` +
        (def.attack.splash > 0 ? ` · splash ${def.attack.splash}` : '') +
        `</span></div>`,
      `<div class="bi-note">${def.attack.hitsFliers ? 'Takes fliers as well as foot.' : 'Cannot reach anything that flies.'}</div>`,
    );
  }
  if (def.grants.length > 0) {
    rows.push(
      `<div class="bi-row"><span class="k">Grants</span><span class="v">${def.grants.map((gid) => esc(power(gid).name)).join(', ')}</span></div>`,
    );
  }

  // What a crew would cost here, quoted from the simulation's own reader so the
  // button cannot promise a price the storehouse will not charge.
  const repairRow = repairQuote(w, i);
  const repairOrdered = repairRow?.ordered ?? false;
  const repairCost = repairRow?.cost ?? {};

  // ── The garrison, and what it can raise ──────────────────────────────────
  let muster = '';
  const trained = unitsTrainedBy(w, def);
  if (trained.length > 0) {
    const ids = trained.map((t) => unitType(t).id);
    const standing = standingWage(w, ids);
    rows.push(
      `<div class="bi-row"><span class="k">Garrison</span><span class="v">` +
        (standing.models > 0
          ? `<b>${standing.models}</b> of its kind standing`
          : `<span class="dim">Nobody yet</span>`) +
        `</span></div>`,
      `<div class="bi-row"><span class="k">Upkeep</span>` +
        `<span class="v ${standing.wage > 0 ? 'bad' : 'dim'}">${standing.wage > 0 ? `−${standing.wage.toFixed(1)} gold/min` : 'none'}</span></div>`,
    );
    muster = `<div class="bi-sec">Drills</div>` + drillList(w, trained, showLaterDrills);
  }

  // ── What can be done to it ───────────────────────────────────────────────
  const maxTier = Math.max(def.maxTier, w.faction.bonuses.maxBuildingTier ?? 3);
  const upCost = upgradeCost(def, b.tier[i]);
  const canUp = b.build[i] >= 1 && b.tier[i] < maxTier && canAfford(w.res, upCost);
  const upLabel =
    b.tier[i] >= maxTier
      ? 'At its highest tier'
      : b.build[i] < 1
        ? 'Not until it is finished'
        : `Raise to tier ${b.tier[i] + 1}`;

  return (
    `<div class="hdr">${esc(def.name)}<span class="bi-x" data-bact="close">✕</span></div>` +
    `<div class="bi-body">` +
    `<div class="bi-flav">${esc(def.flavour)}</div>` +
    `<div class="bi-hp"><i style="width:${(clamp01(hp) * 100).toFixed(0)}%" class="${hp < 0.5 ? 'hurt' : ''}"></i></div>` +
    `<div class="bi-row"><span class="k">Health</span>` +
    `<span class="v ${hp < 0.5 ? 'bad' : ''}">${Math.round(b.hp[i])} of ${Math.round(b.maxHp[i])}</span></div>` +
    rows.join('') +
    muster +
    `<div class="bi-acts">` +
    /**
     * Repair, and only when there is something to repair.
     *
     * `stepRepair` already mends anything damaged during a build phase without
     * being asked; what this verb buys is the *siege* case, where crews stay in
     * cover unless a specific building is named. So the button is a standing
     * order, not an act — hence "Call them off" rather than "Cancel" — and its
     * cost is the one `repairReport` quotes, so the number on the button and
     * the number the storehouse pays are the same arithmetic.
     */
    (hp < 1
      ? `<span class="seal ghost tiny" data-bact="repair" title="${esc(
          repairOrdered ? 'Send the masons somewhere else' : 'Put a crew on it now, siege or no siege',
        )}">${repairOrdered ? 'Call them off' : `Repair · ${costText(repairCost)}`}</span>`
      : '') +
    `<span class="seal ghost tiny${canUp ? '' : ' off disabled'}" data-bact="upgrade" title="${esc(upLabel)}">` +
    (b.tier[i] < maxTier ? `Upgrade · ${costText(upCost)}` : 'Highest tier') +
    `</span>` +
    `<span class="seal ghost tiny" data-bact="demolish" title="Returns two fifths of what went in">Demolish</span>` +
    `</div>` +
    `</div>`
  );
}

/**
 * Mirrored this arithmetic once and no longer does.
 *
 * The old copy read `def.cost`, which is empty for the Hearth, so the panel
 * cheerfully offered "Upgrade · free" for the one building in the game whose
 * upgrade the owner objected to being free. `upgradeCostFor` in `sim/apply.ts`
 * is the price now — the comment above this function always claimed the
 * simulation owned it, and it does.
 */
function upgradeCost(def: (typeof BUILDING_TYPES)[number], tier: number): Record<string, number> {
  const out: Record<string, number> = {};
  const base = def.upgradeCost ?? def.cost;
  const scale = 0.85 * tier;
  for (const k of RESOURCE_KEYS) {
    if ((base[k] ?? 0) <= 0) continue;
    out[k] = Math.round((base[k] ?? 0) * scale) + 10;
  }
  return out;
}

/** Mirrors `recruitCompany`: a whole company, priced per model. */
function musterCost(t: UnitType): Record<string, number> {
  const out: Record<string, number> = {};
  for (const k of RESOURCE_KEYS) {
    const v = t.cost[k] ?? 0;
    if (v > 0) out[k] = v * t.companySize;
  }
  return out;
}

/**
 * Which of the faction's roster this building can raise.
 *
 * The alias rules are `hasTrainer`'s, because they have to be: a Barracks lists
 * `spear` and `heavy` in `trains` and `trainerCategoryFor` never returns
 * either — melee is all one category — so a panel that matched the strings
 * literally would offer nothing at the one building the player builds first.
 */
function unitsTrainedBy(w: World, def: (typeof BUILDING_TYPES)[number]): number[] {
  if (def.trains.length === 0) return [];
  const out: number[] = [];
  for (const id of w.faction.roster) {
    const idx = unitTypeIndex(id);
    const want = trainerCategoryFor(idx);
    const ok =
      def.trains.includes(want) ||
      (want === 'militia' && def.trains.includes('spear')) ||
      (want === 'archer' && def.trains.includes('crossbow'));
    if (ok) out.push(idx);
  }
  return out;
}

/**
 * What a trainer can raise, under the same Age headings as the build menu.
 *
 * A Barracks in a mature settlement offers seven or eight kinds of foot, from
 * the militia of the first wave to the Hollowed of the last, and read as a flat
 * column they are eight near-identical rows in an arbitrary order. Grouped by
 * the Age that opened them they become a line of succession — this is what your
 * spearmen turn into — and the group above the current one is the plainest
 * possible answer to "what do I get for climbing".
 *
 * Same windowing as the build list, same fold, and the same rule about wording:
 * the Age is said once, in the heading. What used to be printed on every gated
 * row here — "Age 4 soldiery. This is Age 2." — was invented in this file, said
 * the same thing eight times, and is now the heading's job. The refusals that
 * survive are `musterRoom`'s, which is `recruitCompany`'s own rule and returns
 * its own sentence.
 */
function drillList(w: World, trained: number[], showLater: boolean): string {
  const now = Math.max(1, Math.min(MAX_AGE, w.age));
  const horizon = ageHorizon(now);
  const byAge: number[][] = [];
  for (let a = 0; a <= MAX_AGE; a++) byAge.push([]);
  for (const idx of trained) byAge[Math.max(1, Math.min(MAX_AGE, unitType(idx).tier))].push(idx);

  const out: string[] = [];
  const shownTo = showLater ? MAX_AGE : horizon;
  for (let a = 1; a <= shownTo; a++) {
    if (byAge[a].length === 0) continue;
    const h = ageHeading(a, now);
    out.push(
      `<div class="bi-age ${h.tone}"><span class="ba-n">${esc(h.numeral)}</span>` +
        `<span class="ba-a">${esc(h.name)}</span>` +
        (h.state ? `<span class="ba-s">${esc(h.state)}</span>` : '') +
        `</div>`,
    );
    for (const idx of byAge[a]) out.push(drillRow(w, idx, a > now));
  }

  let hidden = 0;
  for (let a = horizon + 1; a <= MAX_AGE; a++) hidden += byAge[a].length;
  if (hidden > 0 || showLater) {
    out.push(
      `<div class="bi-more" data-bact="drills">` +
        (showLater ? '▾  Enough — show who I can raise' : `▸  ${hidden} more, in ${esc(agesBeyond(horizon))}`) +
        `</div>`,
    );
  }
  return out.join('');
}

function drillRow(w: World, idx: number, ageLocked: boolean): string {
  const t = unitType(idx);
  const cost = musterCost(t);
  /**
   * Where they will *live* is the simulation's question, not this file's.
   *
   * This used to read `w.pop + t.pop * size > w.popCap + 4` — the housing
   * rule, written out by hand and applied to everybody. Races bind their
   * soldiery in different ways: a mana race holds its host in a pool and
   * has no use for beds at all, so the button greyed with "raise more
   * housing" against a settlement whose housing was never the problem —
   * and, worse, stayed *live* when the pool was full, because the beds
   * were fine. A button that passes its own check and is then refused by
   * `recruitCompany` is a button that does nothing.
   *
   * `musterRoom` is the rule `recruitCompany` itself applies, and it
   * returns the sentence to print. Seven of the eight bindings currently
   * fall through to housing, so this reads identically for them today —
   * and starts telling the truth the moment any of them is built.
   */
  /**
   * **This button asks the question `recruitCompany` asks, not a stricter one.**
   *
   * It used to gate on `canAfford(w.res, cost)` — the price of the *whole*
   * establishment. That was right while a company was an indivisible purchase
   * and became wrong the moment `recruitCompany` learned to raise one short: a
   * company is now mustered at whatever strength the beds, the grown hands, the
   * binding and the purse allow, down to a quarter of the roll, and charged per
   * man. So when the establishment went from fourteen men to forty, this button
   * stayed grey through a muster the simulation would happily have granted —
   * and the measured cost was the opening muster arriving roughly twice as late
   * as it used to, at 21 game-minutes against 13.
   *
   * `musterStrength` (beds, hands, binding) and `affordableModels` (purse)
   * combined against `minMuster` is `recruitCompany`'s own rule, called here
   * rather than restated, which is the discipline the note above about
   * `musterRoom` already argued for: a button that passes its own check and is
   * then refused by the simulation is a button that does nothing, and a button
   * that fails its own check where the simulation would have said yes is worse
   * — it hides a move the player was entitled to make.
   */
  const est = t.companySize;
  const room = ageLocked ? { n: est, why: '' } : musterStrength(w, idx, est);
  const can = ageLocked ? est : Math.min(room.n, affordableModels(w, t.cost, est));
  const short = !ageLocked && can < minMuster(est);
  const err = short ? room.why || 'Not in the stores.' : '';
  const off = ageLocked || err !== '';
  /**
   * A partial muster says so on the button, before it is pressed.
   *
   * The log already says "musters — 17 of 40. The rest of the roll is empty"
   * *afterwards*. Saying it here turns a surprise into a decision: raise a
   * short company now, or wait for the beds and the coin and raise a full one.
   */
  const partial = !off && can < est;
  return (
    `<div class="bi-muster${off ? ' off' : ''}${ageLocked ? ' locked' : ''}" data-bact="recruit" data-a="${idx}"` +
    ` title="${esc(err || t.flavour)}">` +
    `<div class="mn">${esc(t.name)} <span class="dim">×${est}</span></div>` +
    `<div class="mc">${costTextRich(partial ? scaleCost(t.cost, can) : cost, w)}</div>` +
    (err ? `<div class="me">${esc(err)}</div>` : '') +
    (partial ? `<div class="me short">${can} of ${est} — the rest of the roll is empty</div>` : '') +
    `</div>`
  );
}

/** What the soldiers a building drills are costing the purse, right now. */
function standingWage(w: World, unitIds: string[]): { wage: number; models: number } {
  const u = w.units;
  let wage = 0;
  let models = 0;
  for (let i = 0; i < u.slots.high; i++) {
    if (u.slots.alive[i] === 0 || u.team[i] !== Team.Player) continue;
    if ((u.flags[i] & UF.Villager) !== 0) continue;
    const t = unitType(u.typeId[i]);
    if (!unitIds.includes(t.id)) continue;
    wage += unitWage(t);
    models++;
  }
  return { wage, models };
}

// ── Company readouts ───────────────────────────────────────────────────────

interface CompanyReadout {
  hp: number;
  maxHp: number;
  /** hp/maxHp, already clamped and safe when the company has nobody left. */
  health: number;
  ammo: number;
  ammoMax: number;
  /** Mean over the men still standing, 0..1. */
  fatigue: number;
  morale: number;
  routing: number;
}

/**
 * One pass over a company's members.
 *
 * The roster row, the unit card and the tooltip all want the same eight
 * numbers, and they must agree: a card that says a company is fine beside a
 * tooltip that says it is broken is worse than either on its own. Handles that
 * no longer resolve are skipped rather than counted as zero, so a company mid
 * tick — after a death, before the compaction at the end of `stepCompanies` —
 * reads as slightly smaller rather than slightly dead.
 */
function readCompany(w: World, c: Company): CompanyReadout {
  const t = unitType(c.typeIdx);
  const u = w.units;
  let hp = 0;
  let maxHp = 0;
  let ammo = 0;
  let ammoMax = 0;
  let fatigue = 0;
  let morale = 0;
  let routing = 0;
  let live = 0;
  for (const h of c.members) {
    const i = u.resolve(h);
    if (i < 0) continue;
    live++;
    hp += u.hp[i];
    maxHp += u.maxHp[i];
    ammo += u.ammo[i];
    ammoMax += t.ammo;
    fatigue += u.fatigue[i];
    morale += u.morale[i];
    if (u.state[i] === UnitState.Routing) routing++;
  }
  const n = Math.max(1, live);
  return {
    hp,
    maxHp,
    health: maxHp > 0 ? clamp01(hp / maxHp) : 0,
    ammo,
    ammoMax,
    fatigue: fatigue / n,
    morale: morale / n,
    routing,
  };
}

/**
 * What replacements cost.
 *
 * Mirrors `reinforceCompany` in `sim/systems/companies.ts` — the simulation
 * owns the transaction, this only quotes it. If the two ever disagree the
 * simulation is right and this is the bug.
 */
function reinforceCost(w: World, t: UnitType, missing: number): Record<string, number> {
  const out: Record<string, number> = {};
  for (const k of RESOURCE_KEYS) {
    const v = t.cost[k] ?? 0;
    if (v > 0) out[k] = v * missing * 0.85 * w.mods.reinforceCost;
  }
  return out;
}

// ── Unit cards ─────────────────────────────────────────────────────────────

/**
 * The eight silhouettes a card can wear.
 *
 * Role, not unit: a card has to be recognised across the width of the screen in
 * the second before a charge lands, and eight shapes is about as many as anyone
 * can tell apart at that size. Which one a company gets is read off its flags,
 * so a new unit in `units.ts` arrives with the right card and no card work.
 */
type CardRole = 'infantry' | 'spear' | 'missile' | 'cavalry' | 'caster' | 'siege' | 'brute' | 'champion';

const ROLE_NAMES: Record<CardRole, string> = {
  infantry: 'Foot',
  spear: 'Spears',
  missile: 'Missiles',
  cavalry: 'Horse',
  caster: 'Caster',
  siege: 'Engine',
  brute: 'Monster',
  champion: 'Champion',
};

function roleOf(t: UnitType): CardRole {
  const f = t.flags;
  if ((f & UF.Champion) !== 0 || t.vis.build === 'champion') return 'champion';
  if ((f & UF.Siege) !== 0 || t.vis.build === 'siege') return 'siege';
  if ((f & UF.Caster) !== 0 || t.vis.build === 'caster') return 'caster';
  if ((f & UF.Cavalry) !== 0 || t.vis.build === 'cavalry') return 'cavalry';
  if ((f & UF.Ranged) !== 0 || t.vis.build === 'archer') return 'missile';
  if ((f & UF.Large) !== 0 || t.vis.build === 'brute') return 'brute';
  if ((f & UF.Spear) !== 0 || t.vis.weapon === 'spear') return 'spear';
  return 'infantry';
}

// ── Formatting helpers ─────────────────────────────────────────────────────

/**
 * The ceiling on a resource, or Infinity where there is none.
 *
 * Gold and Souls are the two the warehouses do not hold: `settlement.ts` skips
 * everything in `UNCAPPED_RESOURCES` when it clamps, then puts a separate,
 * doubled ceiling on Souls. Both facts have to be true here as well or the
 * strip lies about which of them is full.
 */
function capOf(w: World, k: ResourceKey): number {
  if (k === 'souls') return w.resCap * 2;
  return UNCAPPED_RESOURCES.includes(k) ? Infinity : w.resCap;
}

/** `+3.4` / `−1.0`. A rate without its sign is not a rate. */
function signed(v: number, places: number): string {
  return `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(places)}`;
}

function moraleWord(m: number): string {
  return m > 1.0 ? 'resolute' : m > 0.7 ? 'steady' : m > 0.45 ? 'uneasy' : m > 0.25 ? 'fraying' : 'broken';
}

function costText(cost: Record<string, number | undefined>): string {
  const parts: string[] = [];
  for (const k of RESOURCE_KEYS) {
    const v = cost[k] ?? 0;
    if (v > 0) parts.push(`${RESOURCE_GLYPHS[k]}${Math.round(v)}`);
  }
  return parts.length > 0 ? parts.join('  ') : 'free';
}

function costTextRich(cost: Record<string, number | undefined>, w: World): string {
  const parts: string[] = [];
  for (const k of RESOURCE_KEYS as readonly ResourceKey[]) {
    const v = cost[k] ?? 0;
    if (v <= 0) continue;
    const short = w.res[k] < v;
    parts.push(`<span class="${short ? 'short' : ''}">${resourceIcon(k, 12)}${Math.round(v)}</span>`);
  }
  return parts.length > 0 ? parts.join('  ') : 'free';
}

function damageWord(t: number): string {
  return ['slash', 'pierce', 'blunt', 'arrow', 'siege', 'arcane', 'fire', 'poison'][t] ?? '';
}

function armourWord(t: number): string {
  return ['unarmoured', 'leather', 'mail', 'plate', 'shielded', 'structure', 'monstrous'][t] ?? '';
}

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);
}

export const HUD_COLORS = C;
export const HUD_UF = UF;
export const HUD_POWERS = POWERS;
